/* eslint-disable react-hooks/set-state-in-effect */
/* eslint-disable react-hooks/immutability */
/* eslint-disable react-hooks/refs */
/* eslint-disable react-hooks/static-components */
/* eslint-disable react-hooks/preserve-manual-memoization */
import { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Progress } from "@/components/ui/progress"
import { Spinner } from "@/components/ui/spinner"
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { CheckCircle2, AlertCircle, HardDrive, Search, Plus, CircleDashed, Clock, ListOrdered, ArrowUpDown, Pause, Square, Play, XCircle, Pencil, Check, LayoutGrid, LayoutList, Columns, X, Trash2, User, Download, ChevronDown, Wand2, AlertTriangle, FolderOpen, ChevronRight, Loader2 } from "lucide-react"
import { jobsApi, cloudApi, catalogueApi, cliModulesApi, settingsApi, tasksApi, type ProductGroupDTO, type OrganizeResultDTO } from "@/lib/api"
import type { Job, AvatarOption } from "@/App"
import type { SkuRule } from "@/pages/cli/Settings"

type Props = {
  jobs: Job[]
  selectedId: string | null
  onSelect: (id: string) => void
  onNewBatch: () => void
  onUpdateJobs: (ids: string[], status: Job['status']) => void
  onPatchJob: (id: string, patch: Partial<Job>) => void
  onDeleteJobs: (ids: string[]) => void
  availableAvatars: AvatarOption[]
  skuProperties: string[]
  skuRules: SkuRule[]
  filter?: 'active' | 'done'
  cloud?: { google: { connected: boolean }; dropbox: { connected: boolean } }
}

const PRIORITY: Record<Job['status'], number> = {
  processing: 0, batching: 1, paused: 2, queued: 3, not_started: 4, not_configured: 5, error: 6, partial: 7, cancelled: 8, done: 9
}

const sourceIcon = (source: Job['source']) => {
  if (source === 'drive') return (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 shrink-0" fill="none">
      <path d="M4.5 19L8.5 12L4 5H12L16 12L12 19H4.5Z" fill="#0F9D58"/>
      <path d="M16 12L20 19H8L12 12H16Z" fill="#4285F4"/>
      <path d="M8.5 12L4 5H16L20 12H8.5Z" fill="#FFBB00"/>
    </svg>
  )
  if (source === 'dropbox') return (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 shrink-0" fill="#0061FF">
      <path d="M6 2L1 5.5l5 3.5 5-3.5L6 2zM16 2l-5 3.5 5 3.5 5-3.5L16 2zM1 12.5L6 16l5-3.5L6 9l-5 3.5zM16 9l-5 3.5 5 3.5 5-3.5L16 9zM6 17.5l5 3.5 5-3.5-5-3.5-5 3.5z"/>
    </svg>
  )
  return <HardDrive className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
}

const statusBadge = (status: Job['status']) => {
  if (status === 'done')           return <Badge variant="outline" className="text-green-600 border-green-600/30 gap-1 shrink-0"><CheckCircle2 className="w-3 h-3" />Done</Badge>
  if (status === 'processing')     return <Badge variant="secondary" className="gap-1 shrink-0"><Spinner className="w-3 h-3" />Processing</Badge>
  if (status === 'error')          return <Badge variant="destructive" className="gap-1 shrink-0"><AlertCircle className="w-3 h-3" />Error</Badge>
  if (status === 'not_configured') return <Badge variant="outline" className="text-orange-500 border-orange-400/30 gap-1 shrink-0"><CircleDashed className="w-3 h-3" />Not configured</Badge>
  if (status === 'not_started')    return <Badge variant="outline" className="text-muted-foreground gap-1 shrink-0"><Clock className="w-3 h-3" />Not started</Badge>
  if (status === 'queued')         return <Badge variant="outline" className="text-blue-500 border-blue-400/30 gap-1 shrink-0"><ListOrdered className="w-3 h-3" />In queue</Badge>
  if (status === 'batching')       return <Badge variant="outline" className="text-violet-500 border-violet-400/30 gap-1 shrink-0"><Clock className="w-3 h-3" />Batch (24h)</Badge>
  if (status === 'paused')         return <Badge variant="outline" className="text-amber-500 border-amber-400/30 gap-1 shrink-0"><Pause className="w-3 h-3" />Paused</Badge>
  if (status === 'cancelled')      return <Badge variant="outline" className="text-muted-foreground gap-1 shrink-0"><XCircle className="w-3 h-3" />Cancelled</Badge>
}

type SkuType = 'optique' | 'solaire'

function toImageArray(v: unknown): string[] {
  return Array.isArray(v) ? (v as unknown[]).filter((x): x is string => typeof x === 'string') : []
}

function toTypesSet(v: unknown): Set<SkuType> {
  const allowed = new Set<SkuType>(['optique', 'solaire'])
  if (v instanceof Set)      return new Set([...v].filter((x): x is SkuType => allowed.has(x as SkuType)))
  if (Array.isArray(v))      return new Set(v.filter((x): x is SkuType => allowed.has(x as SkuType)))
  if (typeof v === 'string') return new Set(v.split(',').map(x => x.trim()).filter((x): x is SkuType => allowed.has(x as SkuType)))
  return new Set()
}

function hasType(sku: { types?: unknown } | undefined | null, type: SkuType): boolean {
  return toTypesSet(sku?.types).has(type)
}

type SkuEntry = { id: string; folder: string; sku: string; avatar: string | null; types: Set<'optique' | 'solaire'>; properties: Record<string, string>; images: [string, string] | null; outputImages?: string[]; thumbnail?: string | null; model_override?: string | null; skipped?: boolean; skip_reason?: string | null; type_results?: Record<string, { status: string; url?: string; error?: string; avatar_id?: string; avatar_name?: string; expected_path?: string }>; done?: number; total_needed?: number; total_ops?: number; completed_ops?: number }

type ProgressSnapshot = {
  status: string; pct: number
  total_done: number; total_needed: number
  completed_ops: number; total_ops: number
  failed_count: number; skipped_count: number
  duration_ms: number | null
  cost?: { vision_eur: number; generation_eur: number; input_tokens: number; output_tokens: number }
  skus: {
    id?: string | number
    ref?: string
    sku: string
    output_images: string[]
    thumbnail: string | null
    skipped: boolean
    skip_reason: string | null
    type_results?: Record<string, { status: string; url?: string; error?: string; avatar_id?: string; avatar_name?: string; expected_path?: string }>
    types?: string[]
    done?: number
    total_needed?: number
    total_ops?: number
    completed_ops?: number
  }[]
}

function deriveFinalStatus(p: ProgressSnapshot): Job['status'] {
  const done = p.total_done ?? 0
  const total = p.total_needed ?? 0
  const failed = p.failed_count ?? 0
  const skipped = p.skipped_count ?? 0
  if (total > 0 && done === 0 && (failed > 0 || skipped > 0)) return 'error'
  if (failed > 0 || skipped > 0 || done < total) return 'partial'
  return 'done'
}

function formatDuration(ms: number): string {
  const s = Math.round(ms / 1000)
  const m = Math.floor(s / 60)
  const sec = s % 60
  if (m === 0) return `${sec}s`
  return sec === 0 ? `${m}m` : `${m}m ${sec}s`
}


function GeneratedThumb({ src, alt = '', className = 'absolute inset-0 w-full h-full object-cover' }: { src: string; alt?: string; className?: string }) {
  const [loaded, setLoaded] = useState(false)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    setLoaded(false)
    setFailed(false)
  }, [src])
  return (
    <>
      {!loaded && !failed && <Skeleton className="absolute inset-0 rounded-none" />}
      {failed
        ? <div className="absolute inset-0 flex items-center justify-center"><HardDrive className="w-5 h-5 text-muted-foreground/30" /></div>
        : <img src={src} alt={alt} className={[className, loaded ? '' : 'opacity-0'].filter(Boolean).join(' ')}
            onLoad={() => setLoaded(true)}
            onError={() => { setFailed(true); setLoaded(true) }} />}
    </>
  )
}

function DoneCardCarousel({ imgs, sku }: { imgs: string[]; sku: string }) {
  const [slide, setSlide] = useState(0)
  const [loaded, setLoaded] = useState<Record<number, boolean>>({})
  useEffect(() => {
    // Pre-mark images already cached before React attaches onLoad
    const pre: Record<number, boolean> = {}
    imgs.forEach((src, i) => { const img = new Image(); img.src = src; if (img.complete) pre[i] = true })
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoaded(pre)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgs.join(',')])
  useEffect(() => {
    if (imgs.length < 2) return
    const id = setInterval(() => setSlide(s => (s + 1) % imgs.length), 2200)
    return () => clearInterval(id)
  }, [imgs.length])
  const label = (p: string) => p.includes('_solaire') || p.includes('_Solaire') ? 'Solaire' : 'Optique'
  const allLoaded = imgs.every((_, i) => loaded[i])
  return (
    <div className="absolute inset-0">
      {/* Skeleton tied to current slide — disappears once that image loads */}
      {!loaded[slide] && <Skeleton className="absolute inset-0 rounded-none z-10" />}
      {imgs.map((src, i) => (
        <img key={src} src={src} alt={sku}
          onLoad={() => setLoaded(p => ({ ...p, [i]: true }))}
          onError={() => setLoaded(p => ({ ...p, [i]: true }))}
          className={["absolute inset-0 w-full h-full object-cover transition-opacity duration-500", i === slide && loaded[i] ? "opacity-100" : "opacity-0"].join(' ')}
        />
      ))}
      {allLoaded && (
        <span className="absolute bottom-0 inset-x-0 text-center text-[8px] text-white/80 bg-black/20 py-0.5 z-10">
          {label(imgs[slide])}
        </span>
      )}
      {allLoaded && imgs.length > 1 && (
        <div className="absolute bottom-4 inset-x-0 flex items-center justify-center gap-1 z-10">
          {imgs.map((_, i) => (
            <button key={i}
              onClick={e => { e.stopPropagation(); setSlide(i) }}
              className={["rounded-full transition-all duration-200", i === slide ? "w-3 h-1.5 bg-white" : "w-1.5 h-1.5 bg-white/50 hover:bg-white/80"].join(' ')}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function DoneSkuDetail({ sku, avatars, onClose, onRegen }: { sku: SkuEntry; avatars: AvatarOption[]; onClose: () => void; onRegen: (skuId: string, type?: string) => void }) {
  const avatar = avatars.find(a => a.id === sku.avatar)
  const typeLabel = hasType(sku, 'optique') && hasType(sku, 'solaire') ? 'Optique & Solaire' : hasType(sku, 'solaire') ? 'Solaire' : 'Optique'
  const imgs = sku.outputImages ?? []
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-background rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold text-base">{sku.sku}</span>
            <span className="text-xs text-muted-foreground">{typeLabel} · {avatar ? avatar.name : sku.avatar ? `Avatar ${sku.avatar} (not found)` : 'No model'}</span>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors"><X className="w-4 h-4" /></button>
        </div>
        {/* body */}
        <div className="flex flex-col gap-4 p-5">
          {/* Row 1: Source + Model */}
          <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-center">
            {/* Source */}
            <div className="flex flex-col gap-2">
              <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Source</span>
              <div className="flex flex-row gap-1.5">
                {sku.images ? (
                  <>
                    <div className="relative flex-1 rounded-lg overflow-hidden bg-secondary/40" style={{ aspectRatio: '1' }}>
                      <img src={sku.images[0]} alt="Face" className="w-full h-full object-cover" />
                      <span className="absolute bottom-0 inset-x-0 text-center text-[8px] text-white/80 bg-black/20 py-0.5">Face</span>
                    </div>
                    <div className="relative flex-1 rounded-lg overflow-hidden bg-secondary/40" style={{ aspectRatio: '1' }}>
                      <img src={sku.images[1]} alt="Côté" className="w-full h-full object-cover" />
                      <span className="absolute bottom-0 inset-x-0 text-center text-[8px] text-white/80 bg-black/20 py-0.5">Côté</span>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 rounded-lg bg-secondary/40 flex items-center justify-center" style={{ aspectRatio: '2' }}><HardDrive className="w-6 h-6 text-muted-foreground/30" /></div>
                )}
              </div>
            </div>
            {/* arrow */}
            <div className="flex items-center justify-center text-muted-foreground/30 text-xl">→</div>
            {/* Model */}
            <div className="flex flex-col gap-2">
              <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Model</span>
              {avatar ? (
                <div className="flex flex-row gap-2 items-center">
                  <div className="relative rounded-lg overflow-hidden bg-secondary/40 w-16 shrink-0" style={{ aspectRatio: '3/4' }}>
                    <img src={avatar.img} alt={avatar.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs font-medium">{avatar.name}</span>
                    <span className="text-[10px] text-muted-foreground">{typeLabel}</span>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg bg-secondary/40 flex items-center justify-center" style={{ aspectRatio: '2' }}><User className="w-6 h-6 text-muted-foreground/30" /></div>
              )}
            </div>
          </div>
          {/* Divider */}
          <div className="border-t border-border" />
          {/* Row 2: Output */}
          <div className="flex flex-col gap-2">
            <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Résultat</span>
            <div className="flex flex-row gap-2">
              {(['Optique', 'Solaire'] as const).map(label => {
                const src = imgs.find(s => s.toLowerCase().includes(`_${label.toLowerCase()}`)) ?? (label === 'Optique' ? imgs.find(s => !s.toLowerCase().includes('_solaire')) : null) ?? null
                return (
                  <div key={label} className="group relative rounded-lg overflow-hidden bg-secondary/40 flex-1" style={{ aspectRatio: '3/4' }}>
                    {src ? (
                      <>
                        <img src={src} alt={label} className="absolute inset-0 w-full h-full object-cover object-top" />
                        <span className="absolute bottom-0 inset-x-0 text-center text-[8px] text-white/80 bg-black/20 py-0.5">{label}</span>
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={e => { e.stopPropagation(); onClose(); onRegen(sku.id, label.toLowerCase()) }} className="flex items-center gap-1.5 rounded-full bg-white/90 text-black text-[11px] font-medium px-3 py-1.5 hover:bg-white transition-colors" title={`Regenerate ${label} only`}>
                            Regenerate {label}
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                        <HardDrive className="w-5 h-5 text-muted-foreground/20" />
                        <span className="text-[9px] text-muted-foreground/40">{label}</span>
                        <button onClick={e => { e.stopPropagation(); onClose(); onRegen(sku.id) }} className="flex items-center gap-1.5 rounded-full bg-foreground text-background text-[11px] font-medium px-3 py-1.5 hover:opacity-90 transition-opacity mt-1">
                          Generate
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function ManualPickModal({ product, onPick, onApplyMarque, onClose }: {
  product: ProductGroupDTO & { group: string; errors: string[] }
  onPick: (cat: import('@/lib/api').CatalogueSearchResultDTO) => void
  onApplyMarque: (marque: string) => void
  onClose: () => void
}) {
  const img7 = product.images[6] ?? product.images[product.images.length - 1] ?? null
  const img8 = product.images[7] ?? product.images[product.images.length - 1] ?? null

  // Pre-fill with model part (extracted ref minus trailing color group)
  const initialQ = (() => {
    const ref = product.extracted_ref ?? ''
    const tokens = ref.trim().split(/\s+/)
    return tokens.length > 1 ? tokens.slice(0, -1).join(' ') : ref
  })()

  const [q, setQ] = useState(initialQ)
  const [results, setResults] = useState<import('@/lib/api').CatalogueSearchResultDTO[]>([])
  const [loading, setLoading] = useState(false)
  const [zoomedSrc, setZoomedSrc] = useState<string | null>(null)
  const [manualMarque, setManualMarque] = useState(product.extracted_marque ?? '')

  const missingMarque = !product.extracted_marque
  const hasRef = !!product.extracted_ref

  useEffect(() => {
    if (!q.trim()) { setResults([]); return }
    const t = setTimeout(() => {
      setLoading(true)
      catalogueApi.search(q).then(setResults).catch(() => setResults([])).finally(() => setLoading(false))
    }, 250)
    return () => clearTimeout(t)
  }, [q])

  // Trigger initial search on mount
  useEffect(() => { if (initialQ) setQ(initialQ) }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-background rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b shrink-0">
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold text-base">Fix manually</span>
            <span className="text-xs text-muted-foreground font-mono">{product.group}</span>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors"><X className="w-4 h-4" /></button>
        </div>
        {/* Images */}
        <div className="flex gap-3 px-5 pt-4 pb-3 shrink-0">
          {[{ label: 'Marque (img 7)', src: img7 }, { label: 'Ref (img 8)', src: img8 }].map(({ label, src }) => (
            <div key={label} className="flex flex-col gap-1.5 flex-1">
              <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
              <div
                className={["rounded-xl overflow-hidden bg-secondary/40 flex items-center justify-center", src ? "cursor-zoom-in" : ""].join(' ')}
                style={{ aspectRatio: '1' }}
                onClick={() => src && setZoomedSrc(src)}
              >
                {src
                  ? <img src={src} alt={label} className="w-full h-full object-cover" />
                  : <HardDrive className="w-6 h-6 text-muted-foreground/30" />}
              </div>
            </div>
          ))}
        </div>
        {/* Zoom overlay */}
        {zoomedSrc && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm cursor-zoom-out"
            onClick={() => setZoomedSrc(null)}
          >
            <img src={zoomedSrc} alt="zoom" className="max-w-[90vw] max-h-[90vh] rounded-xl object-contain shadow-2xl" />
          </div>
        )}
        {/* Gemini extraction info */}
        <div className="px-5 pb-3 shrink-0 flex flex-col gap-0.5">
          <div className="flex gap-2 text-xs">
            <span className="text-muted-foreground w-14 shrink-0">Marque</span>
            <span className="font-mono font-semibold">{product.extracted_marque ?? '—'}</span>
          </div>
          <div className="flex gap-2 text-xs">
            <span className="text-muted-foreground w-14 shrink-0">Ref</span>
            <span className="font-mono font-semibold">{product.extracted_ref ?? '—'}</span>
          </div>
        </div>
        {/* Manual marque input — shown when marque is missing but ref exists */}
        {missingMarque && hasRef && (
          <div className="px-5 pb-3 shrink-0 flex flex-col gap-1.5 border-t border-border pt-3">
            <label className="text-[10px] font-medium uppercase tracking-wider text-amber-600">Marque inconnue — saisie manuelle</label>
            <div className="flex gap-2">
              <input
                value={manualMarque}
                onChange={e => setManualMarque(e.target.value)}
                placeholder="ex: VOGUE"
                className="flex-1 h-8 rounded-lg border border-border bg-background px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-foreground/30"
              />
              <button
                disabled={!manualMarque.trim()}
                onClick={() => onApplyMarque(manualMarque.trim())}
                className="px-3 h-8 rounded-lg bg-foreground text-background text-xs font-medium hover:opacity-80 disabled:opacity-40 transition-opacity shrink-0"
              >
                Appliquer
              </button>
            </div>
          </div>
        )}
        {/* Search */}
        <div className="px-5 pb-3 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            <input
              autoFocus
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search catalogue…"
              className="w-full h-9 rounded-lg border border-border bg-background pl-8 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-foreground/30"
            />
            {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-muted-foreground" />}
          </div>
        </div>
        {/* Results */}
        <div className="border-t border-border overflow-y-auto max-h-60">
          {results.length === 0 && q.trim() && !loading && (
            <div className="px-5 py-4 text-sm text-muted-foreground italic text-center">No results for "{q}"</div>
          )}
          {results.map(r => (
            <button
              key={r.id}
              onClick={() => onPick(r)}
              className="w-full flex items-center gap-3 px-5 py-2.5 hover:bg-secondary/60 transition-colors text-left border-b border-border/50 last:border-0"
            >
              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                <span className="text-sm font-semibold truncate">{r.brand ?? '—'} · {r.product_ref ?? '—'}</span>
                <span className="text-xs text-muted-foreground">{r.catalogue_name} {r.inventory_id ? `· ${r.inventory_id}` : ''} {r.avatar_type ? `· ${r.avatar_type}` : ''}</span>
              </div>
              <Check className="w-4 h-4 text-muted-foreground/40 shrink-0" />
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

function AvatarPicker({ avatars, current, onPick, onClose }: {
  avatars: AvatarOption[]; current: string | null
  onPick: (id: string | null) => void; onClose: () => void
}) {
  return (
    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-[200] bg-background border rounded-xl shadow-2xl p-2" style={{width: 'max-content'}} onClick={e => e.stopPropagation()}>
      <div className="grid grid-cols-3 gap-1.5">
        {avatars.map(a => (
          <button key={a.id} onClick={() => { onPick(a.id); onClose() }}
            className={["w-12 aspect-[2/3] rounded-md overflow-hidden bg-secondary transition-all flex-shrink-0", current === a.id ? "ring-2 ring-offset-1 ring-foreground" : "hover:ring-2 hover:ring-offset-1 hover:ring-foreground/40"].join(' ')}>
            <img src={a.img} alt="" className="w-full h-full object-contain" />
          </button>
        ))}
      </div>
      {current && (
        <button onClick={() => { onPick(null); onClose() }}
          className="mt-1.5 w-full text-[10px] text-muted-foreground hover:text-foreground py-0.5 rounded transition-colors">
          Remove
        </button>
      )}
    </div>
  )
}

function SkuCard({ sku, selected, onToggle, onPreview, onAssign, onTypeChange, onModelChange, avatars }: {
  sku: SkuEntry; selected: boolean; avatars: AvatarOption[]
  onToggle: (e: React.MouseEvent) => void
  onPreview: () => void
  onAssign: (avatarId: string | null) => void
  onTypeChange: (value: 'optique' | 'solaire' | 'both') => void
  onModelChange: (model: string | null) => void
  onPropChange?: (key: string, value: string) => void
}) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const assignedAvatar = avatars.find(a => a.id === sku.avatar)
  const currentType = hasType(sku, 'optique') && hasType(sku, 'solaire') ? 'both' : hasType(sku, 'solaire') ? 'solaire' : 'optique'
  const typeLabel = currentType === 'both' ? 'Les deux' : currentType === 'solaire' ? 'Solaire' : 'Optique'

  useEffect(() => {
    if (!pickerOpen) return
    const close = () => setPickerOpen(false)
    window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [pickerOpen])

  return (
    <div
      className={["flex flex-col rounded-xl border cursor-pointer transition-all", selected ? "border-foreground/50 ring-1 ring-foreground/20" : "border-border hover:border-foreground/20"].join(' ')}
      onClick={onToggle}
    >
      {/* Thumbnail — 200px server thumb > full output > source image */}
      <div className="relative aspect-square bg-secondary/40 flex items-center justify-center group rounded-t-xl overflow-hidden" onClick={e => { e.stopPropagation(); onPreview() }}>
        {(sku.thumbnail ?? sku.outputImages?.[0] ?? sku.images?.[0])
          ? <img src={sku.thumbnail ?? sku.outputImages?.[0] ?? sku.images![0]} alt={sku.sku} className="w-full h-full object-cover" />
          : <HardDrive className="w-6 h-6 text-muted-foreground/20" />}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
        {selected && <div className="absolute top-1.5 left-1.5 w-4 h-4 rounded bg-foreground flex items-center justify-center z-10"><Check className="w-2.5 h-2.5 text-background" /></div>}
        {/* Avatar portrait — top right */}
        {assignedAvatar && (
          <div className="absolute top-0 right-0 w-2/5 h-3/5 overflow-hidden rounded-bl-xl border-l border-b border-background/40 bg-secondary">
            <img src={assignedAvatar.img} alt="" className="w-full h-full object-contain" />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-1 px-2 py-1.5">
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-[9px] text-muted-foreground truncate leading-tight">{sku.folder}</span>
          <span className="text-[10px] font-medium truncate leading-tight">{sku.sku}</span>
        </div>
        <div className="relative shrink-0">
          <button
            onClick={e => { e.stopPropagation(); setPickerOpen(v => !v) }}
            className={["w-7 h-7 rounded-md border flex items-center justify-center transition-all hover:ring-1 hover:ring-foreground/30", assignedAvatar ? "border-foreground/20 bg-secondary/40" : "border-dashed border-muted-foreground/40 bg-secondary/30"].join(' ')}
          >
            {assignedAvatar
              ? <Pencil className="w-3 h-3 text-muted-foreground" />
              : <Plus className="w-3 h-3 text-muted-foreground" />}
          </button>
          {pickerOpen && <AvatarPicker avatars={avatars} current={sku.avatar} onPick={id => onAssign(id)} onClose={() => setPickerOpen(false)} />}
        </div>
      </div>

      {/* Type dropdown */}
      <div className="border-t overflow-hidden" onClick={e => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-full flex items-center justify-between px-2.5 py-1.5 text-[10px] font-medium hover:bg-secondary/30 transition-colors">
              <span>{typeLabel}</span>
              <ChevronDown className="w-3 h-3 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[7rem]">
            <DropdownMenuRadioGroup value={currentType} onValueChange={v => onTypeChange(v as 'optique' | 'solaire' | 'both')}>
              <DropdownMenuRadioItem value="optique" className="text-xs">Optique</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="solaire" className="text-xs">Solaire</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="both" className="text-xs">Les deux</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Model override input */}
      <div className="border-t rounded-b-xl px-2 py-1" onClick={e => e.stopPropagation()}>
        <input
          type="text"
          placeholder="Model (default)"
          value={sku.model_override ?? ''}
          onChange={e => onModelChange(e.target.value || null)}
          className="w-full text-[9px] bg-transparent text-muted-foreground placeholder:text-muted-foreground/40 outline-none truncate"
        />
      </div>
    </div>
  )
}

function FloatingAssignBar({ count, avatars, onAssign, onUnassign, onClose, onSetType, typeValue }: {
  count: number; avatars: AvatarOption[]
  onAssign: (id: string) => void; onUnassign: () => void; onClose: () => void
  onSetType: (value: 'optique' | 'solaire' | 'both') => void
  typeValue: 'optique' | 'solaire' | 'both' | 'mixed'
}) {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [open])
  const typeLabel = typeValue === 'both' ? 'Les deux' : typeValue === 'solaire' ? 'Solaire' : typeValue === 'mixed' ? 'Type…' : 'Optique'

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3">
      {/* Action bar */}
      <div className="flex items-center gap-3 sm:gap-2 bg-background border rounded-2xl shadow-xl px-4 sm:px-4 py-3 sm:py-2.5">
        <span className="text-sm font-semibold tabular-nums">{count}<span className="hidden sm:inline font-normal text-muted-foreground"> selected</span></span>
        <div className="w-px h-5 bg-border" />
        <div className="relative">
          <button
            onClick={e => { e.stopPropagation(); setOpen(v => !v) }}
            className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 sm:px-2.5 sm:py-1.5 rounded-xl bg-foreground text-background hover:bg-foreground/90 transition-colors"
          >
            <User className="w-4 h-4 sm:w-3.5 sm:h-3.5" /><span className="hidden sm:inline">Assign avatar</span>
          </button>
          {open && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-background border rounded-xl shadow-2xl p-2" style={{ width: 'max-content' }} onClick={e => e.stopPropagation()}>
              <div className="grid grid-cols-3 gap-1.5">
                {avatars.map(a => (
                  <button key={a.id} onClick={() => { onAssign(a.id); setOpen(false) }}
                    className="w-12 aspect-[2/3] rounded-md overflow-hidden bg-secondary hover:ring-2 hover:ring-offset-1 hover:ring-foreground/50 transition-all">
                    <img src={a.img} alt="" className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="w-px h-5 bg-border" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 sm:px-2.5 sm:py-1.5 rounded-xl border hover:bg-secondary/50 transition-colors">
              {typeLabel}<ChevronDown className="w-3 h-3 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" side="top">
            <DropdownMenuRadioGroup value={typeValue === 'mixed' ? undefined : typeValue} onValueChange={v => onSetType(v as 'optique' | 'solaire' | 'both')}>
              <DropdownMenuRadioItem value="optique" className="text-xs">Optique</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="solaire" className="text-xs">Solaire</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="both" className="text-xs">Les deux</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="w-px h-5 bg-border" />
        <button onClick={onUnassign} className="flex items-center justify-center w-9 h-9 sm:w-7 sm:h-7 rounded-lg hover:bg-red-50 text-red-500 hover:text-red-600 transition-colors" title="Unassign">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Separate close button */}
      <button
        onClick={onClose}
        className="w-10 h-10 rounded-full bg-background border shadow-xl flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}

import type { FolderNode } from "@/lib/api"
function OrganizeButton({ products, onOrganize, alreadyOrganized, onViewOrganized }: { products: { errors?: string[]; normalized_key?: string | null; extracted_marque?: string | null; extracted_ref?: string | null }[]; onOrganize: (uniqueCount: number) => void; alreadyOrganized?: boolean; onViewOrganized?: () => void }) {
  const ok = products.filter(p => !p.errors?.length)
  const allFailed = ok.length === 0
  // Detect (never remove) products sharing a reference — genuinely
  // different physical products can share an identical printed ref, so
  // nothing is dropped; this only surfaces a warning for manual review.
  const keyCount = new Map<string, number>()
  ok.forEach(p => {
    const k = p.normalized_key || (p.extracted_marque && p.extracted_ref ? `${p.extracted_marque}__${p.extracted_ref}` : null)
    if (k) keyCount.set(k, (keyCount.get(k) ?? 0) + 1)
  })
  const nDuplicates = [...keyCount.values()].reduce((s, n) => s + (n > 1 ? n - 1 : 0), 0)
  const uniqueCount = ok.length  // nothing is silently dropped anymore
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3">
      <div className="flex items-center gap-3 bg-background border rounded-2xl shadow-xl px-4 py-2.5">
        {nDuplicates > 0 && (
          <>
            <span className="text-sm text-orange-600 font-medium">
              {nDuplicates} produit{nDuplicates > 1 ? 's' : ''} partage{nDuplicates > 1 ? 'nt' : ''} une référence identique — vérifiez qu'il s'agit bien de produits différents
            </span>
            <div className="w-px h-5 bg-border" />
          </>
        )}
        <span className="text-sm font-semibold tabular-nums">{uniqueCount} <span className="font-normal text-muted-foreground">SKUs</span></span>
        <div className="w-px h-5 bg-border" />
        {alreadyOrganized ? (
          <>
            <button
              onClick={onViewOrganized}
              className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-xl bg-foreground text-background hover:bg-foreground/90 transition-colors"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> View organized
            </button>
            <button
              onClick={() => onOrganize(uniqueCount)}
              className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-xl border hover:bg-secondary/50 transition-colors text-muted-foreground"
              title="Re-organize with current analysis"
            >
              Re-organize
            </button>
          </>
        ) : (
          <button
            onClick={() => onOrganize(uniqueCount)}
            disabled={allFailed}
            className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-xl bg-foreground text-background hover:bg-foreground/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <FolderOpen className="w-3.5 h-3.5" /> Organize files →
          </button>
        )}
      </div>
    </div>
  )
}

function FolderTreeView({ node, depth }: { node: FolderNode; depth: number }) {
  const [open, setOpen] = useState(depth < 3)
  const hasChildren = node.children.length > 0
  const hasFiles = node.files.length > 0
  const totalFiles = node.files.length + node.children.reduce((s, c) => s + c.files.length, 0)
  const isRoot = depth === 0

  return (
    <div style={{ paddingLeft: depth > 0 ? 16 : 0 }}>
      <button
        className={[
          "flex items-center gap-1.5 w-full text-left py-0.5 group",
          isRoot ? "opacity-50 cursor-default" : "hover:text-foreground",
        ].join(" ")}
        onClick={() => !isRoot && setOpen(v => !v)}
        disabled={isRoot}
      >
        {!isRoot && (hasChildren || hasFiles) && (
          <ChevronRight className={["w-3 h-3 shrink-0 transition-transform text-muted-foreground", open ? "rotate-90" : ""].join(" ")} />
        )}
        {(isRoot || (!hasChildren && !hasFiles)) && <span className="w-3" />}
        <FolderOpen className="w-3.5 h-3.5 shrink-0 text-amber-400" />
        <span className={["truncate", isRoot ? "text-muted-foreground" : "text-foreground/90 font-medium"].join(" ")}>{node.name}</span>
        {totalFiles > 0 && (
          <span className="ml-auto text-[10px] text-muted-foreground tabular-nums shrink-0">{totalFiles} img</span>
        )}
      </button>
      {open && (
        <>
          {node.children.map(child => <FolderTreeView key={child.name} node={child} depth={depth + 1} />)}
          {hasFiles && (
            <div style={{ paddingLeft: 16 }} className="space-y-0.5">
              {node.files.map(f => (
                <div key={f} className="flex items-center gap-1.5 py-0.5 text-muted-foreground/70">
                  <span className="w-3 shrink-0" />
                  <span className="w-3.5 shrink-0" />
                  <span className="truncate text-[11px]">{f}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function JobDetail({ job, onBack: _onBack, onSave, avatars, skuRules, cloud }: { job: Job; onBack: () => void; onSave: (id: string, patch: Partial<Job>) => void; avatars: AvatarOption[]; skuRules: SkuRule[]; cloud?: { google: { connected: boolean }; dropbox: { connected: boolean } } }) {
  const onBack = () => {
    if (pushStateRef.current && !pushStateRef.current.url) {
      if (!window.confirm('Transfer to cloud is in progress. Leave anyway?')) return
    }
    _onBack()
  }
  const [name, setName] = useState(job.name)
  const [editingName, setEditingName] = useState(false)
  const nameInputRef = useRef<HTMLInputElement>(null)
  const [sourceUrl, setSourceUrl] = useState(job.sourceUrl ?? '')
  const [skus, setSkus] = useState<SkuEntry[]>([])  // populated from persisted job config on mount
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [viewMode, setViewMode] = useState<'grid' | 'avatar'>('grid')

  // Derived stable id for localStorage keys — never 'undefined'
  const jobId = job?.id ? String(job.id) : null
  // Hydration guard: prevents saving stale prefs from a previous job into the new job's key
  const [prefsHydratedForJob, setPrefsHydratedForJob] = useState<string | null>(null)

  const [gridGroup, setGridGroup] = useState<'avatar' | 'sku-type' | 'type'>(() => {
    if (!job?.id) return 'avatar'
    return (localStorage.getItem(`jobGridGroup_${job.id}`) as 'avatar' | 'sku-type' | 'type') ?? 'avatar'
  })
  const [preview, setPreview] = useState<SkuEntry | null>(null)
  const [addingTo, setAddingTo] = useState<string | null>(null)
  const [drillAvatar, setDrillAvatar] = useState<string | null>(null)
  const [drillPicking, setDrillPicking] = useState(false)
  const [drillPicked, setDrillPicked] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState('')
  const lastClickedId = useRef<string | null>(null)
  const [perItemPicker, setPerItemPicker] = useState<string | null>(null)

  useEffect(() => { if (editingName) nameInputRef.current?.focus() }, [editingName])
  useEffect(() => {
    if (!perItemPicker) return
    const close = () => setPerItemPicker(null)
    window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [perItemPicker])

  const applyRules = useCallback(() =>
    setSkus(prev => prev.map(s => {
      const rule = skuRules.find(r => r.prefixes.some(p => p && s.sku.toUpperCase().startsWith(p.value)))
      return rule ? { ...s, avatar: rule.avatarId } : s
    })), [skuRules])

  const assignAvatar = useCallback((skuIds: string[], avatarId: string | null) =>
    setSkus(prev => prev.map(s => skuIds.includes(s.id) ? { ...s, avatar: avatarId } : s)), [])
  const assignSkuModel = useCallback((skuId: string, model: string | null) =>
    setSkus(prev => prev.map(s => s.id === skuId ? { ...s, model_override: model } : s)), [])
  const assignSkuType = useCallback((skuId: string, value: 'optique' | 'solaire' | 'both') =>
    setSkus(prev => prev.map(s => {
      if (s.id !== skuId) return s
      const types = new Set<'optique' | 'solaire'>()
      if (value === 'optique' || value === 'both') types.add('optique')
      if (value === 'solaire' || value === 'both') types.add('solaire')
      return { ...s, types }
    })), [])


  const toggleSelect = (skuId: string, e: React.MouseEvent, visibleSkus: SkuEntry[]) => {
    e.stopPropagation()
    if (e.shiftKey && lastClickedId.current) {
      const ids = visibleSkus.map(s => s.id)
      const a = ids.indexOf(lastClickedId.current)
      const b = ids.indexOf(skuId)
      if (a !== -1 && b !== -1) {
        const range = ids.slice(Math.min(a, b), Math.max(a, b) + 1)
        setSelected(prev => { const s = new Set(prev); range.forEach(id => s.add(id)); return s })
        return
      }
    }
    lastClickedId.current = skuId
    setSelected(prev => { const s = new Set(prev); if (s.has(skuId)) s.delete(skuId); else s.add(skuId); return s })
  }

  const [editMode, setEditMode] = useState(false)
  const [selectedOutput, setSelectedOutput] = useState<Set<string>>(new Set())
  const [detailSku, setDetailSku] = useState<SkuEntry | null>(null)
  const [outputSortBy, setOutputSortBy] = useState<'default' | 'folder' | 'sku' | 'model'>(() => {
    if (!job?.id) return 'default'
    return (localStorage.getItem(`jobOutSort_${job.id}`) as 'default' | 'folder' | 'sku' | 'model') ?? 'default'
  })
  const [outputGroupBy, setOutputGroupBy] = useState<'none' | 'sku' | 'model' | 'type' | 'status'>(() => {
    if (!job?.id) return 'sku'
    return (localStorage.getItem(`jobOutGroup_${job.id}`) as 'none' | 'sku' | 'model' | 'type' | 'status') ?? 'sku'
  })

  // Reload grouping preferences when job changes (guards against stale pref leaks between jobs)
  useEffect(() => {
    if (!jobId) return
    setPrefsHydratedForJob(null)
    setGridGroup((localStorage.getItem(`jobGridGroup_${jobId}`) as 'avatar' | 'sku-type' | 'type') ?? 'avatar')
    setOutputSortBy((localStorage.getItem(`jobOutSort_${jobId}`) as 'default' | 'folder' | 'sku' | 'model') ?? 'default')
    setOutputGroupBy((localStorage.getItem(`jobOutGroup_${jobId}`) as 'none' | 'sku' | 'model' | 'type' | 'status') ?? 'sku')
    setPrefsHydratedForJob(jobId)
  }, [jobId])
  // Save preferences — only after hydration for the current job to prevent stale writes
  useEffect(() => {
    if (!jobId || prefsHydratedForJob !== jobId) return
    localStorage.setItem(`jobGridGroup_${jobId}`, gridGroup)
  }, [jobId, prefsHydratedForJob, gridGroup])
  useEffect(() => {
    if (!jobId || prefsHydratedForJob !== jobId) return
    localStorage.setItem(`jobOutSort_${jobId}`, outputSortBy)
  }, [jobId, prefsHydratedForJob, outputSortBy])
  useEffect(() => {
    if (!jobId || prefsHydratedForJob !== jobId) return
    localStorage.setItem(`jobOutGroup_${jobId}`, outputGroupBy)
  }, [jobId, prefsHydratedForJob, outputGroupBy])

  const lastClickedOutputId = useRef<string | null>(null)
  const [switchAvatarPickerOpen, setSwitchAvatarPickerOpen] = useState(false)
  const [regenProgress, setRegenProgress] = useState<Set<string>>(new Set())
  const regenInFlight = useRef(false)
  const [lastProgress, setLastProgress] = useState<ProgressSnapshot | null>(null)

  const [step, setStep] = useState(1)
  const [outputFormat, setOutputFormat]     = useState<'jpeg'|'png'|'webp'>(job.outputFormat ?? 'jpeg')
  const [outputRes, setOutputRes]           = useState<'1080x1920'|'1440x2560'|'2160x3840'>(job.outputResolution as '1080x1920'|'1440x2560'|'2160x3840' ?? '1080x1920')
  const [naming, setNaming]                 = useState<'sku'|'marque-sku'|'custom'>(job.namingConvention as 'sku'|'marque-sku'|'custom' ?? 'sku')
  const [namingCustom, setNamingCustom]     = useState(job.namingCustom ?? '')
  const [namingHistory, setNamingHistory]   = useState<string[]>([])
  const [batchMode, setBatchMode]           = useState(true)   // batch by default — user can opt out
  const [jobMaxInputPx, setJobMaxInputPx]   = useState<number | null>(1024)  // 0 = original
  const [catalogueTokens, setCatalogueTokens] = useState<{token: string; label: string}[]>([])
  const [namingExample, setNamingExample]     = useState<Record<string, string>>({})
  const configSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const configLoadedRef = useRef(false)
  const configValuesRef = useRef<Record<string, unknown>>({})
  const skusRef = useRef<SkuEntry[]>([])

  const sortAndGroup = (list: SkuEntry[]) => {
    const sorted = [...list].sort((a, b) => {
      if (outputSortBy === 'folder') return a.folder.localeCompare(b.folder)
      if (outputSortBy === 'sku') return a.sku.localeCompare(b.sku)
      if (outputSortBy === 'model') return (a.avatar ?? '').localeCompare(b.avatar ?? '')
      return 0
    })
    if (outputGroupBy === 'none') return [{ label: null, items: sorted }]
    if (outputGroupBy === 'sku') {
      const map = new Map<string, SkuEntry[]>()
      sorted.forEach(s => { const k = s.folder || '—'; if (!map.has(k)) map.set(k, []); map.get(k)!.push(s) })
      return [...map.entries()].map(([label, items]) => ({ label, items }))
    }
    if (outputGroupBy === 'model') {
      const map = new Map<string, SkuEntry[]>()
      sorted.forEach(s => { const _av = avatars.find(a => a.id === s.avatar); const k = _av ? _av.name : s.avatar ? `Avatar ${s.avatar} (not found)` : 'No model'; if (!map.has(k)) map.set(k, []); map.get(k)!.push(s) })
      return [...map.entries()].map(([label, items]) => ({ label, items }))
    }
    if (outputGroupBy === 'type') {
      const map = new Map<string, SkuEntry[]>()
      sorted.forEach(s => { const k = hasType(s, 'optique') && hasType(s, 'solaire') ? 'Les deux' : hasType(s, 'solaire') ? 'Solaire' : 'Optique'; if (!map.has(k)) map.set(k, []); map.get(k)!.push(s) })
      return [...map.entries()].map(([label, items]) => ({ label, items }))
    }
    if (outputGroupBy === 'status') {
      const jobComplete = job.status === 'done' || job.status === 'partial' || job.status === 'error'
      const partial: SkuEntry[] = [], done: SkuEntry[] = [], skipped: SkuEntry[] = []
      const processing: SkuEntry[] = [], failed: SkuEntry[] = [], notStarted: SkuEntry[] = []
      sorted.forEach(s => {
        const hasOutput = (s.outputImages?.length ?? 0) > 0
        if (hasOutput && s.skipped) partial.push(s)
        else if (hasOutput) done.push(s)
        else if (s.skipped) skipped.push(s)
        else if (regenProgress.has(s.id)) processing.push(s)
        else if (jobComplete) failed.push(s)
        else notStarted.push(s)
      })
      return [
        partial.length    ? { label: 'Partial', items: partial }       : null,
        done.length       ? { label: 'Done', items: done }             : null,
        processing.length ? { label: 'Processing', items: processing } : null,
        skipped.length    ? { label: 'Skipped', items: skipped }       : null,
        failed.length     ? { label: 'Failed', items: failed }         : null,
        notStarted.length ? { label: 'Not started', items: notStarted } : null,
      ].filter(Boolean) as { label: string; items: SkuEntry[] }[]
    }
    return [{ label: null, items: sorted }]
  }

  const OutputToolbar = () => (
    <div className="flex items-center gap-2 flex-wrap">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border hover:bg-secondary/50 transition-colors">
            <ArrowUpDown className="w-3 h-3" />Sort{outputSortBy !== 'default' ? `: ${outputSortBy}` : ''}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuRadioGroup value={outputSortBy} onValueChange={v => setOutputSortBy(v as typeof outputSortBy)}>
            <DropdownMenuRadioItem value="default" className="text-xs">Default</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="folder" className="text-xs">Folder</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="sku" className="text-xs">SKU</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="model" className="text-xs">Model</DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <div className="flex rounded-lg border overflow-hidden">
        {(['none', 'sku', 'model', 'type', 'status'] as const).map((g, i) => (
          <button key={g} onClick={() => setOutputGroupBy(g)}
            className={["px-3 py-1.5 text-xs font-medium transition-colors", i > 0 ? "border-l" : "", outputGroupBy === g ? "bg-foreground text-background" : "hover:bg-secondary/50"].join(' ')}>
            {g === 'none' ? 'No group' : g === 'sku' ? 'Marque' : g === 'model' ? 'Model' : g === 'type' ? 'Type' : 'Status'}
          </button>
        ))}
      </div>
    </div>
  )

  const [queuePosition, setQueuePosition] = useState<number | null>(null)

  const TERMINAL_JOB_STATUSES = new Set(['done', 'partial', 'error', 'cancelled', 'paused'])
  const isTerminalJobStatus = (s: string) => TERMINAL_JOB_STATUSES.has(s)

  const startGenStream = (
    skuIds: string[] | undefined,
    onProgress: (e: { sku: string; type: string; done: number; total: number; url?: string; error?: string; retry?: boolean; attempt?: number; max_attempts?: number; wait_s?: number }) => void,
    onDone: (e: { total: number; errors: number; results: { sku: string; outputs: { type: string; url: string }[] }[] }) => void,
    onError: (msg: string) => void,
    _ctrl: AbortController,
    onStart?: (isReconnect?: boolean) => void,
    types?: string[],
  ) => {
    // SSE stream removed — progress comes from the 5s getProgress heartbeat poll.
    // onProgress/onDone callbacks are retained for call-site compatibility but not invoked.
    void onProgress; void onDone
    jobsApi.enqueue(Number(job.id), skuIds, types).then(res => {
      onStart?.()
      if (res.queue_position === 0) {
        setQueuePosition(null)
      } else {
        setQueuePosition(res.queue_position)
      }
      jobsApi.getProgress(Number(job.id)).then(p => {
        if (p.total_needed > 0)
          setGenProgress({ done: p.total_done, total: p.total_needed, lastSku: '', errors: [] })
        backfillFromProgress(p)
        if (!['queued'].includes(p.status)) setQueuePosition(null)
      }).catch(() => {})
    }).catch((err: unknown) => {
      const apiErr = err as { status?: number; body?: Record<string, unknown> }
      if (apiErr?.status === 409 && (apiErr?.body as { detail?: { active?: boolean } })?.detail?.active === true) {
        onStart?.(true)
        jobsApi.getProgress(Number(job.id)).then(p => {
          backfillFromProgress(p)
          if (p.total_needed > 0)
            setGenProgress({ done: p.total_done, total: p.total_needed, lastSku: '', errors: [] })
        }).catch(() => {})
        return
      }
      jobsApi.getProgress(Number(job.id)).then(p => { backfillFromProgress(p) }).catch(() => {})
      onError(String((apiErr?.body as Record<string, unknown>)?.message ?? err))
    })
  }

  const getUrlOrigin = () =>
    typeof window !== "undefined" ? window.location.origin : "http://localhost"
  const isRelativeUrl = (url: string) =>
    !/^[a-z][a-z\d+\-.]*:/i.test(url) && !url.startsWith("//")
  const withCacheBust = (url: string) => {
    try {
      const parsed = new URL(url, getUrlOrigin())
      parsed.searchParams.set("t", String(Date.now()))
      if (isRelativeUrl(url)) return `${parsed.pathname}${parsed.search}${parsed.hash}`
      return parsed.toString()
    } catch {
      const [beforeHash, hash = ""] = url.split("#")
      const sep = beforeHash.includes("?") ? "&" : "?"
      return `${beforeHash}${sep}t=${Date.now()}${hash ? `#${hash}` : ""}`
    }
  }
  const stripCacheBust = (url: string) => {
    try {
      const parsed = new URL(url, getUrlOrigin())
      parsed.searchParams.delete("t")
      if (isRelativeUrl(url)) return `${parsed.pathname}${parsed.search}${parsed.hash}`
      return parsed.toString()
    } catch {
      const [beforeHash, hash = ""] = url.split("#")
      const cleaned = beforeHash
        .replace(/([?&])t=\d+(&?)/, (_match, sep, tail) => (tail ? sep : ""))
        .replace(/[?&]$/, "")
      return `${cleaned}${hash ? `#${hash}` : ""}`
    }
  }

  const handleRegen = (id: string, type?: string) => { setDetailSku(null); simulateRegen([id], type ? [type] : undefined) }

  const simulateRegen = (ids: string[], types?: string[]) => {
    // ids are sku.id values — convert to actual SKU names for the backend filter
    const skuNames = ids.map(id => skus.find(s => s.id === id)?.sku).filter(Boolean) as string[]
    if (!skuNames.length) return
    // Guard: synchronous ref prevents double-click double-enqueue
    if (regenInFlight.current) return
    if (ids.some(id => regenProgress.has(id))) return
    regenInFlight.current = true
    setRegenProgress(new Set(ids))
    const ctrl = new AbortController()
    startGenStream(
      skuNames,
      e => {
        if (e.url) {
          const nextUrl = e.url
          withSuppressedSave(() => setSkus(prev => prev.map(s => {
            if (s.sku !== e.sku) return s
            const filtered = (s.outputImages ?? []).filter(img => stripCacheBust(img) !== stripCacheBust(nextUrl))
            return { ...s, outputImages: [...filtered, withCacheBust(nextUrl)] }
          })))
        }
        // No onSave PATCH for progress pct — live progress uses local state
      },
      () => {
        regenInFlight.current = false
        setRegenProgress(new Set()); setSelectedOutput(new Set())
        if (configSaveTimer.current) clearTimeout(configSaveTimer.current)
        jobsApi.saveConfig(Number(job.id), {
          ...configValuesRef.current,
          skus: skusRef.current.map(s => ({ id: s.id, sku: s.sku, folder: s.folder, avatar: s.avatar, types: [...s.types], images: s.images ? [...s.images] : [], outputImages: s.outputImages ?? [], model_override: s.model_override ?? null })),
        }).catch(() => {})
        jobsApi.getProgress(Number(job.id)).then(p => {
          const finalStatus = deriveFinalStatus(p)
          onSave(job.id, { status: finalStatus, progress: 100 })
          setLastProgress(p)
          setSkus(prev => prev.map(s => {
            const meta = p.skus.find(m => m.sku === s.sku)
            if (!meta) return s
            const safeOut = toImageArray(meta.output_images); const safeExist = toImageArray(s.outputImages)
            return { ...s, outputImages: safeOut.length ? safeOut : safeExist, thumbnail: meta.thumbnail ?? s.thumbnail, skipped: meta.skipped ?? s.skipped, skip_reason: meta.skip_reason ?? s.skip_reason }
          }))
        }).catch(() => { onSave(job.id, { status: 'error', progress: 100 }) })
      },
      () => { regenInFlight.current = false; setRegenProgress(new Set()) },
      ctrl,
      undefined,
      types,
    )
  }

  const [downloadingOrganized, setDownloadingOrganized] = useState(false)
  const [downloadOrganizedError, setDownloadOrganizedError] = useState<string | null>(null)

  // ── Download completed ZIP ─────────────────────────────────────────────────
  const handleDownloadCompleted = useCallback(async () => {
    setDownloadingCompleted(true)
    setDownloadCompletedError(null)
    try {
      await jobsApi.downloadCompletedZip(Number(job.id))
    } catch (e) {
      setDownloadCompletedError(e instanceof Error ? e.message : 'Download failed')
    } finally {
      setDownloadingCompleted(false)
    }
  }, [job.id])

  const handleDownloadOrganized = useCallback(async () => {
    if (downloadingOrganized) return
    setDownloadingOrganized(true)
    setDownloadOrganizedError(null)
    try {
      await jobsApi.downloadOrganized(Number(job.id), job.name)
    } catch (e) {
      setDownloadOrganizedError(e instanceof Error ? e.message : 'Download failed')
    } finally {
      setDownloadingOrganized(false)
    }
  }, [downloadingOrganized, job.id, job.name])

  // N/M counting: SKU-level counts (products with at least one output)
  const skusWithOutputs = skus.filter(s => (s.outputImages ?? []).length > 0)
  const hasCompletedOutputs = skusWithOutputs.length > 0

  const renderDownloadCompletedButton = () => {
    if (!hasCompletedOutputs) return null
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={handleDownloadCompleted}
          disabled={downloadingCompleted}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-sm font-medium hover:bg-secondary/60 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="w-3.5 h-3.5" />
          {downloadingCompleted ? 'Preparing ZIP…' : `Download completed products (${skusWithOutputs.length}/${skus.length})`}
        </button>
        {downloadCompletedError && (
          <span className="text-xs text-destructive">{downloadCompletedError}</span>
        )}
      </div>
    )
  }

  const STEPS = [
    { n: 1, label: 'Configure' },
    { n: 2, label: 'Analysis' },
    { n: 3, label: 'Organize' },
    { n: 4, label: 'Assign avatar & type' },
    { n: 5, label: 'Check & Validate' },
  ]

  // Step 1 — Analysis
  const [analyzing, setAnalyzing]       = useState(false)
  const [analysisDone, setAnalysisDone] = useState(false)
  const [products, setProducts]         = useState<(ProductGroupDTO & { group: string; errors: string[] })[]>([])
  const [catalogueProds, setCatalogueProds] = useState<import('@/lib/api').CatalogueProductDTO[]>([])
  const [modulePrompts, setModulePrompts] = useState<import('@/lib/api').ModulePromptDTO[]>([])
  const [generating, setGenerating] = useState(false)
  const [genProgress, setGenProgress] = useState<{ done: number; total: number; lastSku: string; errors: string[] } | null>(null)
  const [skuErrors, setSkuErrors] = useState<Map<string, string[]>>(new Map())
  const [streamError, setStreamError] = useState<string | null>(null)
  const [downloadingCompleted, setDownloadingCompleted] = useState(false)
  const [downloadCompletedError, setDownloadCompletedError] = useState<string | null>(null)
  const genAbortRef = useRef<AbortController | null>(null)


  // suppressSaveRef: token counter — prevents saveConfig from firing during backend-driven setSkus
  // Uses a counter (not bool) so overlapping concurrent backfills don't clear each other's guard early
  const suppressSaveRef = useRef(0)
  const withSuppressedSave = useCallback((fn: () => void) => {
    suppressSaveRef.current++
    fn()
    setTimeout(() => { suppressSaveRef.current = Math.max(0, suppressSaveRef.current - 1) }, 0)
  }, [])

  // Throttle ref for SSE-triggered getProgress (max 1 call per 2 s)
  const lastProgressFetchRef = useRef(0)

  // Normalize SKU/ref for fuzzy matching
  const normalizeSku = useCallback((s: string) => s.toLowerCase().replace(/[\s_\-]+/g, ''), [])

  // backfillFromProgress: canonical merge from getProgress snapshot into skus state
  // Uses withSuppressedSave so backend-driven setSkus does not trigger saveConfig
  const backfillFromProgress = useCallback((p: ProgressSnapshot) => {
    withSuppressedSave(() => setSkus(prev => prev.map(s => {
      const meta =
        p.skus.find(m => m.id !== undefined && String(m.id) === String(s.id)) ??
        p.skus.find(m => m.sku === s.sku || m.ref === s.sku) ??
        p.skus.find(m => normalizeSku(m.sku) === normalizeSku(s.sku) || normalizeSku(m.ref ?? '') === normalizeSku(s.sku))
      if (!meta) return s
      return {
        ...s,
        outputImages: (() => { const o = toImageArray(meta.output_images); const e = toImageArray(s.outputImages); return o.length ? o : e })(),
        thumbnail: meta.thumbnail ?? s.thumbnail,
        skipped: meta.skipped ?? s.skipped,
        skip_reason: meta.skip_reason ?? s.skip_reason,
        ...(meta.type_results  !== undefined ? { type_results: meta.type_results }   : {}),
        // types are the USER's selection (config-owned). The meta is a server
        // snapshot frozen at organize time and must NEVER overwrite them:
        // empty wiped the selection (job 34), stale reverted it (job 39).
        ...(meta.done          !== undefined ? { done: meta.done }                   : {}),
        ...(meta.total_needed  !== undefined ? { total_needed: meta.total_needed }   : {}),
        ...(meta.total_ops     !== undefined ? { total_ops: meta.total_ops }         : {}),
        ...(meta.completed_ops !== undefined ? { completed_ops: meta.completed_ops } : {}),
      }
    })))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withSuppressedSave])

  // fetchProgressThrottled: SSE-triggered getProgress, max 1 call per 2 s
  const fetchProgressThrottled = useCallback(() => {
    const now = Date.now()
    if (now - lastProgressFetchRef.current < 2000) return
    lastProgressFetchRef.current = now
    jobsApi.getProgress(Number(job.id)).then(p => {
      backfillFromProgress(p)
      if (p.total_needed > 0)
        setGenProgress(prev => ({
          done: p.total_done, total: p.total_needed,
          lastSku: prev?.lastSku ?? '', errors: prev?.errors ?? [],
        }))
    }).catch(() => {})
  }, [job.id, backfillFromProgress])


  const _AVATAR_ERRORS = ["missing_avatar_file", "unknown_avatar_id", "invalid_avatar_path"] as const

  // Returns true if any type_result has an avatar-resolution error (retry would fail again without a fix)
  const hasAvatarError = useCallback((sku: SkuEntry): boolean =>
    Object.values(sku.type_results ?? {}).some(v => _AVATAR_ERRORS.includes(v.error as typeof _AVATAR_ERRORS[number]))
  , [])

  // Returns human-readable failure reason from type_results, falling back to skuErrors stream data
  const getSkuFailureReason = useCallback((sku: SkuEntry): string => {
    const tr = sku.type_results
    if (tr) {
      const entries = Object.values(tr)
      const missingFile = entries.find(v => v.error === 'missing_avatar_file')
      if (missingFile) return `Missing avatar file: ${missingFile.avatar_name ?? `id ${missingFile.avatar_id}`}`
      const unknownId = entries.find(v => v.error === 'unknown_avatar_id')
      if (unknownId) return `Unknown avatar id: ${unknownId.avatar_id}`
      const invalidPath = entries.find(v => v.error === 'invalid_avatar_path')
      if (invalidPath) return 'Invalid avatar path'
      const firstErr = entries.find(v => v.error)
      if (firstErr?.error) return firstErr.error
    }
    return skuErrors.get(sku.sku)?.join(' · ') ?? 'Generation failed'
  }, [skuErrors])

  // 5 s polling while generating: ensures progress displays even during long API calls
  useEffect(() => {
    if (!generating) return
    const id = setInterval(() => {
      jobsApi.getProgress(Number(job.id)).then(p => {
        backfillFromProgress(p)
        if (p.total_needed > 0)
          setGenProgress(prev => ({
            done: p.total_done, total: p.total_needed,
            lastSku: prev?.lastSku ?? '', errors: prev?.errors ?? [],
          }))
        const isTerminal = isTerminalJobStatus(p.status)
        const isComplete = p.total_needed > 0 && p.total_done >= p.total_needed
        if (isTerminal || isComplete) {
          setGenerating(false)
          setQueuePosition(null)
        }
      }).catch(() => {})
    }, 5000)
    return () => clearInterval(id)
  }, [generating, job.id, backfillFromProgress])

  const _pushKey = `push_${job.id}`
  const [pushState, setPushStateRaw] = useState<{ provider: 'google' | 'dropbox'; done: number; total: number; url?: string } | null>(() => {
    try { const s = localStorage.getItem(_pushKey); return s ? JSON.parse(s) : null } catch { return null }
  })
  const pushStateRef = useRef(pushState)
  const setPushState = (v: typeof pushState | ((p: typeof pushState) => typeof pushState)) => {
    setPushStateRaw(prev => {
      const next = typeof v === 'function' ? v(prev) : v
      pushStateRef.current = next
      if (next?.url) localStorage.setItem(_pushKey, JSON.stringify(next))
      else if (next === null) localStorage.removeItem(_pushKey)
      return next
    })
  }
  useEffect(() => {
    if (!pushState || pushState.url) return
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [!!pushState, !!pushState?.url])

  // Warn on browser back/refresh during generation
  useEffect(() => {
    if (!generating) return
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [generating])

  useEffect(() => {
    return () => { pollCancelledRef.current = true }
  }, [])

  useEffect(() => {
    cliModulesApi.prompts('add_product_on_avatar').then(ps => {
      setModulePrompts(ps)
      // Pick the highest-version active prompt (in case of duplicates)
      const active = ps.filter(p => p.is_active).sort((a, b) => b.version - a.version)[0]
      void active // prompt IDs tracked in modulePrompts state
    }).catch(() => {})
  }, [])
  useEffect(() => {
    catalogueApi.list().then(cats => {
      if (!cats.length) return
      return Promise.all(cats.map(c => catalogueApi.products(c.id).catch(() => [] as import('@/lib/api').CatalogueProductDTO[])))
        .then(pages => setCatalogueProds(pages.flat()))
    }).catch(() => {})
  }, [])
  const [, setAnalysisErrors] = useState<string[]>([])
  const [analyzeTotal, setAnalyzeTotal] = useState(0)
  const [imgsPerProduct, setImgsPerProduct] = useState(job.fileCount && job.skuCount ? Math.round(job.fileCount / job.skuCount) : 8)
  const [productList, setProductList]   = useState<{ group: string; marque_image: string; ref_image: string; total_images: number }[]>([])
  const pollCancelledRef = useRef(false)
  const reloadInFlightRef = useRef(false)

  const triggerReload = async (reason: string) => {
    if (reloadInFlightRef.current) return
    reloadInFlightRef.current = true
    console.log('[triggerReload]', reason)
    try {
      const j = await jobsApi.get(Number(job.id))
      onSave(job.id, { status: j.status as Job['status'], skuCount: j.sku_count, fileCount: j.file_count })
    } catch { /* ignore */ } finally {
      reloadInFlightRef.current = false
    }
  }

  const pollTask = (
    taskId: string,
    onDone?: (result: Record<string, unknown> | null) => void,
    onProgress?: (t: import('@/lib/api').TaskStatus) => void,
  ) => {
    pollCancelledRef.current = false
    const tick = async () => {
      if (pollCancelledRef.current) return
      try {
        const t = await tasksApi.getStatus(taskId)
        if (pollCancelledRef.current) return
        if (t.status === 'not_found') {
          await triggerReload('backend_restart')
          return
        }
        if ('done' in t && 'total' in t && t.total > 0)
          setAnalyzeTotal(t.total)
        if ('partial_products' in t) onProgress?.(t as import('@/lib/api').TaskStatus)
        if (t.status === 'done' || t.status === 'error' || t.reload_required) {
          if (t.status === 'done' && t.reload_required) await triggerReload(t.reload_reason ?? 'task_done')
          onDone?.(t.result ?? null)
          return
        }
        setTimeout(tick, 2000)
      } catch (e) {
        if (pollCancelledRef.current) return
        console.error('[task-poll] failed', e)
        setTimeout(tick, 2000)
      }
    }
    tick()
  }

  useEffect(() => {
    jobsApi.listProducts(Number(job.id), imgsPerProduct)
      .then(setProductList)
      .catch(() => {})
  }, [job.id, imgsPerProduct])

  // Build naming tokens dynamically from CSV schema mapping
  useEffect(() => {
    const BASE = [{ token: '{index}', label: 'Index' }, { token: '{raw_name}', label: 'Nom brut' }]
    // DB field → value accessor on CatalogueProductDTO
    const FIELD_ACCESSOR: Record<string, (p: import('@/lib/api').CatalogueProductDTO) => string> = {
      brand:       p => p.brand ?? '',
      product_ref: p => p.product_ref ?? '',
      inventory_id:p => p.ref_key,
      gender:      p => p.gender ?? '',
      avatar_type: p => p.avatar_type ?? '',
    }
    Promise.all([catalogueApi.getSchema(), catalogueApi.list()]).then(async ([schema, cats]) => {
      const mapping = schema.mapping
      if (!mapping || cats.length === 0) { setCatalogueTokens(BASE); return }
      // Build tokens: { token: '{Marque}', label: 'Marque' } from mapping keys
      const FIELD_LABELS: Record<string, string> = {
        brand: 'Marque', product_ref: 'Produit', inventory_id: 'Ref', gender: 'Sexe', avatar_type: 'Type',
      }
      const catTokens = Object.entries(mapping).map(([field, colName]) => ({
        token: `{${FIELD_LABELS[field] ?? colName}}`,
        label: FIELD_LABELS[field] ?? colName,
        field,
      }))
      setCatalogueTokens([...catTokens.map(t => ({ token: t.token, label: t.label })), ...BASE])
      // Fetch one real product for preview
      const prods = await catalogueApi.products(cats[0].id).catch(() => [])
      setCatalogueProds(prods)
      const p = prods.find(r => r.brand || r.product_ref) ?? prods[0]
      if (p) {
        const ex: Record<string, string> = {}
        for (const { token, field } of catTokens) {
          const accessor = FIELD_ACCESSOR[field]
          if (accessor) ex[token] = accessor(p).replace(/\s+/g,'_').toUpperCase()
        }
        setNamingExample({ ...ex })
      }
    }).catch(() => setCatalogueTokens(BASE))
  }, [])

  // Load persisted config on mount
  useEffect(() => {
    jobsApi.getConfig(Number(job.id)).then((cfg: Record<string, unknown>) => {
      if (!cfg || Object.keys(cfg).length === 0) {
        // New job — seed format/resolution from tenant defaults (Settings → Input & Output)
        settingsApi.get().then(st => {
          const cp = (st.custom_properties ?? {}) as Record<string, unknown>
          if (cp.outputFormat === 'jpeg' || cp.outputFormat === 'png' || cp.outputFormat === 'webp') setOutputFormat(cp.outputFormat)
          if (cp.outputResolution === '1080x1920' || cp.outputResolution === '1440x2560' || cp.outputResolution === '2160x3840') setOutputRes(cp.outputResolution)
          if (typeof cp.maxInputPx === 'number') setJobMaxInputPx(cp.maxInputPx)
          if (typeof cp.batchMode === 'boolean') setBatchMode(cp.batchMode)
          if (cp.naming === 'sku' || cp.naming === 'marque-sku' || cp.naming === 'custom') setNaming(cp.naming)
        }).catch(() => {})
      }
      if (cfg && Object.keys(cfg).length > 0) {
      if (typeof cfg.step === 'number') setStep(cfg.step)
      if (typeof cfg.output_format === 'string') setOutputFormat(cfg.output_format as 'jpeg'|'png'|'webp')
      if (typeof cfg.output_resolution === 'string') setOutputRes(cfg.output_resolution as '1080x1920'|'1440x2560'|'2160x3840')
      if (typeof cfg.naming === 'string') setNaming(cfg.naming as 'sku'|'marque-sku'|'custom')
      if (typeof cfg.naming_custom === 'string') setNamingCustom(cfg.naming_custom)
      if (typeof cfg.batch_mode === 'boolean') setBatchMode(cfg.batch_mode)
      if (typeof cfg.max_input_px === 'number') setJobMaxInputPx(cfg.max_input_px)
      if (Array.isArray(cfg.naming_history)) setNamingHistory(cfg.naming_history as string[])
      if (typeof cfg.imgs_per_product === 'number') setImgsPerProduct(cfg.imgs_per_product)
      if (typeof cfg.organize_by === 'string') setOrganizeBy(cfg.organize_by as 'ref'|'marque'|'marque-ref-base'|'marque-forme')
      if (Array.isArray(cfg.products) && cfg.products.length > 0) {
        const toUrl = (p: string) => {
          const s = p.startsWith('/clients/') ? p : (p.indexOf('/public/') !== -1 ? p.slice(p.indexOf('/public/') + 7) : p)
          return s.replace('/organized_tmp/', '/organized/')
        }
        const normalized = (cfg.products as (ProductGroupDTO & { group: string; errors: string[] })[])
          .map(p => ({ ...p, images: (p.images || []).map(toUrl) }))
        setProducts(normalized)
        setAnalysisDone(true)
      }
      if (cfg.organize_result) setOrganizeResult(cfg.organize_result as OrganizeResultDTO)
      if (Array.isArray(cfg.skus) && cfg.skus.length > 0) {
        setSkus((cfg.skus as { id: string; sku: string; folder: string; avatar: string | null; types: string[]; images?: string[]; outputImages?: string[]; model_override?: string | null }[]).map(s => {
          const imgs = s.images?.length ? s.images : []
          return {
            ...s,
            types: toTypesSet(s.types),
            properties: {},
            images: imgs.length >= 2 ? [imgs[0], imgs[1]] as [string, string] : imgs.length === 1 ? [imgs[0], imgs[0]] as [string, string] : null,
            outputImages: s.outputImages ?? [],
            model_override: s.model_override ?? null,
          }
        }))
      }
      }
      // Backfill from server meta on mount for all non-initial statuses.
      // configLoadedRef is set to true AFTER backfill to prevent saveConfig firing during load.
      if (['processing', 'queued', 'done', 'partial', 'error'].includes(job.status)) {
        jobsApi.getProgress(Number(job.id)).then(p => {
          // Update live progress for active jobs
          if (job.status === 'processing' || job.status === 'queued') {
            if (p.total_needed > 0) {
              setGenProgress({ done: p.total_done, total: p.total_needed, lastSku: '', errors: [] })
              onSave(job.id, { status: p.status as never, progress: p.pct })
            }
          }
          // Backfill all SKU fields from canonical backend meta
          backfillFromProgress(p)
          // For completed jobs, store snapshot for completion display
          if (job.status === 'done' || job.status === 'partial' || job.status === 'error') {
            setLastProgress(p)
          }
          // Enable saves only after initial backfill is applied
          configLoadedRef.current = true
        }).catch(() => { configLoadedRef.current = true })
      } else {
        // No backfill needed for not_started/not_configured — enable saves immediately
        configLoadedRef.current = true
      }

      if (job.status === 'processing') {
        setGenerating(true)
        jobsApi.getProgress(Number(job.id)).then(p => {
          backfillFromProgress(p)
          if (p.total_needed > 0)
            setGenProgress({ done: p.total_done, total: p.total_needed, lastSku: '', errors: [] })
        }).catch(() => {})
      }
    }).catch(() => { configLoadedRef.current = true })
  }, [job.id])

  // saveConfig defined after organizeBy/organizeResult are declared (below)

  const [retryingGroups, setRetryingGroups] = useState<Set<string>>(new Set())
  const [analysisFilter, setAnalysisFilter] = useState<'all' | 'found' | 'notfound' | 'duplicates' | 'errors'>('all')
  const [retryDone, setRetryDone] = useState(0)
  const [manualPickProduct, setManualPickProduct] = useState<(ProductGroupDTO & { group: string; errors: string[] }) | null>(null)

  const _runAnalysis = async (groups?: string[]) => {
    pollCancelledRef.current = true  // cancel any previous poll
    const isRetry = !!groups
    setAnalyzing(true)
    if (groups) {
      setRetryingGroups(new Set(groups))
      setRetryDone(0)
    } else {
      setAnalysisDone(false)
      setProducts([])
      setAnalysisErrors([])
      setRetryDone(0)
    }
    try {
      const { task_id } = await jobsApi.startAnalyze(Number(job.id), {
        imgs_per_product: imgsPerProduct,
        ...(groups ? { groups, existing_products: products } : {}),
      })
      pollTask(task_id, (result) => {
        if (!result) { setAnalysisDone(true); setAnalyzing(false); setRetryingGroups(new Set()); return }
        const prods = (result.products as (ProductGroupDTO & { group: string; errors: string[] })[]) ?? []
        if (!isRetry) {
          setProducts(prods)
        } else {
          setProducts(prev => {
            const byGroup = new Map(prods.map(p => [p.group, p]))
            const merged = prev.map(p => byGroup.has(p.group) ? byGroup.get(p.group)! : p)
            // prev.map() can only update existing entries — groups newly
            // analyzed via "Continue"/"Retry" (never seen in prev before)
            // must be appended, or they're silently dropped.
            const newOnes = prods.filter(p => !prev.some(pp => pp.group === p.group))
            return [...merged, ...newOnes]
          })
        }
        const errs = prods.flatMap(p => (p.errors ?? []).map((e: string) => `${p.group}: ${e}`))
        if (errs.length) setAnalysisErrors(prev => [...prev, ...errs])
        setAnalyzeTotal(prods.length)
        setRetryDone(prods.length)
        setRetryingGroups(new Set())
        setAnalysisDone(true)
        setAnalyzing(false)
      }, (t) => {
        const partial = Array.isArray(t.partial_products)
          ? t.partial_products as (ProductGroupDTO & { group: string; errors: string[] })[]
          : []
        if (!partial.length) return
        if (!isRetry) {
          setProducts(partial)
        } else {
          setProducts(prev => {
            const byGroup = new Map(partial.map(p => [p.group, p]))
            const merged = prev.map(p => byGroup.has(p.group) ? byGroup.get(p.group)! : p)
            const newOnes = partial.filter(p => !prev.some(pp => pp.group === p.group))
            return [...merged, ...newOnes]
          })
          setRetryDone(partial.length)
        }
      })
    } catch (err) {
      setAnalysisErrors(prev => [...prev, String(err)])
      setAnalysisDone(true)
      setAnalyzing(false)
      setRetryingGroups(new Set())
    }
  }

  const startAnalysis = () => _runAnalysis()
  const retryGroups = (groups: string[]) => _runAnalysis(groups)
  const retryAllNotFound = () => {
    const notFound = products.filter(p => !p.in_catalogue && !p.errors?.length).map(p => p.group)
    if (notFound.length) retryGroups(notFound)
  }
  const removeProduct = (group: string) => {
    setProducts(prev => {
      const next = prev.filter(p => p.group !== group)
      onSave(job.id, { skuCount: next.length })
      return next
    })
  }

  const continueScan = () => {
    const done = new Set(products.map(p => p.group))
    const remaining = productList.filter(pl => !done.has(pl.group)).map(pl => pl.group)
    if (remaining.length) _runAnalysis(remaining)
  }

  const applyManualPick = (group: string, cat: import('@/lib/api').CatalogueSearchResultDTO) => {
    setProducts(prev => prev.map(p =>
      p.group === group
        ? { ...p, in_catalogue: true, brand: cat.brand, product_ref: cat.product_ref, inventory_id: cat.inventory_id, catalogue_name: cat.catalogue_name, avatar_type: cat.avatar_type }
        : p
    ))
    setManualPickProduct(null)
  }

  const applyManualMarque = (group: string, marque: string) => {
    setProducts(prev => prev.map(p =>
      p.group === group ? { ...p, extracted_marque: marque } : p
    ))
    setManualPickProduct(null)
  }

  // Step 2 — Organize
  const [organizeBy, setOrganizeBy]     = useState<'ref' | 'marque' | 'marque-ref-base' | 'marque-forme'>('marque')
  const [organizing, setOrganizing]     = useState(false)
  const [organizeResult, setOrganizeResult] = useState<OrganizeResultDTO | null>(null)
  const [organizeErrors, setOrganizeErrors] = useState<string[]>([])

  // Enrich products with forme from catalogueProds when entering organize step
  useEffect(() => {
    if (step !== 3 || !catalogueProds.length) return
    setProducts(prev => prev.map(p => {
      if (p.forme) return p
      const match = catalogueProds.find(c => c.product_ref === (p.product_ref || p.extracted_ref || p.ref))
      return match?.forme ? { ...p, forme: match.forme } : p
    }))
  }, [step, catalogueProds])

  const startOrganize = async () => {
    setOrganizing(true)
    try {
      const result = await jobsApi.organize(Number(job.id), { organize_by: organizeBy, products, naming, naming_custom: namingCustom || undefined, output_format: outputFormat })
      setOrganizeResult(result)
      setOrganizeErrors(result.errors)
      if (result.product_images) {
        // Update images from organize result, keyed by group — the sole
        // stable per-photo identity. Never deduplicate here: distinct
        // photographed products sharing a printed reference must survive
        // as separate entries (see OrganizeButton's warning instead).
        setProducts(prev => prev.map(p => {
          const imgs = result.product_images![p.group]?.map(u => u.replace('/organized_tmp/', '/organized/'))
          return imgs?.length ? { ...p, images: imgs } : p
        }))
      }
    } catch (err) {
      setOrganizeErrors([err instanceof Error ? err.message : 'Organize failed'])
    }
    setOrganizing(false)
  }

  // Keep skusRef in sync so saveConfig always serialises the latest SKU assignments,
  // and trigger a save whenever avatar/type assignments change
  useEffect(() => {
    skusRef.current = skus
    if (configLoadedRef.current && skus.length) {
      saveConfig()
      // Only auto-flag as not_configured — never auto-clear it.
      // Clearing (→ not_started) is intentional and happens at the Save button.
      if (job.status === 'not_started' && skus.some(s => !s.avatar)) {
        onSave(job.id, { status: 'not_configured' })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skus])

  // Keep ref in sync with latest values so saveConfig needs no volatile deps
  const saveConfig = useCallback(() => {
    if (!configLoadedRef.current) return
    if (suppressSaveRef.current > 0) return  // suppressed during backend-driven setSkus
    if (configSaveTimer.current) clearTimeout(configSaveTimer.current)
    configSaveTimer.current = setTimeout(() => {
      jobsApi.saveConfig(Number(job.id), {
        ...configValuesRef.current,
        skus: skusRef.current.map(s => ({
          id: s.id, sku: s.sku, folder: s.folder, avatar: s.avatar,
          types: [...s.types],
          images: s.images ? [...s.images] : [],
          // outputImages are server-owned — never sent; the backend merge preserves them
          model_override: s.model_override ?? null,
        })),
      }).catch(() => {})
    }, 800)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id])

  useEffect(() => {
    configValuesRef.current = {
      step,
      output_format: outputFormat,
      output_resolution: outputRes,
      naming,
      naming_custom: namingCustom,
      naming_history: namingHistory,
      imgs_per_product: imgsPerProduct,
      organize_by: organizeBy,
      products,
      organize_result: organizeResult,
      batch_mode: batchMode,
      max_input_px: jobMaxInputPx,
    }
    saveConfig()
  }, [step, outputFormat, outputRes, naming, namingCustom, namingHistory, imgsPerProduct, organizeBy, products, organizeResult, batchMode, jobMaxInputPx, saveConfig])

  const buildSkus = useCallback((force: boolean) => {
    if (!products.length) return
    setSkus(prev => {
      const prevBySku = Object.fromEntries(prev.map(s => [s.sku, s]))
      // Never merge/drop products by resolved ref — genuinely different
      // photographed products can share an identical printed reference
      // (see OrganizeButton's warning). Every product stays its own SKU;
      // only its display name gains a group-based suffix when its base ref
      // is shared by more than one surviving product.
      const baseRefCounts = new Map<string, number>()
      for (const p of products) {
        const base = p.product_ref || p.ref
        baseRefCounts.set(base, (baseRefCounts.get(base) ?? 0) + 1)
      }
      return products.map((p, i) => {
        const base = p.product_ref || p.ref
        const skuName = (baseRefCounts.get(base) ?? 0) > 1
          ? `${base}_${p.group.replace(/^product_/, '')}`
          : base
        const existing = prevBySku[skuName]
        const matchedRule = skuRules.find(r => r.prefixes.some(pr => pr && base.toUpperCase().startsWith(pr.value)))
        const catProd = catalogueProds.find(c => c.product_ref === base)
        const gender = (catProd?.gender ?? p.gender)?.toLowerCase() ?? null
        const genderKey = gender?.includes('femme') || gender?.includes('female') || gender === 'f' ? 'female'
          : gender?.includes('homme') || gender?.includes('male') || gender === 'm' ? 'male'
          : gender?.includes('enfant') || gender?.includes('kid') || gender?.includes('child') ? 'kids'
          : null
        const genderAvatar = genderKey
          ? (avatars.find(a => a.folder_name && a.folder_name.toLowerCase() === genderKey)
            ?? avatars.find(a => a.folder_name && a.folder_name.toLowerCase().includes(genderKey))
            ?? null)
          : null
        const catalogueAvatar = genderAvatar ? String(genderAvatar.id) : null
        // force=true (manual btn): always apply catalogue; force=false (auto): keep existing if already set
        const autoAvatar = force
          ? (catalogueAvatar ?? matchedRule?.avatarId ?? existing?.avatar ?? null)
          : (existing?.avatar ?? matchedRule?.avatarId ?? catalogueAvatar ?? null)
        const imgs = p.images
        const rawType = p.avatar_type || catProd?.avatar_type || null
        // Empty set when the catalogue has no type info — never a forced default,
        // so the user's manual selection (existing.types) survives auto rebuilds.
        const catalogueTypes: Set<'optique' | 'solaire'> = new Set(([
          rawType?.toLowerCase().includes('optique') ? 'optique' : null,
          rawType?.toLowerCase().includes('solaire') ? 'solaire' : null,
        ].filter(Boolean) as ('optique' | 'solaire')[]))
        const defaultTypes = new Set<'optique' | 'solaire'>(['optique'])
        // Same semantics as `avatar` above: force=true (Auto-assign button) lets the
        // catalogue overwrite; force=false (auto rebuild on every load) keeps the
        // user's selection and only fills gaps.
        const autoTypes = force
          ? (catalogueTypes.size ? catalogueTypes : existing?.types ?? defaultTypes)
          : (existing?.types ?? (catalogueTypes.size ? catalogueTypes : defaultTypes))
        return {
          id: existing?.id ?? `sku-${i}`,
          // Catalogue brand wins once matched (same rule as organize's brand folders);
          // OCR text is only a fallback label for unmatched products.
          folder: (p.in_catalogue && p.brand) || p.extracted_marque || existing?.folder || p.group,
          sku: skuName,
          avatar: autoAvatar,
          types: autoTypes,
          properties: existing?.properties ?? {},
          images: imgs.length >= 2 ? [imgs[0], imgs[1]] as [string, string] : imgs.length === 1 ? [imgs[0], imgs[0]] as [string, string] : null,
          outputImages: existing?.outputImages ?? [],
        }
      })
    })
  }, [products, skuRules, catalogueProds, avatars])

  useEffect(() => { buildSkus(false) }, [buildSkus])

  const assignedCount = skus.filter(s => s.avatar).length
  const selectedArr = [...selected]
  const isCloud = job.source === 'drive' || job.source === 'dropbox'


  const filteredSkus = useMemo(() =>
    search.trim() ? skus.filter(s => s.sku.toLowerCase().includes(search.toLowerCase()) || s.folder.includes(search)) : skus
  , [skus, search])

  // Group skus for grid view
  const grouped: { label: string; avatarId: string | null; items: SkuEntry[] }[] = useMemo(() => gridGroup === 'avatar'
    ? [
        { label: 'Unassigned', avatarId: null, items: filteredSkus.filter(s => !s.avatar) },
        ...avatars.map(a => ({ label: a.name, avatarId: a.id, items: filteredSkus.filter(s => s.avatar === a.id) })),
      ]
    : gridGroup === 'sku-type'
    ? (() => {
        const map = new Map<string, SkuEntry[]>()
        filteredSkus.forEach(s => {
          const key = s.folder || '—'
          if (!map.has(key)) map.set(key, [])
          map.get(key)!.push(s)
        })
        return [...map.entries()].map(([key, items]) => ({ label: key, avatarId: null, items }))
      })()
    : (() => {
        const buckets: { label: string; items: SkuEntry[] }[] = [
          { label: 'Optique & Solaire', items: filteredSkus.filter(s => hasType(s, 'optique') && hasType(s, 'solaire')) },
          { label: 'Optique',           items: filteredSkus.filter(s => hasType(s, 'optique') && !hasType(s, 'solaire')) },
          { label: 'Solaire',           items: filteredSkus.filter(s => hasType(s, 'solaire') && !hasType(s, 'optique')) },
          { label: 'Untyped',           items: filteredSkus.filter(s => toTypesSet(s.types).size === 0) },
        ]
        return buckets.map(b => ({ ...b, avatarId: null }))
      })().filter(g => g.avatarId === null ? g.items.length > 0 : true)
  , [filteredSkus, gridGroup, avatars])

  return (
    <div className="flex flex-col gap-5 pb-10">
      {/* Job name header — always visible */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 group">
          {editingName ? (
            <>
              <input ref={nameInputRef} value={name} onChange={e => setName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setEditingName(false) }}
                onBlur={() => setEditingName(false)}
                className="text-2xl font-semibold bg-transparent border-0 border-b-2 border-foreground/30 outline-none focus:ring-0 py-0.5 flex-1 transition-colors" />
              <button onClick={() => setEditingName(false)} className="text-muted-foreground hover:text-foreground shrink-0"><Check className="w-4 h-4" /></button>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-semibold">{name}</h1>
              <button onClick={() => setEditingName(true)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity shrink-0"><Pencil className="w-4 h-4" /></button>
              <div className="flex items-center gap-1.5 ml-2 px-2 py-0.5 rounded-md border bg-secondary/40 text-xs text-muted-foreground shrink-0">
                {sourceIcon(job.source)}
                <span>{job.source === 'drive' ? 'Google Drive' : job.source === 'dropbox' ? 'Dropbox' : 'Local'}</span>
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-3">
          {statusBadge(job.status)}
<span className="text-xs text-muted-foreground">{(products.length || skus.length || job.skuCount)} SKUs · {job.createdAt}</span>
        </div>
      </div>

      {/* ── DONE view ── */}
      {job.status === 'done' && !editMode && (
        <div className="flex flex-col gap-5">
          {/* Output action bar */}
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={handleDownloadOrganized} disabled={downloadingOrganized} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-foreground text-background text-sm font-medium hover:bg-foreground/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              {downloadingOrganized ? 'Preparing ZIP…' : 'Download ZIP'}
            </button>
            {downloadOrganizedError && <span className="text-xs text-destructive">{downloadOrganizedError}</span>}
            {renderDownloadCompletedButton()}
            {job.source !== 'dropbox' && (cloud?.google.connected || pushState?.provider === 'google') && (
              pushState?.provider === 'google' && pushState.url
                ? <a href={pushState.url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-green-500/50 text-sm font-medium text-green-600 hover:bg-green-50/10 transition-colors">
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none"><path d="M4.5 19L8.5 12L4 5H12L16 12L12 19H4.5Z" fill="#0F9D58"/><path d="M16 12L20 19H8L12 12H16Z" fill="#4285F4"/><path d="M8.5 12L4 5H16L20 12H8.5Z" fill="#FFBB00"/></svg>Open in Drive
                  </a>
                : <button
                    disabled={!!pushState}
                    onClick={() => {
                      setPushState({ provider: 'google', done: 0, total: 0 })
                      cloudApi.startPush(Number(job.id), 'google').then(({ task_id, total }) => {
                        setPushState(s => s ? { ...s, total } : s)
                        pollTask(task_id, () => {
                          setPushState({ provider: 'google', done: 0, total: 0, url: 'https://drive.google.com' })
                        })
                      }).catch(() => setPushState(null))
                    }}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg border text-sm font-medium hover:bg-secondary/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    {pushState?.provider === 'google'
                      ? <><svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>{pushState.total > 0 ? `${pushState.done}/${pushState.total}` : '…'}</>
                      : <><svg viewBox="0 0 24 24" className="w-4 h-4" fill="none"><path d="M4.5 19L8.5 12L4 5H12L16 12L12 19H4.5Z" fill="#0F9D58"/><path d="M16 12L20 19H8L12 12H16Z" fill="#4285F4"/><path d="M8.5 12L4 5H16L20 12H8.5Z" fill="#FFBB00"/></svg>Transfer to Drive</>}
                  </button>
            )}
            {job.source !== 'drive' && (cloud?.dropbox.connected || pushState?.provider === 'dropbox') && (
              pushState?.provider === 'dropbox' && pushState.url
                ? <a href={pushState.url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-blue-500/50 text-sm font-medium text-blue-600 hover:bg-blue-50/10 transition-colors">
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#0061FF"><path d="M6 2L1 5.5l5 3.5 5-3.5L6 2zM16 2l-5 3.5 5 3.5 5-3.5L16 2zM1 12.5L6 16l5-3.5L6 9l-5 3.5zM16 9l-5 3.5 5 3.5 5-3.5L16 9zM6 17.5l5 3.5 5-3.5-5-3.5-5 3.5z"/></svg>Open in Dropbox
                  </a>
                : <button
                    disabled={!!pushState}
                    onClick={() => {
                      setPushState({ provider: 'dropbox', done: 0, total: 0 })
                      cloudApi.startPush(Number(job.id), 'dropbox').then(({ task_id, total }) => {
                        setPushState(s => s ? { ...s, total } : s)
                        pollTask(task_id, () => {
                          setPushState({ provider: 'dropbox', done: 0, total: 0, url: 'https://www.dropbox.com/home' })
                        })
                      }).catch(() => setPushState(null))
                    }}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg border text-sm font-medium hover:bg-secondary/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    {pushState?.provider === 'dropbox'
                      ? <><svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>{pushState.total > 0 ? `${pushState.done}/${pushState.total}` : '…'}</>
                      : <><svg viewBox="0 0 24 24" className="w-4 h-4" fill="#0061FF"><path d="M6 2L1 5.5l5 3.5 5-3.5L6 2zM16 2l-5 3.5 5 3.5 5-3.5L16 2zM1 12.5L6 16l5-3.5L6 9l-5 3.5zM16 9l-5 3.5 5 3.5 5-3.5L16 9zM6 17.5l5 3.5 5-3.5-5-3.5-5 3.5z"/></svg>Transfer to Dropbox</>}
                  </button>
            )}
            <button onClick={() => setEditMode(true)} className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors">
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
          </div>

          {lastProgress && (() => {
            const { total_done, total_needed, failed_count, skipped_count, duration_ms, cost } = lastProgress
            const totalCost = cost ? cost.vision_eur + cost.generation_eur : 0
            return (
              <p className="text-xs text-muted-foreground -mt-1">
                Generation finished
                {' · '}{total_done}/{total_needed} images generated
                {failed_count > 0 && <> · {failed_count} failed</>}
                {skipped_count > 0 && <> · {skipped_count} skipped</>}
                {duration_ms != null && <> · {formatDuration(duration_ms)}</>}
                {totalCost > 0 && cost && <> · €{totalCost.toFixed(2)} <span className="opacity-70">(vision €{cost.vision_eur.toFixed(2)} · generation €{cost.generation_eur.toFixed(2)})</span></>}
              </p>
            )
          })()}

          <OutputToolbar />

          {/* SKU output grid */}
          {sortAndGroup(skus).map(({ label, items }) => (
            <div key={label ?? 'all'} className="flex flex-col gap-2">
              {label && <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>}
              <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-3">
            {items.map(sku => {
              const isSel = selectedOutput.has(sku.id)
              const imgs = sku.outputImages ?? []
              return (
                <div key={sku.id}
                  onClick={e => {
                    if (e.shiftKey && lastClickedOutputId.current) {
                      const ids = skus.map(s => s.id)
                      const a = ids.indexOf(lastClickedOutputId.current), b = ids.indexOf(sku.id)
                      if (a !== -1 && b !== -1) { setSelectedOutput(prev => { const s = new Set(prev); ids.slice(Math.min(a,b), Math.max(a,b)+1).forEach(id => s.add(id)); return s }); return }
                    }
                    lastClickedOutputId.current = sku.id
                    setSelectedOutput(prev => { const s = new Set(prev); if (s.has(sku.id)) s.delete(sku.id); else s.add(sku.id); return s })
                  }}
                  className={["group flex flex-col rounded-xl border cursor-pointer transition-all overflow-hidden", isSel ? "border-foreground/50 ring-1 ring-foreground/20" : "border-border hover:border-foreground/20"].join(' ')}
                >
                  <div className="relative bg-secondary/40 overflow-hidden rounded-t-xl" style={{ aspectRatio: '3/4' }}>
                    {imgs.length > 0
                      ? <DoneCardCarousel imgs={imgs} sku={sku.sku} />
                      : sku.images
                        ? <img src={sku.images[0]} alt={sku.sku} className="w-full h-full object-cover" />
                        : <HardDrive className="w-6 h-6 text-muted-foreground/20 absolute inset-0 m-auto" />}
                    {regenProgress.has(sku.id) && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-20">
                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                    {isSel && <div className="absolute top-1.5 left-1.5 z-10 w-4 h-4 rounded bg-foreground flex items-center justify-center"><Check className="w-2.5 h-2.5 text-background" /></div>}
                    <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity bg-black/20 pointer-events-none group-hover:pointer-events-auto">
                      <button onClick={e => { e.stopPropagation(); setDetailSku(sku) }}
                        className="w-28 py-1.5 rounded-lg bg-background/90 backdrop-blur-sm text-xs font-medium border border-border hover:bg-background transition-colors">
                        Detail
                      </button>
                      <button onClick={e => { e.stopPropagation(); simulateRegen([sku.id]) }}
                        disabled={regenProgress.size > 0}
                        className="w-28 py-1.5 rounded-lg bg-background/90 backdrop-blur-sm text-xs font-medium border border-border hover:bg-background transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                        Regenerate
                      </button>
                      <button onClick={e => { e.stopPropagation(); setSelectedOutput(new Set([sku.id])); setSwitchAvatarPickerOpen(true) }}
                        className="w-28 py-1.5 rounded-lg bg-foreground/90 backdrop-blur-sm text-xs font-medium text-background hover:bg-foreground transition-colors">
                        Switch Model
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-1 px-2 py-1.5">
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-[9px] text-muted-foreground truncate leading-tight">
                        {sku.folder}{sku.avatar ? ` - ${avatars.find(a => a.id === sku.avatar)?.name ?? `Avatar ${sku.avatar}`}` : ''}
                      </span>
                      <span className="text-[10px] font-medium truncate leading-tight">{sku.sku}</span>
                    </div>
                    {sku.images && (
                      <div className="w-7 h-7 rounded-md border border-border overflow-hidden shrink-0 bg-secondary/40">
                        <img src={sku.images[0]} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                  </div>
                  <div className="px-2 pb-1.5">
                    <div className="flex items-center justify-between rounded-md border border-border px-2 py-1 bg-secondary/20">
                      <span className="text-[10px] text-muted-foreground truncate">
                        {hasType(sku, 'optique') && hasType(sku, 'solaire') ? 'Les deux' : hasType(sku, 'solaire') ? 'Solaire' : 'Optique'}
                      </span>
                      <ChevronDown className="w-3 h-3 text-muted-foreground/50 shrink-0" />
                    </div>
                  </div>
                </div>
              )
            })}
              </div>
            </div>
          ))}

          {detailSku && <DoneSkuDetail sku={detailSku} avatars={avatars} onClose={() => setDetailSku(null)} onRegen={handleRegen} />}

          {/* Output selection FAB */}
          {selectedOutput.size > 0 && regenProgress.size === 0 && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3">
              <div className="flex items-center gap-3 bg-background border rounded-2xl shadow-xl px-4 py-3 whitespace-nowrap">
                <span className="text-sm font-semibold tabular-nums">{selectedOutput.size}<span className="font-normal text-muted-foreground"> selected</span></span>
                <div className="w-px h-5 bg-border shrink-0" />
                <button onClick={() => setSwitchAvatarPickerOpen(true)}
                  className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-xl bg-foreground text-background hover:bg-foreground/90 transition-colors whitespace-nowrap">
                  <User className="w-3.5 h-3.5 shrink-0" />Switch & Regen
                </button>
                <div className="w-px h-5 bg-border shrink-0" />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-xl border hover:bg-secondary/50 transition-colors whitespace-nowrap">
                      {(() => {
                        const sel = [...selectedOutput]
                        const allO = sel.every(id => hasType(skus.find(s => s.id === id), 'optique'))
                        const allS = sel.every(id => hasType(skus.find(s => s.id === id), 'solaire'))
                        return allO && allS ? 'Les deux' : allS ? 'Solaire' : allO ? 'Optique' : 'Type…'
                      })()}
                      <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" side="top">
                    <DropdownMenuRadioGroup onValueChange={v => [...selectedOutput].forEach(id => assignSkuType(id, v as 'optique' | 'solaire' | 'both'))}>
                      <DropdownMenuRadioItem value="optique" className="text-xs">Optique</DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="solaire" className="text-xs">Solaire</DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="both" className="text-xs">Les deux</DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
                <div className="w-px h-5 bg-border shrink-0" />
                <button onClick={() => simulateRegen([...selectedOutput])}
                  className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-xl border hover:bg-secondary/50 transition-colors whitespace-nowrap">
                  <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3"/></svg>
                  Regenerate
                </button>
              </div>
              <button onClick={() => setSelectedOutput(new Set())}
                className="w-10 h-10 rounded-full bg-background border shadow-xl flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Switch Avatar modal */}
          {switchAvatarPickerOpen && (
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-8" onClick={() => setSwitchAvatarPickerOpen(false)}>
              <div className="bg-background rounded-2xl p-6 flex flex-col gap-4 w-full max-w-sm" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm">Switch avatar for {selectedOutput.size} SKU{selectedOutput.size > 1 ? 's' : ''}</span>
                  <button onClick={() => setSwitchAvatarPickerOpen(false)}><X className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {avatars.map(a => (
                    <button key={a.id} onClick={() => {
                      const ids = [...selectedOutput]
                      // Update ref immediately so saveConfig writes the new avatar before generation starts
                      skusRef.current = skusRef.current.map(s => ids.includes(s.id) ? { ...s, avatar: a.id } : s)
                      setSkus(skusRef.current)
                      setSwitchAvatarPickerOpen(false)
                      // Save config to disk first, then regenerate
                      jobsApi.saveConfig(Number(job.id), {
                        ...configValuesRef.current,
                        skus: skusRef.current.map(s => ({ id: s.id, sku: s.sku, folder: s.folder, avatar: s.avatar, types: [...s.types], images: s.images ? [...s.images] : [], outputImages: s.outputImages ?? [], model_override: s.model_override ?? null })),
                      }).then(() => simulateRegen(ids)).catch(() => simulateRegen(ids))
                    }} className="flex flex-col items-center gap-1.5 p-2 rounded-xl border hover:border-foreground/40 hover:bg-secondary/40 transition-all">
                      <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-secondary">
                        <img src={a.img} alt="" className="w-full h-full object-contain" />
                      </div>
                      <span className="text-[10px] text-muted-foreground">{a.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Queue position banner — job is waiting for a free worker slot */}
      {queuePosition !== null && queuePosition > 0 && job.status === 'queued' && (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20 text-yellow-700 dark:text-yellow-400 text-sm">
          <svg className="w-4 h-4 animate-spin shrink-0" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
          <span>Position <strong>{queuePosition}</strong> in queue — waiting for a free generation slot…</span>
        </div>
      )}

      {/* Stream-level error banner (shown even after stream failure, before status refreshes) */}
      {streamError && job.status !== 'error' && (
        <div className="flex items-start gap-3 p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          <AlertCircle className="size-4 shrink-0 mt-0.5" />
          <div>
            <span className="font-medium">Erreur de connexion stream</span>
            <span className="block text-xs font-mono mt-0.5 opacity-70">{streamError}</span>
          </div>
          <button onClick={() => setStreamError(null)} className="ml-auto text-destructive/50 hover:text-destructive">×</button>
        </div>
      )}

      {/* ── PROCESSING view ── */}
      {job.status === 'processing' && !editMode && (() => {
        const isDetached = !generating // job says processing but no active stream in this tab
        // imagePct = total_done / total_needed (output-based) — never mix with backend lifecycle pct
        const generatedImages = lastProgress?.total_done ?? genProgress?.done ?? 0
        const totalImages = lastProgress?.total_needed ?? genProgress?.total ?? skus.length
        const imagePct = totalImages > 0 ? Math.round((generatedImages / totalImages) * 100) : 0
        const progress = imagePct  // used for progress bar width
        return (
          <div className="flex flex-col gap-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{imagePct}% — {generatedImages} of {totalImages} images generated</p>
                {genProgress?.lastSku && <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-xs">{genProgress.lastSku}</p>}
                {lastProgress?.cost && (lastProgress.cost.vision_eur + lastProgress.cost.generation_eur) > 0 && (
                  <p className="text-xs text-muted-foreground mt-0.5">€{(lastProgress.cost.vision_eur + lastProgress.cost.generation_eur).toFixed(2)} so far</p>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                {!isDetached && <>
                <button onClick={() => {
                  genAbortRef.current?.abort()
                  setGenerating(false)
                  onSave(job.id, { status: 'paused' })
                  // Backfill progress so paused view immediately shows completed outputs + download button
                  jobsApi.getProgress(Number(job.id)).then(p => {
                    setLastProgress(p)
                    backfillFromProgress(p)
                  }).catch(() => {})
                }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium hover:bg-secondary/50 transition-colors">
                  <Pause className="w-3.5 h-3.5" /> Pause
                </button>
                <button onClick={() => { genAbortRef.current?.abort(); setGenerating(false); onSave(job.id, { status: 'cancelled', progress: 0 }) }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-destructive/40 text-destructive text-sm font-medium hover:bg-destructive/5 transition-colors">
                  <Square className="w-3.5 h-3.5" /> Cancel
                </button>
                </>}
                {renderDownloadCompletedButton()}
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="h-2 rounded-full bg-secondary overflow-hidden">
                <div className="h-full bg-foreground rounded-full transition-all" style={{ width: `${progress}%` }} />
              </div>
              <span className="text-xs text-muted-foreground text-right">{progress}%</span>
            </div>

            <OutputToolbar />

            {/* SKU grid — done = full color, active = spinner, pending = grayscale */}
            {(() => {
              // Count SKUs where ALL types are done — used as the sequential SKU index
              const skuDoneCount = skus.filter(s => {
                const vals = s.type_results ? Object.values(s.type_results) : []
                return vals.length > 0 && vals.every(r => r.status === 'done')
              }).length
              return sortAndGroup(skus).map(({ label, items }) => (
              <div key={label ?? 'all'} className="flex flex-col gap-2">
                {label && <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>}
                <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-3">
              {items.map((sku) => {
                const i = skus.indexOf(sku)
                const typeResultVals = sku.type_results ? Object.values(sku.type_results) : []
                const hasMeta = typeResultVals.length > 0
                const done = hasMeta
                  ? typeResultVals.every(r => r.status === 'done')
                  : i < skuDoneCount
                const hasStarted = hasMeta && typeResultVals.some(r =>
                  r.status === 'processing' || r.status === 'done'
                )
                const active = !done && (hasStarted || i === skuDoneCount)
                const isSel = selectedOutput.has(sku.id)
                const assignedAvatar = avatars.find(a => a.id === sku.avatar)
                return (
                  <div key={sku.id}
                    onClick={() => setSelectedOutput(prev => { const s = new Set(prev); if (s.has(sku.id)) s.delete(sku.id); else s.add(sku.id); return s })}
                    className={["group flex flex-col rounded-xl border overflow-hidden cursor-pointer transition-all", isSel ? "ring-2 ring-foreground/60 border-foreground/40" : "border-border hover:border-foreground/20"].join(' ')}>
                    {done ? (
                      <>
                        {/* Done: same card as finished view */}
                        <div className="relative bg-secondary/40 overflow-hidden rounded-t-xl" style={{ aspectRatio: '3/4' }}>
                          {sku.outputImages?.length
                            ? <DoneCardCarousel imgs={sku.outputImages} sku={sku.sku} />
                            : sku.images
                              ? <img src={sku.images[0]} alt={sku.sku} className="w-full h-full object-cover" />
                              : <HardDrive className="w-6 h-6 text-muted-foreground/20 absolute inset-0 m-auto" />}
                          {skuErrors.has(sku.sku) && (
                            <div className="absolute bottom-1 inset-x-1 z-10 rounded bg-destructive/90 px-1.5 py-0.5 text-[9px] text-white leading-tight truncate">
                              {skuErrors.get(sku.sku)!.join(' · ')}
                            </div>
                          )}
                          {regenProgress.has(sku.id) && (
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-20">
                              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            </div>
                          )}
                          {isSel
                            ? <div className="absolute top-1.5 left-1.5 z-10 w-4 h-4 rounded bg-foreground flex items-center justify-center"><Check className="w-2.5 h-2.5 text-background" /></div>
                            : <div className="absolute top-1.5 left-1.5 z-10"><CheckCircle2 className="w-4 h-4 text-green-500 drop-shadow" /></div>
                          }
                          <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
                            <button onClick={e => { e.stopPropagation(); setDetailSku(sku) }}
                              className="w-28 py-1.5 rounded-lg bg-background/90 backdrop-blur-sm text-xs font-medium border border-border hover:bg-background transition-colors">Detail</button>
                            <button onClick={e => { e.stopPropagation(); simulateRegen([sku.id]) }}
                              disabled={regenProgress.size > 0}
                              className="w-28 py-1.5 rounded-lg bg-background/90 backdrop-blur-sm text-xs font-medium border border-border hover:bg-background transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Regenerate</button>
                            <button onClick={e => { e.stopPropagation(); setSelectedOutput(new Set([sku.id])); setSwitchAvatarPickerOpen(true) }}
                              className="w-28 py-1.5 rounded-lg bg-foreground/90 backdrop-blur-sm text-xs font-medium text-background hover:bg-foreground transition-colors">Switch Model</button>
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-1 px-2 py-1.5">
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="text-[9px] text-muted-foreground truncate leading-tight">
                              {sku.folder}{sku.avatar ? ` - ${avatars.find(a => a.id === sku.avatar)?.name ?? `Avatar ${sku.avatar}`}` : ''}
                            </span>
                            <span className="text-[10px] font-medium truncate leading-tight">{sku.sku}</span>
                          </div>
                          {sku.images && (
                            <div className="w-7 h-7 rounded-md border border-border overflow-hidden shrink-0 bg-secondary/40">
                              <img src={sku.images[0]} alt="" className="w-full h-full object-cover" />
                            </div>
                          )}
                        </div>
                        <div className="px-2 pb-1.5">
                          <div className="flex items-center justify-between rounded-md border border-border px-2 py-1 bg-secondary/20">
                            <span className="text-[10px] text-muted-foreground truncate">
                              {hasType(sku, 'optique') && hasType(sku, 'solaire') ? 'Les deux' : hasType(sku, 'solaire') ? 'Solaire' : 'Optique'}
                            </span>
                            <ChevronDown className="w-3 h-3 text-muted-foreground/50 shrink-0" />
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Not done: model as main image, glasses as thumbnail */}
                        <div className="relative bg-secondary/40 overflow-hidden rounded-t-xl" style={{ aspectRatio: '3/4' }}>
                          {sku.images
                            ? <img src={sku.images[0]} alt={sku.sku} className="absolute inset-0 w-full h-full object-cover grayscale opacity-50" />
                            : <HardDrive className="w-8 h-8 text-muted-foreground/20 absolute inset-0 m-auto" />}
                          {active && (
                            <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10">
                              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            </div>
                          )}
                          {isSel && <div className="absolute top-1.5 left-1.5 w-4 h-4 rounded bg-foreground flex items-center justify-center z-10"><Check className="w-2.5 h-2.5 text-background" /></div>}
                        </div>
                        <div className="flex items-center justify-between gap-1 px-2 py-1.5">
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="text-[9px] text-muted-foreground truncate leading-tight">
                              {sku.folder}{assignedAvatar ? ` - ${assignedAvatar.name}` : ''}
                            </span>
                            <span className="text-[10px] font-medium truncate leading-tight text-muted-foreground">{sku.sku}</span>
                          </div>
                          {assignedAvatar && (
                            <div className="w-7 h-7 rounded-md border border-border overflow-hidden shrink-0 bg-secondary/40">
                              <img src={assignedAvatar.img} alt="" className="w-full h-full object-cover object-top" />
                            </div>
                          )}
                        </div>
                        <div className="px-2 pb-1.5">
                          <div className="flex items-center justify-between rounded-md border border-border px-2 py-1 bg-secondary/20">
                            <span className="text-[10px] text-muted-foreground truncate">
                              {hasType(sku, 'optique') && hasType(sku, 'solaire') ? 'Les deux' : hasType(sku, 'solaire') ? 'Solaire' : 'Optique'}
                            </span>
                            <ChevronDown className="w-3 h-3 text-muted-foreground/50 shrink-0" />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )
              })}
                </div>
              </div>
            ))
            })()}

            {detailSku && <DoneSkuDetail sku={detailSku} avatars={avatars} onClose={() => setDetailSku(null)} onRegen={handleRegen} />}

            {/* Batch FAB */}
            {selectedOutput.size > 0 && regenProgress.size === 0 && (
              <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3">
                <div className="flex items-center gap-3 bg-background border rounded-2xl shadow-xl px-4 py-3 whitespace-nowrap">
                  <span className="text-sm font-semibold tabular-nums">{selectedOutput.size}<span className="font-normal text-muted-foreground"> selected</span></span>
                  <div className="w-px h-5 bg-border shrink-0" />
                  <button onClick={() => setSwitchAvatarPickerOpen(true)}
                    className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-xl bg-foreground text-background hover:bg-foreground/90 transition-colors whitespace-nowrap">
                    <User className="w-3.5 h-3.5 shrink-0" />Switch & Regen
                  </button>
                  <div className="w-px h-5 bg-border shrink-0" />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-xl border hover:bg-secondary/50 transition-colors whitespace-nowrap">
                        {(() => {
                          const sel = [...selectedOutput]
                          const allO = sel.every(id => hasType(skus.find(s => s.id === id), 'optique'))
                          const allS = sel.every(id => hasType(skus.find(s => s.id === id), 'solaire'))
                          return allO && allS ? 'Les deux' : allS ? 'Solaire' : allO ? 'Optique' : 'Type…'
                        })()}
                        <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center" side="top">
                      <DropdownMenuRadioGroup onValueChange={v => [...selectedOutput].forEach(id => assignSkuType(id, v as 'optique' | 'solaire' | 'both'))}>
                        <DropdownMenuRadioItem value="optique" className="text-xs">Optique</DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="solaire" className="text-xs">Solaire</DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="both" className="text-xs">Les deux</DropdownMenuRadioItem>
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <div className="w-px h-5 bg-border shrink-0" />
                  <button onClick={() => simulateRegen([...selectedOutput])}
                    className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-xl border hover:bg-secondary/50 transition-colors whitespace-nowrap">
                    <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3"/></svg>
                    Regenerate
                  </button>
                </div>
                <button onClick={() => setSelectedOutput(new Set())}
                  className="w-10 h-10 rounded-full bg-background border shadow-xl flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )
      })()}

      {/* ── CANCELLED view ── */}
      {job.status === 'cancelled' && !editMode && (() => {
        const okSkus = skus.filter(s => (s.outputImages ?? []).length > 0)
        const pendingSkus = skus.filter(s => (s.outputImages ?? []).length === 0)
        return (
        <div className="flex flex-col gap-5">
          <div className="flex items-start gap-3 p-4 rounded-xl bg-secondary/60 border border-border">
            <XCircle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">Job cancelled</p>
              <p className="text-xs text-muted-foreground mt-1">Your files are safe — nothing was deleted. You can restart from where you left off.</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEditMode(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium hover:bg-secondary/50 transition-colors">
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
              <button onClick={() => {
                setGenProgress(null)
                setSkuErrors(new Map())
                setStreamError(null)
                const ctrl = new AbortController()
                genAbortRef.current = ctrl
                startGenStream(
                  undefined,
                  e => {
                    // pct no longer used for PATCH — live progress uses local state
                    setGenProgress(prev => ({
                      done: e.done, total: e.total, lastSku: e.sku,
                      errors: e.error ? [...(prev?.errors ?? []), `${e.sku} (${e.type}): ${e.error}`] : (prev?.errors ?? []),
                    }))
                    if (e.url) withSuppressedSave(() => setSkus(prev => prev.map(s => { if (s.sku !== e.sku) return s; const tf = `_${e.type}.`; return { ...s, outputImages: [...(s.outputImages ?? []).filter(u => !u.includes(tf)), e.url!] } })))
                    if (e.error) setSkuErrors(prev => { const m = new Map(prev); m.set(e.sku, [...(m.get(e.sku) ?? []), `${e.type}: ${e.error}`]); return m })
                    // No onSave PATCH for pct — live progress uses local state
                    fetchProgressThrottled()
                  },
                  () => {
                    setGenerating(false)
                    if (configSaveTimer.current) clearTimeout(configSaveTimer.current)
                    jobsApi.saveConfig(Number(job.id), {
                      ...configValuesRef.current,
                      skus: skusRef.current.map(s => ({ id: s.id, sku: s.sku, folder: s.folder, avatar: s.avatar, types: [...s.types], images: s.images ? [...s.images] : [], model_override: s.model_override ?? null })),
                    }).catch(() => {})
                    jobsApi.getProgress(Number(job.id)).then(p => {
                      const finalStatus = deriveFinalStatus(p)
                      onSave(job.id, { status: finalStatus, progress: 100 })
                      setLastProgress(p)
                      backfillFromProgress(p)
                    }).catch(() => { onSave(job.id, { status: 'error', progress: 100 }) })
                  },
                  (msg) => { setGenerating(false); if (!msg.includes('aborted')) setStreamError('Connection lost — job still running') },
                  ctrl,
                  (isReconnect) => { setGenerating(true); if (!isReconnect) onSave(job.id, { status: 'processing', progress: 0 }) },
                )
              }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-foreground text-background text-sm font-medium hover:bg-foreground/90 transition-colors">
                <Play className="w-3.5 h-3.5" /> Restart
              </button>
            </div>
          </div>
          {okSkus.length > 0 && (
            <>
              <OutputToolbar />
              {sortAndGroup(okSkus).map(({ label, items }) => (
                <div key={label ?? 'done'} className="flex flex-col gap-2">
                  {label && <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>}
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-3">
                    {items.map(sku => (
                      <div key={sku.id} className="flex flex-col rounded-xl border border-border overflow-hidden">
                        <div className="relative bg-secondary/40 rounded-t-xl overflow-hidden" style={{ aspectRatio: '3/4' }}>
                          {(sku.outputImages ?? []).length > 0
                            ? <DoneCardCarousel imgs={sku.outputImages!} sku={sku.sku} />
                            : sku.images ? <img src={sku.images[0]} alt={sku.sku} className="w-full h-full object-cover" /> : null}
                        </div>
                        <div className="px-2 py-1.5 flex flex-col gap-0.5">
                          <span className="text-[9px] text-muted-foreground truncate">{sku.folder}</span>
                          <span className="text-[10px] font-medium truncate">{sku.sku}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {pendingSkus.length > 0 && (
                <p className="text-xs text-muted-foreground">{pendingSkus.length} SKU{pendingSkus.length > 1 ? 's' : ''} not yet generated — restart to continue.</p>
              )}
            </>
          )}
        </div>
        )
      })()}

      {/* ── ERROR view ── */}
      {/* ── ERROR / PARTIAL view ── */}
      {(job.status === 'error' || job.status === 'partial') && !editMode && (() => {
        const okSkus = skus.filter(s => (s.outputImages ?? []).length > 0)
        const failedSkus = skus.filter(s => (s.outputImages ?? []).length === 0 && !s.skipped)
        const skippedSkus = skus.filter(s => s.skipped)
        const isPartial = job.status === 'partial'

        return (
          <div className="flex flex-col gap-5">
            {/* Banner — different wording for partial vs error */}
            <div className={['flex items-start gap-3 p-4 rounded-xl border', isPartial ? 'bg-amber-500/10 border-amber-500/30' : 'bg-destructive/10 border-destructive/20'].join(' ')}>
              <AlertCircle className={['w-5 h-5 shrink-0 mt-0.5', isPartial ? 'text-amber-500' : 'text-destructive'].join(' ')} />
              <div className="flex-1 min-w-0">
                <p className={['text-sm font-semibold', isPartial ? 'text-amber-600' : 'text-destructive'].join(' ')}>
                  {isPartial
                    ? `Completed with failures · ${okSkus.length}/${skus.length} images generated`
                    : okSkus.length > 0
                      ? `Generation stopped with errors · ${okSkus.length}/${skus.length} images generated`
                      : 'Generation failed'}
                </p>
                <p className={['text-xs mt-1 leading-relaxed', isPartial ? 'text-amber-600/70' : 'text-destructive/70'].join(' ')}>
                  {streamError && <span className="block font-mono mb-1">{streamError}</span>}
                  {failedSkus.length > 0 && <span>{failedSkus.length} failed{skippedSkus.length > 0 ? ` · ${skippedSkus.length} skipped` : ''}. </span>}
                  {lastProgress?.duration_ms != null && <span>Completed in {formatDuration(lastProgress.duration_ms)}. </span>}
                  {okSkus.length > 0
                    ? 'Completed outputs are available for download.'
                    : 'Check avatar assignment and source image quality.'}
                </p>
              </div>
              <div className="flex gap-2 shrink-0 flex-wrap">
                <button onClick={() => {
                  setGenProgress(null); setSkuErrors(new Map()); setStreamError(null)
                  const ctrl = new AbortController(); genAbortRef.current = ctrl
                  startGenStream(failedSkus.length > 0 ? failedSkus.map(s => s.id) : undefined,
                    e => {
                      // pct no longer used for PATCH — live progress uses local state
                      setGenProgress(prev => ({ done: e.done, total: e.total, lastSku: e.sku, errors: e.error ? [...(prev?.errors ?? []), `${e.sku} (${e.type}): ${e.error}`] : (prev?.errors ?? []) }))
                      if (e.url) withSuppressedSave(() => setSkus(prev => prev.map(s => { if (s.sku !== e.sku) return s; const tf = `_${e.type}.`; return { ...s, outputImages: [...(s.outputImages ?? []).filter(u => !u.includes(tf)), e.url!] } })))
                      if (e.error) setSkuErrors(prev => { const m = new Map(prev); m.set(e.sku, [...(m.get(e.sku) ?? []), `${e.type}: ${e.error}`]); return m })
                      // No onSave PATCH for pct — live progress uses local state
                      fetchProgressThrottled()
                    },
                    () => {
                      setGenerating(false)
                      jobsApi.getProgress(Number(job.id)).then(p => {
                        const finalStatus = deriveFinalStatus(p)
                        onSave(job.id, { status: finalStatus, progress: 100 })
                        setLastProgress(p)
                        backfillFromProgress(p)
                      }).catch(() => { onSave(job.id, { status: 'error', progress: 100 }) })
                    },
                    (msg) => { setGenerating(false); if (!msg.includes('aborted')) setStreamError('Connection lost — job still running') },
                    ctrl,
                    (isReconnect) => {
                      setGenerating(true)
                      if (!isReconnect) onSave(job.id, { status: 'processing', progress: okSkus.length > 0 ? Math.round(okSkus.length / skus.length * 100) : 0 })
                    })
                }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-sm font-medium hover:bg-secondary/60 transition-colors">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3"/></svg>
                  Reconnect
                </button>
                <button onClick={() => setEditMode(true)}
                  className={['flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors', isPartial ? 'border-border hover:bg-secondary/60' : 'border-destructive/30 text-destructive hover:bg-destructive/10'].join(' ')}>
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
                {renderDownloadCompletedButton()}
                {failedSkus.length === 0 && (
                  <button onClick={() => onSave(job.id, { status: 'done' })}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-foreground text-background text-sm font-medium hover:bg-foreground/90 transition-colors">
                    <Check className="w-3.5 h-3.5" /> Mark as done
                  </button>
                )}
                {failedSkus.length > 0 && (() => {
                  const retryable = failedSkus.filter(s => !hasAvatarError(s))
                  const blocked = failedSkus.filter(s => hasAvatarError(s))
                  return (
                    <>
                      {retryable.length > 0 && (
                        <button onClick={() => simulateRegen(retryable.map(s => s.id))}
                          disabled={regenProgress.size > 0}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-foreground text-background text-sm font-medium hover:bg-foreground/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                          <Play className="w-3.5 h-3.5" /> Retry failed ({retryable.length})
                        </button>
                      )}
                      {blocked.length > 0 && (
                        <span className="text-xs text-destructive/70" title="Fix or reassign avatar for these SKUs before retrying">
                          {blocked.length} SKU{blocked.length > 1 ? 's' : ''} blocked — fix avatar first
                        </span>
                      )}
                    </>
                  )
                })()}
              </div>
            </div>

            <OutputToolbar />

            {/* SKU grid — same cards as processing view */}
            {sortAndGroup(skus).map(({ label, items }) => (
              <div key={label ?? 'all'} className="flex flex-col gap-2">
                {label && <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>}
                <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-3">
              {items.map((sku) => {
                const isDone = (sku.outputImages ?? []).length > 0
                const isFailed = !isDone
                const isSel = selectedOutput.has(sku.id)
                const isRegen = regenProgress.has(sku.id)
                const assignedAvatar = avatars.find(a => a.id === sku.avatar)
                return (
                  <div key={sku.id}
                    onClick={() => setSelectedOutput(prev => { const s = new Set(prev); if (s.has(sku.id)) s.delete(sku.id); else s.add(sku.id); return s })}
                    className={["group flex flex-col rounded-xl border overflow-hidden cursor-pointer transition-all", isSel ? "ring-2 ring-foreground/60 border-foreground/40" : isFailed ? "border-destructive/30 hover:border-destructive/60" : "border-border hover:border-foreground/20"].join(' ')}>
                    {isDone ? (
                      <>
                        <div className="relative bg-secondary/40 overflow-hidden rounded-t-xl" style={{ aspectRatio: '3/4' }}>
                          {sku.outputImages?.length
                            ? <DoneCardCarousel imgs={sku.outputImages} sku={sku.sku} />
                            : sku.images
                              ? <img src={sku.images[0]} alt={sku.sku} className="w-full h-full object-cover" />
                              : <HardDrive className="w-6 h-6 text-muted-foreground/20 absolute inset-0 m-auto" />}
                          {isSel
                            ? <div className="absolute top-1.5 left-1.5 z-10 w-4 h-4 rounded bg-foreground flex items-center justify-center"><Check className="w-2.5 h-2.5 text-background" /></div>
                            : <div className="absolute top-1.5 left-1.5 z-10"><CheckCircle2 className="w-4 h-4 text-green-500 drop-shadow" /></div>}
                          {isRegen
                            ? <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/50"><svg className="w-6 h-6 text-white animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg></div>
                            : <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity bg-black/20 pointer-events-none group-hover:pointer-events-auto">
                                <button onClick={e => { e.stopPropagation(); setDetailSku(sku) }} className="w-28 py-1.5 rounded-lg bg-background/90 backdrop-blur-sm text-xs font-medium border border-border hover:bg-background transition-colors">Detail</button>
                                <button onClick={e => { e.stopPropagation(); simulateRegen([sku.id]) }} disabled={regenProgress.size > 0} className="w-28 py-1.5 rounded-lg bg-background/90 backdrop-blur-sm text-xs font-medium border border-border hover:bg-background transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Regenerate</button>
                                <button onClick={e => { e.stopPropagation(); setSelectedOutput(new Set([sku.id])); setSwitchAvatarPickerOpen(true) }} className="w-28 py-1.5 rounded-lg bg-foreground/90 backdrop-blur-sm text-xs font-medium text-background hover:bg-foreground transition-colors">Switch Model</button>
                              </div>}
                        </div>
                        <div className="flex items-center justify-between gap-1 px-2 py-1.5">
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="text-[9px] text-muted-foreground truncate leading-tight">{sku.folder}{sku.avatar ? ` - ${avatars.find(a => a.id === sku.avatar)?.name ?? `Avatar ${sku.avatar}`}` : ''}</span>
                            <span className="text-[10px] font-medium truncate leading-tight">{sku.sku}</span>
                          </div>
                          {sku.images && <div className="w-7 h-7 rounded-md border border-border overflow-hidden shrink-0 bg-secondary/40"><img src={sku.images[0]} alt="" className="w-full h-full object-cover" /></div>}
                        </div>
                        <div className="px-2 pb-1.5">
                          <div className="flex items-center justify-between rounded-md border border-border px-2 py-1 bg-secondary/20">
                            <span className="text-[10px] text-muted-foreground truncate">{hasType(sku, 'optique') && hasType(sku, 'solaire') ? 'Les deux' : hasType(sku, 'solaire') ? 'Solaire' : 'Optique'}</span>
                            <ChevronDown className="w-3 h-3 text-muted-foreground/50 shrink-0" />
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="relative bg-destructive/5 overflow-hidden rounded-t-xl" style={{ aspectRatio: '3/4' }}>
                          {sku.images
                            ? <img src={sku.images[0]} alt={sku.sku} className="absolute inset-0 w-full h-full object-cover grayscale opacity-40" />
                            : <HardDrive className="w-6 h-6 text-muted-foreground/20 absolute inset-0 m-auto" />}
                          {isSel
                            ? <div className="absolute top-1.5 left-1.5 w-4 h-4 rounded bg-foreground flex items-center justify-center z-10"><Check className="w-2.5 h-2.5 text-background" /></div>
                            : <div className="absolute top-1.5 left-1.5 z-10"><AlertCircle className="w-4 h-4 text-destructive drop-shadow" /></div>}
                          {isRegen
                            ? <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/50"><svg className="w-6 h-6 text-white animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg></div>
                            : <>
                                <div className="absolute inset-x-0 bottom-0 bg-black/80 text-white text-[9px] leading-tight px-1.5 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity z-10">{getSkuFailureReason(sku)}</div>
                                <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity bg-black/20 pointer-events-none group-hover:pointer-events-auto">
                                  <button
                                    onClick={e => { e.stopPropagation(); simulateRegen([sku.id]) }}
                                    disabled={regenProgress.size > 0 || hasAvatarError(sku)}
                                    title={hasAvatarError(sku) ? 'Fix or reassign avatar before retrying' : undefined}
                                    className="w-28 py-1.5 rounded-lg bg-background/90 backdrop-blur-sm text-xs font-medium border border-border hover:bg-background transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Regenerate</button>
                                  <button onClick={e => { e.stopPropagation(); setSelectedOutput(new Set([sku.id])); setSwitchAvatarPickerOpen(true) }} className="w-28 py-1.5 rounded-lg bg-foreground/90 backdrop-blur-sm text-xs font-medium text-background hover:bg-foreground transition-colors">Switch Model</button>
                                </div>
                              </>}
                        </div>
                        <div className="flex items-center justify-between gap-1 px-2 py-1.5 bg-destructive/5">
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="text-[9px] text-muted-foreground truncate leading-tight">{sku.folder}{assignedAvatar ? ` - ${assignedAvatar.name}` : ''}</span>
                            <span className="text-[10px] font-medium truncate leading-tight text-destructive">{sku.sku}</span>
                          </div>
                          {assignedAvatar && <div className="w-7 h-7 rounded-md border border-border overflow-hidden shrink-0 bg-secondary/40"><img src={assignedAvatar.img} alt="" className="w-full h-full object-cover object-top" /></div>}
                        </div>
                        <div className="px-2 pb-1.5 bg-destructive/5">
                          <div className="flex items-center justify-between rounded-md border border-destructive/20 px-2 py-1 bg-destructive/10">
                            <span className="text-[10px] text-destructive/70 truncate">{hasType(sku, 'optique') && hasType(sku, 'solaire') ? 'Les deux' : hasType(sku, 'solaire') ? 'Solaire' : 'Optique'}</span>
                            <ChevronDown className="w-3 h-3 text-destructive/30 shrink-0" />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )
              })}
                </div>
              </div>
            ))}
            {detailSku && <DoneSkuDetail sku={detailSku} avatars={avatars} onClose={() => setDetailSku(null)} onRegen={handleRegen} />}

            {/* Batch FAB */}
            {selectedOutput.size > 0 && regenProgress.size === 0 && (() => {
              const hasFailedSelected = [...selectedOutput].some(id => (skus.find(s => s.id === id)?.outputImages ?? []).length === 0)
              return (
              <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3">
                <div className="flex items-center gap-3 bg-background border rounded-2xl shadow-xl px-4 py-3 whitespace-nowrap">
                  <span className="text-sm font-semibold tabular-nums">{selectedOutput.size}<span className="font-normal text-muted-foreground"> selected</span></span>
                  <div className="w-px h-5 bg-border shrink-0" />
                  <button onClick={() => setSwitchAvatarPickerOpen(true)}
                    className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-xl border hover:bg-secondary/50 transition-colors whitespace-nowrap">
                    <User className="w-3.5 h-3.5 shrink-0" />Switch & Regen
                  </button>
                  <div className="w-px h-5 bg-border shrink-0" />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-xl border hover:bg-secondary/50 transition-colors whitespace-nowrap">
                        {(() => {
                          const sel = [...selectedOutput]
                          const allO = sel.every(id => hasType(skus.find(s => s.id === id), 'optique'))
                          const allS = sel.every(id => hasType(skus.find(s => s.id === id), 'solaire'))
                          return allO && allS ? 'Les deux' : allS ? 'Solaire' : allO ? 'Optique' : 'Type…'
                        })()}
                        <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center" side="top">
                      <DropdownMenuRadioGroup onValueChange={v => [...selectedOutput].forEach(id => assignSkuType(id, v as 'optique' | 'solaire' | 'both'))}>
                        <DropdownMenuRadioItem value="optique" className="text-xs">Optique</DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="solaire" className="text-xs">Solaire</DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="both" className="text-xs">Les deux</DropdownMenuRadioItem>
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {hasFailedSelected && <>
                    <div className="w-px h-5 bg-border shrink-0" />
                    <button onClick={() => {
                      const failedSelected = [...selectedOutput].filter(id => (skus.find(s => s.id === id)?.outputImages ?? []).length === 0)
                      if (failedSelected.length) simulateRegen(failedSelected)
                    }} className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-xl bg-foreground text-background hover:bg-foreground/90 transition-colors whitespace-nowrap">
                      <Play className="w-3.5 h-3.5 shrink-0" />Retry selected
                    </button>
                  </>}
                </div>
                <button onClick={() => setSelectedOutput(new Set())}
                  className="w-10 h-10 rounded-full bg-background border shadow-xl flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </div>
              )
            })()}
          </div>
        )
      })()}

      {/* ── PENDING views (not_started / queued / paused) ── */}
      {(['not_started','queued','paused','batching'] as const).includes(job.status as 'not_started'|'queued'|'paused'|'batching') && !editMode && (() => {
        const resLabel = ({ '1080x1920': '1K', '1440x2560': '2K', '2160x3840': '4K' } as Record<string,string>)[outputRes] ?? outputRes
        return (
          <div className="flex flex-col gap-5">
            {/* Summary card + actions */}
            <div className="rounded-xl border p-4 flex flex-col gap-4 text-sm">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                <div className="flex justify-between"><span className="text-muted-foreground">Products</span><span className="font-medium">{skus.length}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Format</span><span className="font-medium">{outputFormat.toUpperCase()}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Resolution</span><span className="font-medium">{resLabel}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Naming</span><span className="font-medium">{naming === 'custom' ? namingCustom || 'custom' : naming}</span></div>
              </div>
              <div className="flex items-center gap-3 flex-wrap border-t pt-3">
                {job.status === 'batching' && (
                  <p className="text-sm text-violet-600 dark:text-violet-400 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Batch running at the provider — results within 24h at −50% cost.
                  </p>
                )}
                {(job.status === 'not_started' || job.status === 'queued' || job.status === 'paused') && <>
                  <button onClick={async () => {
                    // Flush any pending debounced save so worker reads the latest avatars
                    if (configSaveTimer.current) {
                      clearTimeout(configSaveTimer.current)
                      configSaveTimer.current = null
                    }
                    await jobsApi.saveConfig(Number(job.id), {
                      ...configValuesRef.current,
                      skus: skusRef.current.map(s => ({ id: s.id, sku: s.sku, folder: s.folder, avatar: s.avatar, types: [...s.types], images: s.images ? [...s.images] : [], outputImages: s.outputImages ?? [], model_override: s.model_override ?? null })),
                    }).catch(() => {})
                    setGenProgress(null)
                    const ctrl = new AbortController()
                    genAbortRef.current = ctrl
                    // Resume: only process SKUs without output yet
                    const pendingSkuNames = job.status === 'paused'
                      ? skus.filter(s => (s.outputImages ?? []).length === 0).map(s => s.sku)
                      : undefined
                    startGenStream(
                      pendingSkuNames,
                      e => {
                        if (e.retry) {
                          setGenProgress(prev => prev ? { ...prev, lastSku: `⟳ ${e.sku} (${e.type}) — rate limit, retrying in ${e.wait_s}s (${e.attempt}/${e.max_attempts ?? 5})` } : prev)
                          return
                        }
                        // pct no longer used for PATCH — live progress uses local state
                        setGenProgress(prev => ({
                          done: e.done, total: e.total, lastSku: e.sku,
                          errors: e.error ? [...(prev?.errors ?? []), `${e.sku} (${e.type}): ${e.error}`] : (prev?.errors ?? []),
                        }))
                        if (e.url) withSuppressedSave(() => setSkus(prev => prev.map(s => { if (s.sku !== e.sku) return s; const tf = `_${e.type}.`; return { ...s, outputImages: [...(s.outputImages ?? []).filter(u => !u.includes(tf)), e.url!] } })))
                        // No onSave PATCH for pct — live progress uses local state
                        fetchProgressThrottled()
                      },
                      () => {
                        setGenerating(false)
                        jobsApi.getProgress(Number(job.id)).then(p => {
                          const finalStatus = deriveFinalStatus(p)
                          onSave(job.id, { status: finalStatus, progress: 100 })
                          setLastProgress(p)
                          backfillFromProgress(p)
                        }).catch(() => { onSave(job.id, { status: 'error', progress: 100 }) })
                      },
                      () => { setGenerating(false); setStreamError('Connection lost — job still running') },
                      ctrl,
                      (isReconnect) => { setGenerating(true); if (!isReconnect) onSave(job.id, { status: 'processing', progress: 0 }) },
                    )
                  }}
                    className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 transition-colors">
                    <Play className="w-4 h-4" /> {job.status === 'paused' ? 'Resume' : 'Start now'}
                  </button>
                  {job.status === 'not_started' && (
                    <button onClick={() => {
                      jobsApi.start(Number(job.id)).then(() => onSave(job.id, { status: 'queued' })).catch(() => {})
                    }}
                      className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border text-sm font-medium hover:bg-secondary/50 transition-colors">
                      <ListOrdered className="w-4 h-4" /> Add to queue
                    </button>
                  )}
                </>}
                <button onClick={() => setEditMode(true)}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors">
                  <Pencil className="w-3.5 h-3.5" /> Edit config
                </button>
              </div>
              {/* Show download button for paused jobs that have completed outputs */}
              {job.status === 'paused' && hasCompletedOutputs && (
                <div className="border-t pt-3">{renderDownloadCompletedButton()}</div>
              )}
            </div>

            {/* SKU grid */}
            <OutputToolbar />
            {sortAndGroup(skus).map(({ label, items }) => (
              <div key={label ?? 'all'} className="flex flex-col gap-2">
                {label && <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>}
                <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-3">
                  {items.map(sku => {
                    const av = avatars.find(a => a.id === sku.avatar)
                    const isSel = selectedOutput.has(sku.id)
                    return (
                      <div key={sku.id}
                        onClick={() => setSelectedOutput(prev => { const s = new Set(prev); if (s.has(sku.id)) s.delete(sku.id); else s.add(sku.id); return s })}
                        className={["group flex flex-col rounded-xl border overflow-hidden cursor-pointer transition-all", isSel ? "ring-2 ring-foreground/60 border-foreground/40" : "border-border hover:border-foreground/20"].join(' ')}>
                        <div className="relative bg-secondary/40 overflow-hidden rounded-t-xl" style={{ aspectRatio: '3/4' }}>
                          {regenProgress.has(sku.id) && (
                            <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/30">
                              <Loader2 className="h-6 w-6 animate-spin text-white" />
                            </div>
                          )}
                          <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity bg-black/20 pointer-events-none group-hover:pointer-events-auto">
                            <button type="button" onClick={e => { e.stopPropagation(); setDetailSku(sku) }}
                              className="w-28 py-1.5 rounded-lg bg-background/90 backdrop-blur-sm text-xs font-medium border border-border hover:bg-background transition-colors">
                              Detail
                            </button>
                            <button type="button" onClick={e => { e.stopPropagation(); simulateRegen([sku.id]) }}
                              disabled={regenProgress.size > 0}
                              className="w-28 py-1.5 rounded-lg bg-background/90 backdrop-blur-sm text-xs font-medium border border-border hover:bg-background transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                              Regenerate
                            </button>
                            <button type="button" onClick={e => { e.stopPropagation(); setSelectedOutput(new Set([sku.id])); setSwitchAvatarPickerOpen(true) }}
                              className="w-28 py-1.5 rounded-lg bg-foreground/90 backdrop-blur-sm text-xs font-medium text-background hover:bg-foreground transition-colors">
                              Switch Model
                            </button>
                          </div>
                          {(sku.outputImages ?? []).length > 0
                            ? <GeneratedThumb src={sku.outputImages![0]} />
                            : sku.images
                              ? <img src={sku.images[0]} alt="" className="absolute inset-0 w-full h-full object-cover grayscale opacity-50" />
                              : <HardDrive className="w-5 h-5 m-auto absolute inset-0 text-muted-foreground/20" />}
                          {isSel && <div className="absolute top-1.5 left-1.5 w-4 h-4 rounded bg-foreground flex items-center justify-center z-10"><Check className="w-2.5 h-2.5 text-background" /></div>}
                        </div>
                        <div className="flex items-center justify-between gap-1 px-2 py-1.5">
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="text-[9px] text-muted-foreground truncate leading-tight">{sku.folder}{av ? ` - ${av.name}` : ''}</span>
                            <span className="text-[10px] font-medium truncate leading-tight text-muted-foreground">{sku.sku}</span>
                          </div>
                          {av && <div className="w-7 h-7 rounded-md border border-border overflow-hidden shrink-0 bg-secondary/40"><img src={av.img} alt="" className="w-full h-full object-cover object-top" /></div>}
                        </div>
                        <div className="px-2 pb-1.5">
                          <div className="flex items-center justify-between rounded-md border border-border px-2 py-1 bg-secondary/20">
                            <span className="text-[10px] text-muted-foreground truncate">{hasType(sku, 'optique') && hasType(sku, 'solaire') ? 'Les deux' : hasType(sku, 'solaire') ? 'Solaire' : 'Optique'}</span>
                            <ChevronDown className="w-3 h-3 text-muted-foreground/50 shrink-0" />
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}

            {/* Batch FAB */}
            {selectedOutput.size > 0 && (
              <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3">
                <div className="flex items-center gap-3 bg-background border rounded-2xl shadow-xl px-4 py-3 whitespace-nowrap">
                  <span className="text-sm font-semibold tabular-nums">{selectedOutput.size}<span className="font-normal text-muted-foreground"> selected</span></span>
                  <div className="w-px h-5 bg-border shrink-0" />
                  <button onClick={() => setSwitchAvatarPickerOpen(true)}
                    className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-xl border hover:bg-secondary/50 transition-colors">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                    Assign Avatar
                  </button>
                </div>
                <button onClick={() => setSelectedOutput(new Set())}
                  className="w-10 h-10 rounded-full bg-background border shadow-xl flex items-center justify-center hover:bg-secondary/50 transition-colors shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {detailSku && <DoneSkuDetail sku={detailSku} avatars={avatars} onClose={() => setDetailSku(null)} onRegen={handleRegen} />}

            {switchAvatarPickerOpen && (
              <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-8" onClick={() => setSwitchAvatarPickerOpen(false)}>
                <div className="bg-background rounded-2xl p-6 flex flex-col gap-4 w-full max-w-sm" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm">Switch avatar for {selectedOutput.size} SKU{selectedOutput.size > 1 ? 's' : ''}</span>
                    <button onClick={() => setSwitchAvatarPickerOpen(false)}><X className="w-4 h-4" /></button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {avatars.map(a => (
                      <button key={a.id} onClick={() => {
                        const ids = [...selectedOutput]
                        skusRef.current = skusRef.current.map(s => ids.includes(s.id) ? { ...s, avatar: a.id } : s)
                        setSkus(skusRef.current)
                        setSwitchAvatarPickerOpen(false)
                        jobsApi.saveConfig(Number(job.id), {
                          ...configValuesRef.current,
                          skus: skusRef.current.map(s => ({ id: s.id, sku: s.sku, folder: s.folder, avatar: s.avatar, types: [...s.types], images: s.images ? [...s.images] : [], outputImages: s.outputImages ?? [], model_override: s.model_override ?? null })),
                        }).then(() => simulateRegen(ids)).catch(() => simulateRegen(ids))
                      }} className="flex flex-col items-center gap-1.5 p-2 rounded-xl border hover:border-foreground/40 hover:bg-secondary/40 transition-all">
                        <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-secondary">
                          <img src={a.img} alt="" className="w-full h-full object-contain" />
                        </div>
                        <span className="text-[10px] text-muted-foreground">{a.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

          </div>
        )
      })()}

      {/* ── CONFIGURE flow (not_configured or editMode) ── */}
      {(job.status === 'not_configured' || editMode) && <>
        {editMode && (
          <button onClick={() => setEditMode(false)} className="self-start flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            ← Back
          </button>
        )}

      {/* Step indicator */}
      <div className="flex items-center gap-3">
        {STEPS.map((s, i) => (
          <div key={s.n} className="flex items-center gap-3">
            <button
              onClick={() => setStep(s.n)}
              className="flex items-center gap-2"
            >
              <div className={["w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors", step === s.n ? "bg-foreground text-background" : step > s.n ? "bg-foreground text-background" : "bg-secondary text-muted-foreground"].join(' ')}>
                {step > s.n ? <Check className="w-3.5 h-3.5" /> : s.n}
              </div>
              <span className={["text-xs font-medium transition-colors", step === s.n ? "" : "text-muted-foreground"].join(' ')}>{s.label}</span>
            </button>
            {i < STEPS.length - 1 && <div className="w-8 h-px bg-border" />}
          </div>
        ))}
      </div>

      {/* ── Step 1 — Analysis ── */}
      {step === 2 && (
        <div className="flex flex-col gap-5">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Vision Analysis</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Gemini reads image 7 (Marque) and image 8 (Ref) per product</p>
          </div>

          {/* ── Stats bar ── */}
          {products.length > 0 && (() => {
            const keyCount = new Map<string, number>()
            products.forEach(p => {
              const k = p.normalized_key || (p.extracted_marque && p.extracted_ref ? `${p.extracted_marque}__${p.extracted_ref}` : null)
              if (k) keyCount.set(k, (keyCount.get(k) ?? 0) + 1)
            })
            const nDuplicates = [...keyCount.values()].reduce((s, n) => s + (n > 1 ? n - 1 : 0), 0)
            const nUnique = products.length - nDuplicates
            const nFound = products.filter(p => p.in_catalogue).length
            const nNotFound = products.filter(p => !p.in_catalogue && !p.errors?.length).length
            const nErrors = products.filter(p => p.errors?.length).length
            return (
              <div className="grid grid-cols-4 gap-2">
                <button
                  onClick={() => setAnalysisFilter(f => f === 'found' ? 'all' : 'found')}
                  className={["rounded-xl border p-3 flex flex-col gap-0.5 text-left transition-all", analysisFilter === 'found' ? "border-green-500 bg-green-500/10" : "border-border hover:border-green-500/50"].join(' ')}
                >
                  <span className="text-xs text-muted-foreground">Found</span>
                  <span className="text-xl font-semibold tabular-nums text-green-600">{nFound}</span>
                </button>
                <button
                  onClick={() => setAnalysisFilter(f => f === 'notfound' ? 'all' : 'notfound')}
                  className={["rounded-xl border p-3 flex flex-col gap-0.5 text-left transition-all", analysisFilter === 'notfound' ? "border-amber-500 bg-amber-500/10" : "border-border hover:border-amber-500/50"].join(' ')}
                >
                  <span className="text-xs text-muted-foreground">Unknown</span>
                  <span className="text-xl font-semibold tabular-nums text-amber-600">{nNotFound}</span>
                </button>
                <button
                  onClick={() => nDuplicates > 0 && setAnalysisFilter(f => f === 'duplicates' ? 'all' : 'duplicates')}
                  className={["rounded-xl border p-3 flex flex-col gap-0.5 text-left transition-all", nDuplicates > 0 ? (analysisFilter === 'duplicates' ? "border-orange-500 bg-orange-500/10" : "border-orange-400/50 bg-orange-500/5 hover:border-orange-500/70") : "border-border cursor-default"].join(' ')}
                >
                  <span className="text-xs text-muted-foreground">Duplicates</span>
                  <span className={["text-xl font-semibold tabular-nums", nDuplicates > 0 ? "text-orange-600" : ""].join(' ')}>{nDuplicates}</span>
                  {nDuplicates > 0 && <span className="text-[10px] text-orange-500/70">{nUnique} unique</span>}
                </button>
                <button
                  onClick={() => nErrors > 0 && setAnalysisFilter(f => f === 'errors' ? 'all' : 'errors')}
                  className={["rounded-xl border p-3 flex flex-col gap-0.5 text-left transition-all", nErrors > 0 ? (analysisFilter === 'errors' ? "border-destructive bg-destructive/10" : "border-destructive/50 bg-destructive/5 hover:border-destructive/70") : "border-border cursor-default"].join(' ')}
                >
                  <span className="text-xs text-muted-foreground">Errors</span>
                  <span className={["text-xl font-semibold tabular-nums", nErrors > 0 ? "text-destructive" : ""].join(' ')}>{nErrors}</span>
                </button>
              </div>
            )
          })()}

          {(!analysisDone || products.every(p => p.errors?.length)) && !analyzing && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium">Images per product</label>
                <input type="number" min={1} max={100} value={imgsPerProduct}
                  onChange={e => setImgsPerProduct(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-20 h-8 rounded-lg border border-border bg-background px-3 text-sm text-center tabular-nums focus:outline-none focus:ring-1 focus:ring-foreground/30" />
              </div>
              {analysisDone && products.every(p => p.errors?.length) && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm text-destructive">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div className="flex flex-col gap-1">
                    <span className="font-medium">Vision analysis failed for all products.</span>
                    <span>Check your API key and model name in <strong>Settings → API Keys</strong>. Common fix: use <code className="bg-destructive/10 px-1 rounded font-mono">gemini-2.0-flash-001</code> instead of <code className="bg-destructive/10 px-1 rounded font-mono">gemini-2.0-flash</code> for Vertex AI.</span>
                  </div>
                </div>
              )}
              <Button onClick={startAnalysis} className="w-fit">
                {analysisDone ? 'Retry analysis' : 'Start vision analysis'}
              </Button>
            </div>
          )}

          {analyzing && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                <span>{retryingGroups.size > 0 ? `Retrying` : 'Analyzing'} product {retryingGroups.size > 0 ? retryDone + 1 : Math.min(products.length + 1, analyzeTotal || products.length + 1)} of {analyzeTotal || '?'}…</span>
                <button
                  onClick={() => { pollCancelledRef.current = true; setAnalyzing(false); setAnalysisDone(true) }}
                  className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-destructive/40 text-destructive text-xs hover:bg-destructive/10 transition-colors"
                >
                  <Square className="w-3 h-3 fill-current" /> Stop
                </button>
              </div>
              {analyzeTotal > 0 && (
                <div className="flex flex-col gap-1">
                  <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full bg-foreground rounded-full transition-all duration-300" style={{ width: `${Math.round(((retryingGroups.size > 0 ? retryDone : products.length) / (analyzeTotal || 1)) * 100)}%` }} />
                  </div>
                  <div className="text-xs text-muted-foreground text-right">{retryingGroups.size > 0 ? retryDone : products.length} / {analyzeTotal}</div>
                </div>
              )}
            </div>
          )}

          {/* Product list — always visible once files are known */}
          {productList.length > 0 && (() => {
            const dupKeySet = new Set<string>()
            products.forEach(p => {
              const k = p.normalized_key || (p.extracted_marque && p.extracted_ref ? `${p.extracted_marque}__${p.extracted_ref}` : null)
              if (k) dupKeySet.add(k)
            })
            const dupGroups = new Set<string>()
            dupKeySet.forEach(k => {
              const matches = products.filter(p => (p.normalized_key || (p.extracted_marque && p.extracted_ref ? `${p.extracted_marque}__${p.extracted_ref}` : null)) === k)
              if (matches.length > 1) matches.slice(1).forEach(p => dupGroups.add(p.group))
            })
            const visibleList = analysisFilter === 'all'
              ? productList
              : analysisFilter === 'found'
                ? productList.filter(pl => products.find(p => p.group === pl.group)?.in_catalogue)
                : analysisFilter === 'duplicates'
                  ? productList.filter(pl => dupGroups.has(pl.group))
                  : analysisFilter === 'errors'
                    ? productList.filter(pl => (products.find(p => p.group === pl.group)?.errors?.length ?? 0) > 0)
                    : productList.filter(pl => { const d = products.find(p => p.group === pl.group); return d && !d.in_catalogue && !d.errors?.length })
            return (
              <div className="border border-border rounded-xl overflow-hidden">
                {analysisFilter !== 'all' && (
                  <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-secondary/30">
                    <span className="text-xs text-muted-foreground">
                      Showing {visibleList.length} {analysisFilter === 'found' ? 'found' : analysisFilter === 'duplicates' ? 'duplicate' : analysisFilter === 'errors' ? 'error' : 'unknown'} item{visibleList.length !== 1 ? 's' : ''}
                    </span>
                    <button onClick={() => setAnalysisFilter('all')} className="text-xs text-muted-foreground hover:text-foreground transition-colors">Show all</button>
                  </div>
                )}
                <div className="max-h-[480px] overflow-y-auto divide-y divide-border">
                  {(() => {
                    const keyCount = new Map<string, string[]>()
                    products.forEach(p => {
                      const k = p.normalized_key || (p.extracted_marque && p.extracted_ref ? `${p.extracted_marque}__${p.extracted_ref}` : null)
                      if (k) { if (!keyCount.has(k)) keyCount.set(k, []); keyCount.get(k)!.push(p.group) }
                    })
                    const duplicateOf = new Map<string, string[]>()
                    keyCount.forEach(groups => { if (groups.length > 1) groups.forEach(g => duplicateOf.set(g, groups.filter(x => x !== g))) })
                    return visibleList.map((pl) => {
                    const done = products.find(p => p.group === pl.group)
                    const isCurrent = analyzing && !done && productList.indexOf(pl) === products.length
                    const isRetrying = retryingGroups.has(pl.group)
                    const dupeOf = done ? duplicateOf.get(pl.group) : undefined
                    const rows = [
                      { label: 'Marque', filename: pl.marque_image, result: done?.extracted_marque },
                      { label: 'Ref',    filename: pl.ref_image,    result: done?.extracted_ref ?? done?.ref },
                    ]
                    return (
                      <div key={pl.group} className="px-4 py-3 flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          {isRetrying
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-500 shrink-0" />
                            : done
                              ? done.errors?.length
                                ? <AlertCircle className="w-3.5 h-3.5 text-destructive shrink-0" />
                                : done.in_catalogue
                                  ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
                                  : <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                              : isCurrent
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground shrink-0" />
                                : <span className="w-3.5 h-3.5 rounded-full border border-border shrink-0" />
                          }
                          <span className="text-sm font-semibold">{pl.group}</span>
                          <span className="text-xs text-muted-foreground">{pl.total_images} images</span>
                          {dupeOf && (
                            <>
                              <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-600 border border-orange-400/30" title={`Same product as ${dupeOf.join(', ')}`}>
                                ⚠ duplicate of {dupeOf.join(', ')}
                              </span>
                              <button
                                onClick={() => removeProduct(pl.group)}
                                className="text-xs px-2 py-0.5 rounded border border-destructive/40 text-destructive hover:bg-destructive/10 transition-colors"
                              >
                                Remove
                              </button>
                            </>
                          )}
                          {done?.in_catalogue && (
                            <span className="ml-auto text-xs text-green-600 font-medium">
                              {done.brand} · {done.product_ref}
                              {done.catalogue_name && <span className="ml-1.5 text-green-500/70 font-normal">({done.catalogue_name})</span>}
                            </span>
                          )}
                          {done && !done.in_catalogue && !done.errors?.length && !isRetrying && (
                            <div className="ml-auto flex items-center gap-2">
                              <span className="text-xs text-amber-600 italic">not in catalogue</span>
                              <button
                                onClick={() => retryGroups([pl.group])}
                                disabled={analyzing}
                                className="flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-amber-400/50 text-amber-600 hover:bg-amber-500/10 disabled:opacity-40 transition-colors"
                              >
                                <Play className="w-2.5 h-2.5" />Retry
                              </button>
                              <button
                                onClick={() => setManualPickProduct(done)}
                                disabled={analyzing}
                                className="flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-border text-muted-foreground hover:bg-secondary/60 hover:text-foreground disabled:opacity-40 transition-colors"
                              >
                                <Pencil className="w-2.5 h-2.5" />Fix
                              </button>
                            </div>
                          )}
                          {done?.errors?.length && !isRetrying && (
                            <button
                              onClick={() => retryGroups([pl.group])}
                              disabled={analyzing}
                              className="ml-auto flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-destructive/40 text-destructive hover:bg-destructive/10 disabled:opacity-40 transition-colors"
                            >
                              <Play className="w-2.5 h-2.5" />Retry
                            </button>
                          )}
                        </div>
                        <div className="pl-5 flex flex-col gap-1">
                          {rows.map(row => (
                            <div key={row.label} className="grid grid-cols-[48px_1fr_auto] items-center gap-2 text-xs">
                              <span className="text-muted-foreground font-medium">{row.label}</span>
                              <span className="font-mono text-muted-foreground truncate">{row.filename}</span>
                              {done
                                ? done.errors?.length
                                  ? <span className="text-destructive italic text-right truncate max-w-[260px]">
                                      {done.errors.find(e => e.toLowerCase().startsWith(row.label.toLowerCase())) ?? done.errors[0]}
                                    </span>
                                  : <span className="font-mono font-semibold text-foreground truncate max-w-[180px] text-right">{row.result ?? '—'}</span>
                                : isCurrent
                                  ? <span className="text-muted-foreground italic text-right">In progress…</span>
                                  : <span className="text-muted-foreground/50 text-right">N/A</span>
                              }
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })
                  })()}
                </div>
              </div>
            )
          })()}

          {manualPickProduct && (
            <ManualPickModal
              product={manualPickProduct}
              onPick={cat => applyManualPick(manualPickProduct.group, cat)}
              onApplyMarque={marque => applyManualMarque(manualPickProduct.group, marque)}
              onClose={() => setManualPickProduct(null)}
            />
          )}

          {analysisDone && products.length > 0 && (
            <div className="flex items-center gap-3 flex-wrap">
              <OrganizeButton
                products={products}
                onOrganize={(uniqueCount) => { onSave(job.id, { skuCount: uniqueCount }); setOrganizeResult(null); setStep(3) }}
                alreadyOrganized={!!organizeResult}
                onViewOrganized={() => setStep(3)}
              />
              {!analyzing && (
                <>
                  <Button variant="outline" size="sm" onClick={startAnalysis} className="gap-1.5">
                    <Play className="w-3.5 h-3.5" /> Re-scan all
                  </Button>
                  {(() => { const done = new Set(products.map(p => p.group)); const n = productList.filter(pl => !done.has(pl.group)).length; return n > 0 ? (
                    <Button variant="outline" size="sm" onClick={continueScan} className="gap-1.5 border-blue-400/60 text-blue-600 hover:bg-blue-500/10">
                      <Play className="w-3.5 h-3.5" /> Continue ({n} remaining)
                    </Button>
                  ) : null })()}
                  {products.some(p => !p.in_catalogue && !p.errors?.length) && (
                    <Button variant="outline" size="sm" onClick={retryAllNotFound} className="gap-1.5 border-amber-400/60 text-amber-600 hover:bg-amber-500/10">
                      <Play className="w-3.5 h-3.5" /> Retry unknown ({products.filter(p => !p.in_catalogue && !p.errors?.length).length})
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Step 2 — Organize ── */}
      {step === 3 && (
        <div className="flex flex-col gap-5">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Organize files</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Structure the output folder and rename images</p>
          </div>

          {!organizeResult && (
            <div className="flex flex-col gap-4">
              {(() => {
                type OrgMode = 'ref' | 'marque' | 'marque-ref-base' | 'marque-forme'
                const toSlug = (s: string) => s.replace(/\s+/g, '_').toUpperCase()
                const sample = products.filter(p => !('errors' in p && (p as {errors?:string[]}).errors?.length)).slice(0, 3)
                const buildTree = (mode: OrgMode): { line: string; indent: number }[] => {
                  const lines: { line: string; indent: number }[] = []
                  const seen = new Set<string>()
                  for (const p of sample) {
                    const marque = toSlug(p.extracted_marque || p.brand || 'UNKNOWN')
                    const refRaw = toSlug(p.product_ref || p.extracted_ref || p.ref || '')
                    const refBase = refRaw.includes('_') ? refRaw.split('_')[0] : refRaw
                    const forme = p.forme ? toSlug(p.forme) : 'UNKNOWN'
                    if (mode === 'ref') {
                      if (!seen.has(refRaw)) { lines.push({ line: refRaw + '/', indent: 0 }); seen.add(refRaw) }
                    } else if (mode === 'marque') {
                      if (!seen.has(marque)) { lines.push({ line: marque + '/', indent: 0 }); seen.add(marque) }
                      lines.push({ line: refRaw + '/', indent: 1 })
                    } else if (mode === 'marque-ref-base') {
                      if (!seen.has(marque)) { lines.push({ line: marque + '/', indent: 0 }); seen.add(marque) }
                      const bk = marque + '/' + refBase
                      if (!seen.has(bk)) { lines.push({ line: refBase + '/', indent: 1 }); seen.add(bk) }
                      lines.push({ line: refRaw + '/', indent: 2 })
                    } else {
                      if (!seen.has(marque)) { lines.push({ line: marque + '/', indent: 0 }); seen.add(marque) }
                      const fk = marque + '/' + forme
                      if (!seen.has(fk)) { lines.push({ line: forme + '/', indent: 1 }); seen.add(fk) }
                      const bk = fk + '/' + refBase
                      if (!seen.has(bk)) { lines.push({ line: refBase + '/', indent: 2 }); seen.add(bk) }
                      lines.push({ line: refRaw + '/', indent: 3 })
                    }
                  }
                  return lines
                }

                const OPTS: { id: OrgMode; section: 'basic' | 'advanced'; label: string }[] = [
                  { id: 'ref',             section: 'basic',    label: 'By Reference' },
                  { id: 'marque',          section: 'advanced', label: 'Marque › Référence' },
                  { id: 'marque-ref-base', section: 'advanced', label: 'Marque › Base Référence › Référence' },
                  { id: 'marque-forme',    section: 'advanced', label: 'Marque › Forme › Base Référence › Référence' },
                ]
                const basic    = OPTS.filter(o => o.section === 'basic')
                const advanced = OPTS.filter(o => o.section === 'advanced')

                const Card = ({ opt }: { opt: typeof OPTS[number] }) => {
                  const tree = buildTree(opt.id)
                  const active = organizeBy === opt.id
                  return (
                    <button onClick={() => setOrganizeBy(opt.id as typeof organizeBy)}
                      className={["flex flex-col gap-3 p-3.5 rounded-xl border text-left transition-all w-full",
                        active ? "border-foreground/60 bg-secondary/40" : "border-border hover:border-foreground/30"].join(" ")}>
                      <div className="flex items-center gap-2">
                        <FolderOpen className={["w-4 h-4 shrink-0", active ? "text-foreground" : "text-muted-foreground"].join(" ")} />
                        <span className="text-sm font-semibold">{opt.label}</span>
                      </div>
                      {sample.length > 0 && (
                        <div className="font-mono text-[11px] leading-relaxed text-muted-foreground space-y-0.5 max-h-40 overflow-y-auto">
                          {tree.map((l, i) => (
                            <div key={i} className="flex items-center gap-1" style={{ paddingLeft: l.indent * 14 }}>
                              <span className="text-muted-foreground/50">{l.indent > 0 ? '└─' : ''}</span>
                              <span className={active ? "text-foreground/80" : ""}>{l.line}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </button>
                  )
                }

                return (
                  <div className="flex flex-col gap-4">
                    <div>
                      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-2">Basic</p>
                      <div className="grid grid-cols-1 gap-2">
                        {basic.map(o => <Card key={o.id} opt={o} />)}
                      </div>
                    </div>
                    <div>
                      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-2">Advanced</p>
                      <div className="grid grid-cols-3 gap-2">
                        {advanced.map(o => <Card key={o.id} opt={o} />)}
                      </div>
                    </div>
                  </div>
                )
              })()}

              {organizeErrors.length > 0 && organizeErrors.map((e, i) => (
                <div key={i} className="flex items-start gap-2 text-xs rounded-lg p-2.5 bg-destructive/10 text-destructive">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{e}
                </div>
              ))}
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(2)}>← Back</Button>
                <Button onClick={startOrganize} disabled={organizing || products.length === 0}>
                  {organizing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Organizing…</> : 'Organize files'}
                </Button>
              </div>
            </div>
          )}

          {organizeResult && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                <span><strong>{organizeResult.renamed_count} images</strong> renamed and organized</span>
              </div>
              <div className="border border-border rounded-xl p-3 bg-secondary/10 text-xs max-h-96 overflow-y-auto">
                <FolderTreeView node={organizeResult.root} depth={0} />
              </div>
              <div className="flex items-center gap-3">
                <Button onClick={() => setStep(4)} className="w-fit">Assign avatars →</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Step 3 — Configure ── */}
      {step === 1 && (
        <div className="flex flex-col gap-6">
          {isCloud && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">Source URL</label>
              <Input value={sourceUrl} onChange={e => setSourceUrl(e.target.value)}
                placeholder={job.source === 'drive' ? 'https://drive.google.com/drive/folders/…' : 'https://www.dropbox.com/sh/…'}
                className="h-9 text-sm" />
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">Output format</label>
            <div className="flex rounded-lg border overflow-hidden w-fit">
              {(['jpeg','png','webp'] as const).map(f => (
                <button key={f} onClick={() => setOutputFormat(f)}
                  className={["px-4 py-2 text-sm font-medium transition-colors border-l first:border-l-0", outputFormat === f ? "bg-foreground text-background" : "hover:bg-secondary/50"].join(' ')}>
                  {f.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">Resolution</label>
            <div className="flex rounded-lg border overflow-hidden w-fit">
              {([['1080x1920','1K'],['1440x2560','2K'],['2160x3840','4K']] as const).map(([val, lbl]) => (
                <button key={val} onClick={() => setOutputRes(val)}
                  className={["px-4 py-2 text-sm font-medium transition-colors border-l first:border-l-0", outputRes === val ? "bg-foreground text-background" : "hover:bg-secondary/50"].join(' ')}>
                  {lbl}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">Higher resolution = more API tokens billed to your key.</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">AI input size</label>
            <div className="flex rounded-lg border overflow-hidden w-fit">
              {([[512,'512px'],[1024,'1024px'],[2048,'2048px'],[0,'Original']] as const).map(([val, lbl]) => (
                <button key={lbl} onClick={() => setJobMaxInputPx(val)}
                  className={["px-4 py-2 text-sm font-medium transition-colors border-l first:border-l-0", (jobMaxInputPx ?? 1024) === val ? "bg-foreground text-background" : "hover:bg-secondary/50"].join(' ')}>
                  {lbl}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">Product photos are downscaled to this size before the AI call — 1024px is the recommended default.</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">Batch mode</label>
            <button onClick={() => setBatchMode(v => !v)}
              className={["flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium w-fit transition-colors", batchMode ? "border-violet-500 bg-violet-500/10 text-violet-600 dark:text-violet-400" : "hover:bg-secondary/50"].join(' ')}>
              <Clock className="w-4 h-4" />
              {batchMode ? 'Batch enabled — −50% cost' : 'Enable batch (−50% cost)'}
            </button>
            <p className="text-[11px] text-muted-foreground max-w-md">
              Generation runs via the provider's batch API at half price — results arrive within 24h.
              Optique and solaire are both included in the batch; live regenerate keeps the transform option.
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">File naming</label>
            <div className="flex rounded-lg border overflow-hidden w-fit">
              {([['sku','SKU'],['marque-sku','Marque + SKU'],['custom','Custom']] as const).map(([val, lbl]) => (
                <button key={val} onClick={() => setNaming(val)}
                  className={["px-4 py-2 text-sm font-medium transition-colors border-l first:border-l-0", naming === val ? "bg-foreground text-background" : "hover:bg-secondary/50"].join(' ')}>
                  {lbl}
                </button>
              ))}
            </div>
            {naming === 'custom' && (
              <div className="flex flex-col gap-2 max-w-sm">
                <div className="flex flex-wrap gap-1.5">
                  {catalogueTokens.map(({ token, label }) => (
                    <button key={token} type="button"
                      onClick={() => setNamingCustom(p => p + token)}
                      title={token}
                      className="px-2 py-0.5 rounded border text-xs font-mono hover:bg-secondary transition-colors">
                      {label}
                    </button>
                  ))}
                </div>
                <Input value={namingCustom} onChange={e => setNamingCustom(e.target.value)}
                  placeholder="{marque}_{sku}_{index}" className="h-9 text-sm font-mono" />
                {namingHistory.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <span className="text-[11px] text-muted-foreground">Recent</span>
                    {namingHistory.map(h => (
                      <button key={h} type="button" onClick={() => setNamingCustom(h)}
                        className="text-left text-xs font-mono text-muted-foreground hover:text-foreground px-1 transition-colors truncate">
                        {h}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {(() => {
              const exMap: Record<string, string> = {
                ...namingExample,
                '{index}':    '01',
                '{raw_name}': (productList[0]?.group ?? 'DSC00265').toUpperCase(),
              }
              const apply = (pat: string) => Object.entries(exMap).reduce((s,[k,v]) => s.replaceAll(k,v), pat)
              const exProduit= exMap['{Produit}'] ?? ''
              const exMarque = exMap['{Marque}']  ?? ''
              return (
                <p className="text-xs text-muted-foreground">
                  {naming === 'sku'        && (exProduit  ? `Output: ${exProduit}.${outputFormat}`                  : null)}
                  {naming === 'marque-sku' && (exMarque  ? `Output: ${exMarque}_${exProduit}.${outputFormat}`     : null)}
                  {naming === 'custom'     && (namingCustom ? `Output: ${apply(namingCustom)}.${outputFormat}` : 'Clique sur les tokens pour construire ton pattern')}
                </p>
              )
            })()}
          </div>

          <div className="flex items-center gap-3">
            <button onClick={() => {
                if (naming === 'custom' && namingCustom.trim()) {
                  setNamingHistory(prev => [namingCustom.trim(), ...prev.filter(h => h !== namingCustom.trim())].slice(0, 5))
                }
                // Persist this setup as the tenant default for future jobs
                // (merge — never clobber other custom properties)
                settingsApi.get().then(st => settingsApi.update({
                  custom_properties: {
                    ...(st.custom_properties ?? {}),
                    outputFormat, outputResolution: outputRes,
                    maxInputPx: jobMaxInputPx ?? 1024, batchMode, naming,
                  },
                })).catch(() => {})
                setStep(2)
              }}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 transition-colors">
              Next: Vision Analysis →
            </button>
            {skuRules.length > 0 && (
              <Button variant="outline" size="sm" onClick={applyRules}>
                <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                Auto-assign from rules
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Step 2 — Assign avatars */}
      {step === 4 && <>
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold">Assign avatar and type</h2>
          <p className="text-sm text-muted-foreground">{assignedCount} / {skus.length} SKUs assigned</p>
        </div>
        <button
          onClick={() => buildSkus(true)}
          disabled={!catalogueProds.length}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-foreground/20 hover:bg-secondary/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
        >
          Auto-assign depuis catalogue
        </button>
      </div>

      {/* View toggle */}
      <div className="flex items-center gap-2 justify-between flex-wrap">
        <div className="flex rounded-lg border overflow-hidden">
          <button onClick={() => { setViewMode('grid'); setDrillAvatar(null) }} className={["flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors", viewMode === 'grid' ? "bg-foreground text-background" : "hover:bg-secondary/50"].join(' ')}>
            <LayoutGrid className="w-3.5 h-3.5" />Grid
          </button>
          <button onClick={() => { setViewMode('avatar'); setDrillAvatar(null) }} className={["flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border-l", viewMode === 'avatar' ? "bg-foreground text-background" : "hover:bg-secondary/50"].join(' ')}>
            <Columns className="w-3.5 h-3.5" />By Avatar
          </button>
        </div>
        {(viewMode === 'grid' || (viewMode === 'avatar' && !!drillAvatar)) && (
          <div className="flex rounded-lg border overflow-hidden">
            {viewMode === 'grid' && (
              <button onClick={() => setGridGroup('avatar')} className={["px-3 py-1.5 text-xs font-medium transition-colors", gridGroup === 'avatar' ? "bg-foreground text-background" : "hover:bg-secondary/50"].join(' ')}>
                Avatar
              </button>
            )}
            <button onClick={() => setGridGroup('sku-type')} className={["px-3 py-1.5 text-xs font-medium transition-colors", (viewMode === 'avatar' && !!drillAvatar) ? "" : "border-l", gridGroup === 'sku-type' ? "bg-foreground text-background" : "hover:bg-secondary/50"].join(' ')}>
              Marque
            </button>
            <button onClick={() => setGridGroup('type')} className={["px-3 py-1.5 text-xs font-medium transition-colors border-l", gridGroup === 'type' ? "bg-foreground text-background" : "hover:bg-secondary/50"].join(' ')}>
              Type
            </button>
          </div>
        )}
      </div>

      {/* Searchbar */}
      {(() => {
        const noResults = search.trim() !== '' && filteredSkus.length === 0
        return (
          <div className="relative">
            <Search className={["absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none", noResults ? "text-red-400" : "text-muted-foreground"].join(' ')} />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search SKU or folder…"
              className={["w-full h-9 pl-8 pr-3 text-sm rounded-lg border bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 transition-colors", noResults ? "border-red-300 bg-red-50 focus:ring-red-200 text-red-600" : "border-border focus:ring-foreground/20"].join(' ')}
            />
            {search && (
              <button onClick={() => setSearch('')} className={["absolute right-2 top-1/2 -translate-y-1/2 transition-colors", noResults ? "text-red-400 hover:text-red-600" : "text-muted-foreground hover:text-foreground"].join(' ')}>
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )
      })()}
      </>}

      {/* ── Step 3 — Check & Validate ── */}
      {step === 5 && (() => {
        const assignedSkus = skus.filter(s => s.avatar)
        const unassigned = skus.filter(s => !s.avatar)
        const nOptique = skus.filter(s => hasType(s, 'optique')).length
        const nSolaire = skus.filter(s => hasType(s, 'solaire')).length
        const ready = unassigned.length === 0
        return (
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border p-4 flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">Products</span>
                <span className="text-2xl font-semibold tabular-nums">{skus.length}</span>
              </div>
              <div className="rounded-xl border p-4 flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">Assigned</span>
                <span className={["text-2xl font-semibold tabular-nums", ready ? "text-green-600" : "text-destructive"].join(' ')}>{assignedSkus.length} / {skus.length}</span>
              </div>
              <div className="rounded-xl border p-4 flex flex-col gap-1">
                <span className="text-xs text-muted-foreground flex items-center gap-1.5"><svg width="14" height="8" viewBox="0 0 24 12" fill="none" stroke="currentColor" strokeWidth="2"><rect x="0.9" y="2.9" width="7.2" height="6.2" rx="3.1"/><rect x="15.9" y="2.9" width="7.2" height="6.2" rx="3.1"/><line x1="8.1" y1="6" x2="15.9" y2="6"/></svg>Optique</span>
                <span className="text-2xl font-semibold tabular-nums">{nOptique}</span>
              </div>
              <div className="rounded-xl border p-4 flex flex-col gap-1">
                <span className="text-xs text-muted-foreground flex items-center gap-1.5"><svg width="14" height="8" viewBox="0 0 24 12" fill="currentColor"><rect x="0" y="2" width="9" height="8" rx="4"/><rect x="15" y="2" width="9" height="8" rx="4"/><rect x="9" y="5" width="6" height="2"/></svg>Solaire</span>
                <span className="text-2xl font-semibold tabular-nums">{nSolaire}</span>
              </div>
            </div>


            {!ready && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-xs">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                {unassigned.length} product{unassigned.length > 1 ? 's' : ''} not assigned — go back to step 4
              </div>
            )}


            {/* ── Generate button ── */}
            {ready && modulePrompts.length > 0 && (
              <div className="flex flex-col gap-2">
                {genProgress && (
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{genProgress.lastSku}</span>
                      <span className="tabular-nums">{genProgress.done} / {genProgress.total}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                      <div
                        className="h-full bg-foreground rounded-full transition-all duration-300"
                        style={{ width: `${Math.round((genProgress.done / Math.max(genProgress.total, 1)) * 100)}%` }}
                      />
                    </div>
                    {genProgress.errors.length > 0 && (
                      <p className="text-[10px] text-destructive">{genProgress.errors.slice(-1)[0]}</p>
                    )}
                  </div>
                )}
                <button
                  disabled={generating}
                  onClick={() => {
                    if (generating) { genAbortRef.current?.abort(); return }
                    setGenProgress(null)
                    const ctrl = new AbortController()
                    genAbortRef.current = ctrl
                    startGenStream(
                      undefined,
                      e => {
                        // pct no longer used for PATCH — live progress uses local state
                        setGenProgress(prev => ({
                          done: e.done,
                          total: e.total,
                          lastSku: e.sku,
                          errors: e.error ? [...(prev?.errors ?? []), `${e.sku} (${e.type}): ${e.error}`] : (prev?.errors ?? []),
                        }))
                        if (e.url) withSuppressedSave(() => setSkus(prev => prev.map(s => { if (s.sku !== e.sku) return s; const tf = `_${e.type}.`; return { ...s, outputImages: [...(s.outputImages ?? []).filter(u => !u.includes(tf)), e.url!] } })))
                        // No onSave PATCH for pct — live progress uses local state
                      },
                      () => {
                        setGenerating(false)
                        jobsApi.getProgress(Number(job.id)).then(p => {
                          const finalStatus = deriveFinalStatus(p)
                          onSave(job.id, { status: finalStatus, progress: 100 })
                          setLastProgress(p)
                          setSkus(prev => prev.map(s => {
                            const meta = p.skus.find(m => m.sku === s.sku)
                            if (!meta) return s
                            const safeOut2 = toImageArray(meta.output_images); const safeExist2 = toImageArray(s.outputImages)
                            return { ...s, outputImages: safeOut2.length ? safeOut2 : safeExist2, thumbnail: meta.thumbnail ?? s.thumbnail, skipped: meta.skipped ?? s.skipped, skip_reason: meta.skip_reason ?? s.skip_reason }
                          }))
                        }).catch(() => { onSave(job.id, { status: 'error', progress: 100 }) })
                      },
                      () => { setGenerating(false); setStreamError('Connection lost — job still running') },
                      ctrl,
                      (isReconnect) => { setGenerating(true); if (!isReconnect) onSave(job.id, { status: 'processing', progress: 0 }) },
                    )
                  }}
                  className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-foreground/30 text-sm font-medium hover:bg-secondary/50 disabled:opacity-50 transition-colors"
                >
                  {generating
                    ? <><Loader2 className="w-4 h-4 animate-spin" />{queuePosition !== null ? `Queue #${queuePosition}…` : 'Generating…'}</>
                    : '⚡ Generate images'}
                </button>
              </div>
            )}

            <button
              disabled={!ready}
              onClick={() => { onSave(job.id, { name: name.trim() || job.name, sourceUrl: sourceUrl.trim() || undefined, outputFormat, outputResolution: outputRes, namingConvention: naming, namingCustom: namingCustom.trim() || undefined, status: 'not_started' }); onBack() }}
              className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Save →
            </button>
          </div>
        )
      })()}

      {/* ── VIEW: GRID (grouped by avatar) ── */}
      {step === 4 && viewMode === 'grid' && (
        <div className="flex flex-col gap-6">
          {grouped.filter(g => g.items.length > 0).map((group, gi) => (
            <div key={group.label} className="flex flex-col gap-3">
              {gi > 0 && (gridGroup === 'avatar' || gridGroup === 'type') && <div className="h-px bg-border" />}
              {(gridGroup === 'sku-type' || gridGroup === 'type') && (
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">{group.label}</span>
                  <button
                    onClick={() => {
                      const ids = group.items.map(s => s.id)
                      const allSelected = ids.every(id => selected.has(id))
                      setSelected(prev => {
                        const next = new Set(prev)
                        if (allSelected) ids.forEach(id => next.delete(id)); else ids.forEach(id => next.add(id))
                        return next
                      })
                    }}
                    className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {group.items.every(s => selected.has(s.id)) ? 'Deselect all' : 'Select all'}
                  </button>
                </div>
              )}
              <div className="grid grid-cols-[repeat(auto-fill,minmax(110px,1fr))] gap-3">
                {group.items.map(sku => (
                  <SkuCard key={sku.id} sku={sku} selected={selected.has(sku.id)} avatars={avatars}
                    onToggle={e => toggleSelect(sku.id, e, group.items)}
                    onPreview={() => setPreview(sku)}
                    onAssign={id => assignAvatar([sku.id], id)}
                    onTypeChange={type => assignSkuType(sku.id, type)}
                    onModelChange={model => assignSkuModel(sku.id, model)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── VIEW: BY AVATAR — drill-down ── */}
      {/* eslint-disable-next-line react-hooks/refs */}
      {step === 4 && viewMode === 'avatar' && drillAvatar && (() => {
        const drillSkus = filteredSkus.filter(s => s.avatar === drillAvatar)
        const unassigned = filteredSkus.filter(s => !s.avatar)
        return (
          <div className="flex flex-col gap-4">
            {/* Header */}
            <div className="flex items-center gap-3">
              <button onClick={() => { setDrillAvatar(null); setDrillPicking(false); setDrillPicked(new Set()) }} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors shrink-0">
                ← Back
              </button>

              <div className="ml-auto">
                {!drillPicking && (
                  <button onClick={() => setDrillPicking(true)} className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border hover:bg-secondary/50 transition-colors">
                    <Plus className="w-3.5 h-3.5" />Add items
                  </button>
                )}
                {drillPicking && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{drillPicked.size} selected</span>
                    <button
                      disabled={drillPicked.size === 0}
                      onClick={() => { assignAvatar([...drillPicked], drillAvatar); setDrillPicked(new Set()); setDrillPicking(false) }}
                      className="text-sm font-medium px-3 py-1.5 rounded-lg bg-foreground text-background hover:bg-foreground/90 disabled:opacity-40 transition-colors"
                    >
                      Assign {drillPicked.size > 0 ? drillPicked.size : ''} items
                    </button>
                    <button onClick={() => { setDrillPicking(false); setDrillPicked(new Set()) }} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Pick mode: show unassigned, grouped */}
            {drillPicking && (
              <div className="flex flex-col gap-6">
                {unassigned.length === 0
                  ? <div className="text-sm text-muted-foreground text-center py-8">All SKUs already assigned</div>
                  : (() => {
                      const pickGroups = gridGroup === 'type'
                        ? [
                            { label: 'Optique & Solaire', items: unassigned.filter(s => hasType(s, 'optique') && hasType(s, 'solaire')) },
                            { label: 'Optique',           items: unassigned.filter(s => hasType(s, 'optique') && !hasType(s, 'solaire')) },
                            { label: 'Solaire',           items: unassigned.filter(s => hasType(s, 'solaire') && !hasType(s, 'optique')) },
                            { label: 'Untyped',           items: unassigned.filter(s => toTypesSet(s.types).size === 0) },
                          ].filter(g => g.items.length > 0)
                        : gridGroup === 'sku-type'
                        ? (() => { const m = new Map<string, SkuEntry[]>(); unassigned.forEach(s => { const k = s.sku.slice(0,3); if (!m.has(k)) m.set(k,[]); m.get(k)!.push(s) }); return [...m.entries()].map(([k,items]) => ({ label: k, items })) })()
                        : [{ label: '', items: unassigned }]
                      return pickGroups.map(g => (
                        <div key={g.label} className="flex flex-col gap-3">
                          {g.label && <><div className="h-px bg-border" /><span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">{g.label}</span></>}
                          <div className="grid grid-cols-[repeat(auto-fill,minmax(110px,1fr))] gap-3">
                            {g.items.map(sku => (
                              <div key={sku.id} onClick={() => setDrillPicked(prev => { const s = new Set(prev); if (s.has(sku.id)) s.delete(sku.id); else s.add(sku.id); return s })}
                                className={["flex flex-col rounded-xl border cursor-pointer transition-all", drillPicked.has(sku.id) ? "border-foreground/50 ring-1 ring-foreground/20 bg-secondary/30" : "border-border hover:border-foreground/20"].join(' ')}>
                                <div className="relative aspect-square bg-secondary/40 flex items-center justify-center rounded-t-xl overflow-hidden">
                                  {sku.images
                                    ? <img src={sku.images[0]} alt={sku.sku} className="w-full h-full object-cover" />
                                    : <HardDrive className="w-6 h-6 text-muted-foreground/20" />}
                                  {drillPicked.has(sku.id) && <div className="absolute top-1.5 left-1.5 w-4 h-4 rounded bg-foreground flex items-center justify-center"><Check className="w-2.5 h-2.5 text-background" /></div>}
                                </div>
                                <div className="px-2 py-1.5">
                                  <span className="text-[9px] text-muted-foreground truncate block">{sku.folder}</span>
                                  <span className="text-[10px] font-medium truncate block">{sku.sku}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    })()
                }
              </div>
            )}

            {/* Assigned SKUs */}
            {!drillPicking && (drillSkus.length === 0
              ? <div className="text-sm text-muted-foreground text-center py-8">No items — click Add items to assign</div>
              : <div className="flex flex-col gap-6">
                  {(() => {
                    const drillGrouped = gridGroup === 'type'
                      ? [
                          { label: 'Optique & Solaire', items: drillSkus.filter(s => hasType(s, 'optique') && hasType(s, 'solaire')) },
                          { label: 'Optique',           items: drillSkus.filter(s => hasType(s, 'optique') && !hasType(s, 'solaire')) },
                          { label: 'Solaire',           items: drillSkus.filter(s => hasType(s, 'solaire') && !hasType(s, 'optique')) },
                          { label: 'Untyped',           items: drillSkus.filter(s => toTypesSet(s.types).size === 0) },
                        ].filter(g => g.items.length > 0)
                      : gridGroup === 'sku-type'
                      ? (() => { const m = new Map<string, SkuEntry[]>(); drillSkus.forEach(s => { const k = s.sku.slice(0,3); if (!m.has(k)) m.set(k,[]); m.get(k)!.push(s) }); return [...m.entries()].map(([k,items]) => ({ label: k, items })) })()
                      : [{ label: '', items: drillSkus }]
                    return drillGrouped.map((g) => (
                      <div key={g.label} className="flex flex-col gap-3">
                        {g.label && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">{g.label}</span>
                            <button
                              onClick={() => {
                                const ids = g.items.map(s => s.id)
                                const allSel = ids.every(id => selected.has(id))
                                setSelected(prev => { const next = new Set(prev); if (allSel) ids.forEach(id => next.delete(id)); else ids.forEach(id => next.add(id)); return next })
                              }}
                              className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                            >
                              {g.items.every(s => selected.has(s.id)) ? 'Deselect all' : 'Select all'}
                            </button>
                          </div>
                        )}
                        <div className="grid grid-cols-[repeat(auto-fill,minmax(110px,1fr))] gap-3">
                          {g.items.map(sku => (
                            <SkuCard key={sku.id} sku={sku} selected={selected.has(sku.id)} avatars={avatars}
                              onToggle={e => toggleSelect(sku.id, e, g.items)}
                              onPreview={() => setPreview(sku)}
                              onAssign={id => assignAvatar([sku.id], id)}
                              onTypeChange={type => assignSkuType(sku.id, type)}
                              onModelChange={model => assignSkuModel(sku.id, model)} />
                          ))}
                        </div>
                      </div>
                    ))
                  })()}
                </div>
            )}
          </div>
        )
      })()}

      {/* ── VIEW: BY AVATAR — grid ── */}
      {step === 4 && viewMode === 'avatar' && !drillAvatar && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
          {avatars.map(avatar => {
            const count = skus.filter(s => s.avatar === avatar.id).length
            return (
              <div key={avatar.id} onClick={() => setDrillAvatar(avatar.id)} className="flex flex-col rounded-xl border overflow-hidden cursor-pointer hover:border-foreground/30 transition-colors">
                <div className="aspect-[2/3] bg-secondary/40 overflow-hidden">
                  <img src={avatar.img} alt="" className="w-full h-full object-cover object-top" />
                </div>
                <div className="px-2 py-1.5 flex items-center justify-center gap-2 flex-wrap">
                  {count === 0 ? (
                    <span className="text-xs text-muted-foreground">0 item</span>
                  ) : (() => {
                    const avatarSkus = skus.filter(s => s.avatar === avatar.id)
                    const nOptique = avatarSkus.filter(s => hasType(s, 'optique')).length
                    const nSolaire = avatarSkus.filter(s => hasType(s, 'solaire')).length
                    return (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="tabular-nums">{count} item{count !== 1 ? 's' : ''}</span>
                        {nOptique > 0 && (
                          <span className="flex items-center gap-0.5">
                            <svg width="14" height="8" viewBox="0 0 24 12" fill="none" stroke="currentColor" strokeWidth="2"><rect x="0.9" y="2.9" width="7.2" height="6.2" rx="3.1"/><rect x="15.9" y="2.9" width="7.2" height="6.2" rx="3.1"/><line x1="8.1" y1="6" x2="15.9" y2="6"/></svg>
                            {nOptique}
                          </span>
                        )}
                        {nSolaire > 0 && (
                          <span className="flex items-center gap-0.5">
                            <svg width="14" height="8" viewBox="0 0 24 12" fill="currentColor"><rect x="0" y="2" width="9" height="8" rx="4"/><rect x="15" y="2" width="9" height="8" rx="4"/><rect x="9" y="5" width="6" height="2"/></svg>
                            {nSolaire}
                          </span>
                        )}
                      </div>
                    )
                  })()}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Next FAB — nothing selected + all assigned */}
      {step === 4 && selected.size === 0 && skus.length > 0 && skus.every(s => s.avatar !== null) && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
          <button
            onClick={() => setStep(5)}
            className="flex items-center gap-2 px-10 py-4 rounded-full bg-foreground text-background text-base font-semibold shadow-xl hover:bg-foreground/90 transition-colors"
          >
            Next: Check &amp; Validate →
          </button>
        </div>
      )}

      {/* Floating multi-select FAB */}
      {selected.size > 0 && (
        <FloatingAssignBar
          count={selected.size}
          avatars={avatars}
          onAssign={avatarId => { assignAvatar(selectedArr, avatarId); setSelected(new Set()) }}
          onUnassign={() => { assignAvatar(selectedArr, null); setSelected(new Set()) }}
          onClose={() => setSelected(new Set())}
          typeValue={(() => {
            const allOptique = selectedArr.every(id => hasType(skus.find(s => s.id === id), 'optique'))
            const allSolaire = selectedArr.every(id => hasType(skus.find(s => s.id === id), 'solaire'))
            if (allOptique && allSolaire) return 'both'
            if (allSolaire) return 'solaire'
            if (allOptique) return 'optique'
            return 'mixed'
          })()}
          onSetType={value => {
            setSkus(prev => prev.map(s => {
              if (!selectedArr.includes(s.id)) return s
              const types = new Set<'optique' | 'solaire'>()
              if (value === 'optique' || value === 'both') types.add('optique')
              if (value === 'solaire' || value === 'both') types.add('solaire')
              return { ...s, types }
            }))
          }}
        />
      )}

      </> /* end configure flow */}

      {/* Add SKU overlay (avatar view) */}
      {addingTo && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end justify-center" onClick={() => setAddingTo(null)}>
          <div className="bg-background rounded-t-2xl w-full max-w-2xl p-6 flex flex-col gap-4 max-h-[70vh]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <span className="font-semibold">Add SKUs → {avatars.find(a => a.id === addingTo)?.name}</span>
              <button onClick={() => setAddingTo(null)}><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(110px,1fr))] gap-2 overflow-y-auto">
              {skus.filter(s => !s.avatar).map(sku => (
                <div key={sku.id}
                  className="flex flex-col rounded-lg border overflow-hidden cursor-pointer hover:border-foreground/30 transition-all"
                  onClick={() => assignAvatar([sku.id], addingTo)}
                >
                  <div className="aspect-square bg-secondary/40 flex items-center justify-center">
                    <HardDrive className="w-5 h-5 text-muted-foreground/20" />
                  </div>
                  <div className="px-1.5 py-1">
                    <span className="text-[9px] text-muted-foreground truncate block leading-tight">{sku.folder}</span>
                    <span className="text-xs truncate block leading-tight">{sku.sku}</span>
                  </div>
                </div>
              ))}
              {skus.filter(s => !s.avatar).length === 0 && (
                <div className="col-span-full text-sm text-muted-foreground text-center py-8">No unassigned SKUs</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SKU preview modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-8" onClick={() => setPreview(null)}>
          <div className="bg-background rounded-2xl overflow-hidden max-w-2xl w-full flex flex-col" onClick={e => e.stopPropagation()}>
            {preview.images ? (
              <div className="grid grid-cols-2">
                <div className="relative bg-secondary/40 flex items-center justify-center aspect-square">
                  <img src={preview.images[0]} alt="Front view" className="w-full h-full object-cover" />
                  <span className="absolute bottom-2 left-2 text-[10px] font-medium bg-black/50 text-white px-1.5 py-0.5 rounded">Front</span>
                </div>
                <div className="relative bg-secondary/40 flex items-center justify-center aspect-square border-l border-background/20">
                  <img src={preview.images[1]} alt="Side view" className="w-full h-full object-cover" />
                  <span className="absolute bottom-2 left-2 text-[10px] font-medium bg-black/50 text-white px-1.5 py-0.5 rounded">Side</span>
                </div>
              </div>
            ) : (
              <div className="aspect-square bg-secondary/40 flex items-center justify-center">
                <HardDrive className="w-16 h-16 text-muted-foreground/20" />
              </div>
            )}
            <div className="flex items-center justify-between p-4">
              <div className="flex flex-col gap-0.5">
                <span className="font-medium">{preview.sku}</span>
                <span className="text-xs text-muted-foreground">{preview.folder} · {preview.avatar ? `Avatar: ${avatars.find(a => a.id === preview.avatar)?.name}` : 'Not assigned'}</span>
              </div>
              <button onClick={() => setPreview(null)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const STATUS_LABELS: Record<string, string> = {
  processing: 'Processing', done: 'Done', error: 'Error', paused: 'Paused', cancelled: 'Cancelled',
  not_configured: 'Not configured', not_started: 'Not started', queued: 'In queue'
}
const SOURCE_LABELS: Record<string, string> = { drive: 'Google Drive', dropbox: 'Dropbox', local: 'Local' }
const RES_LABEL: Record<string, string> = { '1080x1920': '1K', '1440x2560': '2K', '2160x3840': '4K' }

export function JobsView({ jobs, selectedId, onSelect, onNewBatch, onUpdateJobs, onPatchJob, onDeleteJobs, availableAvatars, skuRules, filter = 'active', cloud }: Props) {
  const [searchQuery, setSearchQuery] = useState("")
  const [sortBy, setSortBy] = useState("priority")

  const [groupBy, setGroupBy] = useState("none")
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [viewMode, setViewMode] = useState<'list' | 'card'>('list') // list = table, card = stacked cards

  const sorted = [...jobs].sort((a, b) => {
    if (sortBy === 'priority') return PRIORITY[a.status] - PRIORITY[b.status]
    if (sortBy === 'name')     return a.name.localeCompare(b.name)
    if (sortBy === 'skus')     return b.skuCount - a.skuCount
    if (sortBy === 'status')   return a.status.localeCompare(b.status)
    return b.createdAt.localeCompare(a.createdAt)
  })
  const filtered = sorted.filter(j => (filter === 'done' ? j.status === 'done' : j.status !== 'done') && j.name.toLowerCase().includes(searchQuery.toLowerCase()))

  type Group = { label: string; items: typeof filtered }
  const groups: Group[] = (() => {
    if (groupBy === 'status') {
      const map = new Map<string, typeof filtered>()
      filtered.forEach(j => { if (!map.has(j.status)) map.set(j.status, []); map.get(j.status)!.push(j) })
      return [...map.entries()].map(([k, items]) => ({ label: STATUS_LABELS[k] ?? k, items }))
    }
    if (groupBy === 'source') {
      const map = new Map<string, typeof filtered>()
      filtered.forEach(j => { if (!map.has(j.source)) map.set(j.source, []); map.get(j.source)!.push(j) })
      return [...map.entries()].map(([k, items]) => ({ label: SOURCE_LABELS[k] ?? k, items }))
    }
    return [{ label: '', items: filtered }]
  })()

  const toggleSelect = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setSelected(prev => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s })
  }

  const clearSelection = () => setSelected(new Set())

  const handleDeleteSelected = async () => {
    const ids = [...selected]
    await Promise.allSettled(ids.map(id => jobsApi.remove(Number(id))))
    onDeleteJobs(ids)
    clearSelection()
  }

  const runnable = ['not_started', 'paused', 'queued']
  const selectedArr = [...selected]
  const canRun = selectedArr.every(id => runnable.includes(jobs.find(j => j.id === id)?.status ?? ''))

  const processingJobs = jobs.filter(j => j.status === 'processing')
  const processedSKUs = processingJobs.reduce((acc, j) => acc + Math.round((j.progress ?? 0) / 100 * j.skuCount), 0)
  const totalInProgressSKUs = processingJobs.reduce((acc, j) => acc + j.skuCount, 0)
  const queuedCount = jobs.filter(j => j.status === 'queued').length
  const notStartedCount = jobs.filter(j => j.status === 'not_started').length
  const errorCount = jobs.filter(j => j.status === 'error').length
  const notConfiguredCount = jobs.filter(j => j.status === 'not_configured').length
  const pausedCount = jobs.filter(j => j.status === 'paused').length
  const attentionCount = errorCount + notConfiguredCount + pausedCount
  const attentionColor = errorCount > 0 ? 'text-rose-500' : attentionCount > 0 ? 'text-orange-500' : 'text-muted-foreground'

  if (jobs.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-base font-semibold mb-1">No jobs yet</div>
          <div className="text-sm text-muted-foreground mb-4">Create your first batch to get started</div>
          <Button onClick={onNewBatch}><Plus className="w-4 h-4 mr-1.5" />New Batch</Button>
        </div>
      </div>
    )
  }

  const selectedJob = selectedId ? jobs.find(j => j.id === selectedId) : null
  if (selectedJob) {
    return (
      <JobDetail
        key={selectedJob.id}
        job={selectedJob}
        onBack={() => { onSelect('') }}
        onSave={onPatchJob}
        avatars={availableAvatars}
        skuRules={skuRules}
        cloud={cloud}
      />
    )
  }

  return (
    <div className="h-full relative">
      <div className="flex flex-col gap-2 w-full">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          {/* In Progress */}
          <div className="rounded-xl border bg-secondary/20 p-3 flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">In Progress</span>
            <span className="text-lg font-semibold tabular-nums text-indigo-500 leading-tight">
              {processedSKUs} <span className="text-sm font-normal text-muted-foreground">/ {totalInProgressSKUs} SKUs</span>
            </span>
            <span className="text-xs text-muted-foreground">{processingJobs.length} job{processingJobs.length !== 1 ? 's' : ''} running</span>
          </div>
          {/* In Queue */}
          <div className="rounded-xl border bg-secondary/20 p-3 flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">In Queue</span>
            <span className="text-lg font-semibold tabular-nums text-blue-500 leading-tight">{queuedCount}</span>
            <span className="text-xs text-muted-foreground">jobs waiting</span>
          </div>
          {/* Not Started */}
          <div className="rounded-xl border bg-secondary/20 p-3 flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Not Started</span>
            <span className="text-lg font-semibold tabular-nums text-muted-foreground leading-tight">{notStartedCount}</span>
            <span className="text-xs text-muted-foreground">jobs pending</span>
          </div>
          {/* Attention */}
          <div className="rounded-xl border bg-secondary/20 p-3 flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Attention Needed</span>
            <span className={`text-lg font-semibold tabular-nums leading-tight ${attentionColor}`}>{attentionCount}</span>
            <span className="text-xs text-muted-foreground">
              {[
                errorCount > 0 && `${errorCount} error`,
                notConfiguredCount > 0 && `${notConfiguredCount} not configured`,
                pausedCount > 0 && `${pausedCount} paused`,
              ].filter(Boolean).join(' · ') || 'all clear'}
            </span>
          </div>
        </div>
        {/* Toolbar */}
        <div className="flex items-center gap-2 mb-1">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search jobs…" className="pl-8 h-8 text-sm" />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="h-8 w-8 p-0 shrink-0">
                <ArrowUpDown className="w-3.5 h-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel className="text-xs text-muted-foreground">Sort by</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={sortBy} onValueChange={setSortBy}>
                <DropdownMenuRadioItem value="priority">Priority</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="date">Date</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="name">Name</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="skus">SKUs</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="status">Status</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground">Group by</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={groupBy} onValueChange={setGroupBy}>
                <DropdownMenuRadioItem value="none">None</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="status">Status</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="source">Source</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="flex border rounded-lg overflow-hidden shrink-0">
            <Button size="sm" variant={viewMode === 'list' ? 'secondary' : 'ghost'} className="h-8 w-8 p-0 rounded-none" onClick={() => setViewMode('list')}>
              <LayoutList className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant={viewMode === 'card' ? 'secondary' : 'ghost'} className="h-8 w-8 p-0 rounded-none border-l" onClick={() => setViewMode('card')}>
              <LayoutGrid className="w-3.5 h-3.5" />
            </Button>
          </div>
          <Button size="sm" onClick={onNewBatch} className="h-8 px-3 shrink-0">
            <Plus className="w-3.5 h-3.5 mr-1" />New Batch
          </Button>
        </div>

        {/* Job list / card view */}
        {filtered.length === 0 ? (
          <div className="text-sm text-muted-foreground px-1 py-4 text-center">No jobs match</div>
        ) : viewMode === 'list' ? (
          /* ── TABLE VIEW ── */
          <div className="rounded-xl border overflow-hidden">
            {/* Header */}
            <div className="grid text-[11px] font-semibold text-muted-foreground uppercase tracking-wider px-4 py-2 border-b bg-secondary/20"
              style={{ gridTemplateColumns: '2fr 1fr 1.4fr 80px 1fr' }}>
              <span>Job</span>
              <span>Status</span>
              <span>Progress</span>
              <span className="text-center">SKUs</span>
              <span className="text-right">Created</span>
            </div>
            {groups.map(group => (
              <div key={group.label}>
                {group.label && (
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest px-4 py-2 border-b bg-secondary/10">{group.label}</div>
                )}
                {group.items.map(job => {
                  const isSelected = selected.has(job.id)
                  return (
                    <div
                      key={job.id}
                      className={[
                        "grid items-center gap-3 px-4 py-3 border-b last:border-b-0 cursor-pointer transition-colors group text-sm",
                        isSelected ? "bg-secondary/40" : "hover:bg-secondary/20"
                      ].join(' ')}
                      style={{ gridTemplateColumns: '2fr 1fr 1.4fr 80px 1fr' }}
                      onClick={() => onSelect(job.id)}
                    >
                      {/* JOB */}
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div
                          onClick={e => toggleSelect(job.id, e)}
                          className={[
                            "w-4 h-4 rounded border shrink-0 flex items-center justify-center transition-all",
                            isSelected ? "bg-foreground border-foreground" : "border-border group-hover:border-foreground/40"
                          ].join(' ')}
                        >
                          {isSelected && <CheckCircle2 className="w-3 h-3 text-background" />}
                        </div>
                        {sourceIcon(job.source)}
                        <span className="font-medium truncate">{job.name}</span>
                        {job.outputResolution && <span className="shrink-0 text-[10px] font-mono px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{RES_LABEL[job.outputResolution]}</span>}
                      </div>
                      {/* STATUS */}
                      <div>{statusBadge(job.status)}</div>
                      {/* PROGRESS */}
                      <div className="flex items-center gap-2">
                        {job.status === 'not_configured' ? (
                          <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={e => { e.stopPropagation(); onSelect(job.id) }}>Configure</Button>
                        ) : (
                          <>
                            <Progress
                              value={job.status === 'done' ? 100 : (job.progress ?? 0)}
                              className={`flex-1 h-1.5 ${
                                job.status === 'error' || job.status === 'cancelled'
                                  ? '[&>[data-slot=progress-indicator]]:bg-rose-400'
                                  : job.status === 'processing'
                                  ? '[&>[data-slot=progress-indicator]]:bg-indigo-400 [&>[data-slot=progress-indicator]]:animate-pulse'
                                  : job.status === 'done'
                                  ? '[&>[data-slot=progress-indicator]]:bg-emerald-400'
                                  : job.status === 'paused'
                                  ? '[&>[data-slot=progress-indicator]]:bg-amber-400'
                                  : '[&>[data-slot=progress-indicator]]:bg-muted-foreground/20'
                              }`}
                            />
                            <span className="tabular-nums text-xs text-muted-foreground shrink-0 w-8 text-right">{job.status === 'done' ? '100%' : job.status === 'cancelled' || job.status === 'paused' ? `${job.progress ?? 0}%` : `${job.progress ?? 0}%`}</span>
                          </>
                        )}
                      </div>
                      {/* SKUS */}
                      <span className="tabular-nums text-muted-foreground text-center">{job.skuCount}</span>
                      {/* CREATED */}
                      <span className="text-muted-foreground text-xs truncate text-right">{job.createdAt}</span>
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        ) : (
          /* ── CARD VIEW ── */
          <>
            {groups.map(group => (
              <div key={group.label} className="flex flex-col gap-2">
                {group.label && (
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest px-1 pt-2">{group.label}</div>
                )}
                <div className="flex flex-col gap-2">
                  {group.items.map(job => {
                    const isSelected = selected.has(job.id)
                    return (
                      <div
                        key={job.id}
                        className={[
                          "flex items-center gap-3 p-3.5 rounded-xl border text-left transition-all cursor-pointer group",
                          isSelected
                            ? "border-foreground/40 bg-secondary/40"
                            : "border-border hover:border-foreground/20 hover:bg-secondary/20"
                        ].join(' ')}
                        onClick={() => onSelect(job.id)}
                      >
                        <div
                          onClick={e => toggleSelect(job.id, e)}
                          className={[
                            "w-4 h-4 rounded border shrink-0 flex items-center justify-center transition-all",
                            isSelected
                              ? "bg-foreground border-foreground"
                              : "border-border group-hover:border-foreground/40"
                          ].join(' ')}
                        >
                          {isSelected && <CheckCircle2 className="w-3 h-3 text-background" />}
                        </div>
                        <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-semibold truncate">{job.name}</span>
                            <div className="flex items-center gap-2 shrink-0">
                              {statusBadge(job.status)}
                            </div>
                          </div>
                          <div className="flex items-center flex-wrap gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
                            {sourceIcon(job.source)}
                            <span>{job.skuCount} SKU{job.skuCount !== 1 ? 's' : ''}</span>
                            {job.outputResolution && <>
                              <span>·</span>
                              <span className="font-mono">{RES_LABEL[job.outputResolution]}</span>
                            </>}
                            <span>·</span>
                            <span>{job.createdAt}</span>
                            {job.status === 'not_configured'
                              ? <Button size="sm" variant="outline" className="basis-full sm:basis-auto sm:ml-auto h-6 px-2 text-xs" onClick={e => { e.stopPropagation(); onSelect(job.id) }}>Configure</Button>
                              : (
                                <div className="basis-full sm:basis-auto sm:w-[50%] sm:max-w-[50%] sm:ml-auto flex items-center gap-2">
                                  <Progress
                                    value={job.progress ?? 0}
                                    className={`flex-1 h-1.5 ${
                                      job.status === 'error' || job.status === 'cancelled'
                                        ? '[&>[data-slot=progress-indicator]]:bg-rose-400'
                                        : job.status === 'processing'
                                        ? '[&>[data-slot=progress-indicator]]:bg-indigo-400 [&>[data-slot=progress-indicator]]:animate-pulse'
                                        : job.status === 'done'
                                        ? '[&>[data-slot=progress-indicator]]:bg-emerald-400'
                                        : job.status === 'paused'
                                        ? '[&>[data-slot=progress-indicator]]:bg-amber-400'
                                        : '[&>[data-slot=progress-indicator]]:bg-muted-foreground/20'
                                    }`}
                                  />
                                  <span className="tabular-nums shrink-0">{job.progress ?? 0}%</span>
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Floating multi-select toolbar */}
      {selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-background border rounded-xl shadow-lg px-4 py-2.5 z-50">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <div className="w-px h-4 bg-border mx-1" />
          {canRun && (
            <Button size="sm" className="h-7 gap-1.5" onClick={async () => {
              await Promise.allSettled(
                selectedArr.map(id =>
                  jobsApi.start(Number(id)).then(() => onPatchJob(id, { status: 'queued' }))
                )
              )
              clearSelection()
            }}>
              <Play className="w-3.5 h-3.5" />Run {selected.size} job{selected.size > 1 ? 's' : ''}
            </Button>
          )}
          <Button size="sm" variant="outline" className="h-7 gap-1.5 text-destructive hover:text-destructive" onClick={() => { onUpdateJobs(selectedArr, 'cancelled'); clearSelection() }}>
            <Square className="w-3.5 h-3.5" />Cancel all
          </Button>
          <Button size="sm" variant="outline" className="h-7 gap-1.5 text-destructive hover:text-destructive" onClick={handleDeleteSelected}>
            <Trash2 className="w-3.5 h-3.5" />Delete
          </Button>
          <Button size="sm" variant="ghost" className="h-7" onClick={clearSelection}>Deselect</Button>
        </div>
      )}
    </div>
  )
}
